import { useState, useEffect, useRef, useCallback, createContext, useContext } from "react";

// ── Fonts ──────────────────────────────────────────────────────────────────
const fontLink = document.createElement("link");
fontLink.rel = "stylesheet";
fontLink.href = "https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400&family=DM+Mono:wght@400;500&family=Outfit:wght@400;500;600;700;800;900&display=swap";
document.head.appendChild(fontLink);

const css = `
  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
  :root {
    --bg:      #120f1e;
    --sidebar: #1a1630;
    --surface: #1e1a2e;
    --card:    #252040;
    --card2:   #2a2545;
    --border:  #332e50;
    --border2: #3d3760;
    --green:   #00e676;
    --red:     #ff5252;
    --amber:   #ffab40;
    --blue:    #448aff;
    --purple:  #e040fb;
    --text:    #e8e4f0;
    --text2:   #ffffff;
    --muted:   #7b7599;
    --muted2:  #a89ec0;
    --ff-head: 'Outfit', sans-serif;
    --ff-body: 'DM Sans', sans-serif;
    --ff-mono: 'DM Mono', monospace;
  }
  html,body,#root { height:100%; }
  body { background:var(--sidebar); color:var(--text); font-family:var(--ff-body); -webkit-font-smoothing:antialiased; }
  ::-webkit-scrollbar { width:4px; }
  ::-webkit-scrollbar-track { background:var(--bg); }
  ::-webkit-scrollbar-thumb { background:var(--border2); border-radius:4px; }
  @keyframes fadeUp  { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
  @keyframes fadeIn  { from{opacity:0} to{opacity:1} }
  @keyframes pulse   { 0%,100%{opacity:1} 50%{opacity:.25} }
  @keyframes shimmer { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
  .fu  { animation:fadeUp .3s ease both; }
  .fu2 { animation:fadeUp .3s .06s ease both; }
  .fu3 { animation:fadeUp .3s .12s ease both; }
  .fi  { animation:fadeIn .25s ease both; }
  .shimmer-el {
    background:linear-gradient(90deg,var(--card) 25%,var(--card2) 50%,var(--card) 75%);
    background-size:400px 100%; animation:shimmer 1.5s infinite; border-radius:6px;
  }
  button { font-family:var(--ff-body); cursor:pointer; transition:all .15s; }
  input { font-family:var(--ff-body); }
  input::placeholder { color:var(--muted); }
  input:focus { outline:none; }
  .nav-item {
    display:flex; align-items:center; gap:10px; padding:10px 16px;
    border-radius:10px; cursor:pointer; color:var(--muted2); font-size:13px;
    font-weight:500; border:none; background:none; width:100%; text-align:left;
  }
  .nav-item:hover { background:#ffffff08; color:var(--text2); }
  .nav-item.active { background:linear-gradient(135deg,#00e67620,#00e67608); color:var(--green); border:1px solid #00e67625; }
  .card { background:var(--card); border:1px solid var(--border); border-radius:14px; }
  .badge { display:inline-flex; align-items:center; justify-content:center; border-radius:6px; padding:3px 8px; font-size:10px; font-family:var(--ff-mono); font-weight:600; letter-spacing:0.06em; }
  .drop-zone { border:2px dashed var(--border2); border-radius:12px; padding:36px; text-align:center; cursor:pointer; transition:all .2s; }
  .drop-zone:hover,.drop-zone.drag { border-color:var(--green); background:#00e67608; }
  .pill-toggle { display:inline-flex; background:var(--surface); border:1px solid var(--border); border-radius:8px; padding:3px; gap:2px; }
  .pill-toggle button { border:none; border-radius:6px; padding:5px 14px; font-size:11px; font-family:var(--ff-mono); letter-spacing:0.04em; color:var(--muted); background:none; }
  .pill-toggle button.active { background:var(--green); color:#0a0a14; font-weight:700; }
  .section-label { font-size:10px; font-family:var(--ff-mono); color:var(--muted); letter-spacing:0.12em; text-transform:uppercase; margin-bottom:14px; }
  .holding-row { background:var(--card); border:1px solid var(--border); border-radius:12px; padding:16px 20px; cursor:pointer; transition:all .15s; }
  .holding-row:hover { border-color:var(--border2); background:var(--card2); }
  .stat-card { background:var(--card); border:1px solid var(--border); border-radius:14px; padding:20px; }
  .filter-btn { border-radius:8px; padding:7px 16px; font-size:11px; font-family:var(--ff-mono); letter-spacing:0.06em; border:1px solid var(--border); background:none; color:var(--muted2); }
  .filter-btn.active { background:#00e67618; border-color:#00e67640; color:var(--green); }
  .epl-form-bar { display:flex; gap:4; justify-content:center; }
  .epl-prob { border-radius:6px; padding:4px 10px; font-size:11px; font-family:var(--ff-mono); font-weight:600; border:1px solid; }

  /* ── Mobile-only elements hidden on desktop ── */
  .mobile-more-btn { display: none !important; }
  @media (min-width: 769px) {
    .mobile-more-overlay, .mobile-more-menu { display: none !important; }
  }

  /* ── Mobile responsive ── */
  @media (max-width: 768px) {
    .mobile-more-btn { display: flex !important; }
    html, body { overflow-x: hidden !important; width: 100% !important; max-width: 100% !important; }
    .app-layout { flex-direction: column !important; min-height: 100vh !important; min-height: 100dvh !important; }
    .app-sidebar {
      position: fixed !important; bottom: 0 !important; left: 0 !important; right: 0 !important; top: auto !important;
      width: 100% !important; height: auto !important;
      flex-direction: row !important; padding: 0 !important;
      border-right: none !important; border-top: 1px solid var(--border) !important;
      z-index: 999; overflow: hidden;
      background: var(--sidebar) !important;
    }
    .app-sidebar .sidebar-header,
    .app-sidebar .sidebar-footer,
    .app-sidebar .sidebar-glossary,
    .app-sidebar .sidebar-divider { display: none !important; }
    .app-sidebar nav {
      flex-direction: row !important; gap: 0 !important;
      width: 100%; justify-content: space-evenly; padding: 6px 2px 2px;
      flex-wrap: nowrap;
    }
    .app-sidebar .nav-item {
      flex-direction: column; gap: 2px !important; padding: 6px 4px !important;
      font-size: 9px !important; min-width: 0; flex: 1; text-align: center;
      justify-content: center; align-items: center;
      white-space: nowrap; line-height: 1.2;
    }
    .app-sidebar .nav-item .nav-badge { display: none; }
    .app-sidebar .nav-item .nav-icon { font-size: 16px !important; display: block; margin: 0 auto 1px; }
    .app-sidebar .nav-item .nav-label { display: block; }
    .app-sidebar .nav-item.mobile-hidden { display: none !important; }
    .mobile-more-overlay {
      position: fixed; bottom: 0; left: 0; right: 0; top: 0;
      background: rgba(0,0,0,0.6); z-index: 1000;
    }
    .mobile-more-menu {
      position: fixed; bottom: calc(56px + env(safe-area-inset-bottom, 0px)); left: 8px; right: 8px;
      background: var(--sidebar); border: 1px solid var(--border); border-radius: 16px;
      padding: 16px; z-index: 1001;
      display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px;
    }
    .mobile-more-menu button {
      display: flex; flex-direction: column; align-items: center; gap: 4px;
      background: var(--surface); border: 1px solid var(--border); border-radius: 12px;
      padding: 12px 8px; color: var(--text2); font-size: 11px; font-family: var(--ff-head);
    }
    .mobile-more-menu button.active { border-color: var(--green); color: var(--green); }
    .mobile-more-menu button .nav-icon { font-size: 18px; }
    .pull-indicator {
      position: fixed; top: 0; left: 0; right: 0; z-index: 998;
      display: flex; justify-content: center; align-items: center;
      pointer-events: none; transition: transform 0.2s ease;
    }
    .pull-indicator-dot {
      width: 32px; height: 32px; border-radius: 50%; background: var(--surface);
      border: 2px solid var(--green); display: flex; align-items: center;
      justify-content: center; font-size: 14px; color: var(--green);
      box-shadow: 0 2px 12px rgba(0,0,0,0.4);
    }
    .pull-indicator-dot.refreshing { animation: spin 0.8s linear infinite; }
    @keyframes spin { to { transform: rotate(360deg); } }
    .app-main {
      flex: 1 !important;
      width: 100% !important; max-width: 100vw !important;
      overflow-x: hidden !important;
      padding: 16px 12px 80px !important;
      padding-top: calc(env(safe-area-inset-top, 0px) + 16px) !important;
      padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)) !important;
    }
    .app-main * { max-width: 100% !important; box-sizing: border-box !important; }
    .app-main canvas { max-width: 100% !important; }
    .app-main input, .app-main button { max-width: 100% !important; }
    .fu2 { flex-wrap: nowrap !important; }
    .card { border-radius: 10px !important; }
    .holding-row { padding: 12px 14px !important; border-radius: 10px !important; }
    .stat-card { padding: 14px !important; border-radius: 10px !important; }
    h1 { font-size: 22px !important; }
    .pick-grid { grid-template-columns: 1fr !important; }
    .summary-grid { grid-template-columns: repeat(2, 1fr) !important; }
    .alloc-grid { grid-template-columns: 1fr !important; }
    .section-label { font-size: 9px !important; }
    .coach-stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
    .alloc-panel-grid { grid-template-columns: 1fr !important; gap: 16px !important; }
    .alloc-panel-grid > div:first-child canvas { width: 140px !important; height: 140px !important; }
    .risk-stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
    .risk-stats-grid > div { padding: 12px !important; min-width: 0 !important; }
    .risk-stats-grid > div .risk-value { font-size: 18px !important; }
    .bench-row { grid-template-columns: 1fr auto !important; gap: 8px !important; }
    .bench-row .bench-bar { grid-column: 1 / -1 !important; }
  }
  @media (max-width: 480px) {
    .app-main { padding: 12px 8px !important;
      padding-top: calc(env(safe-area-inset-top, 0px) + 12px) !important;
      padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)) !important;
    }
    .summary-grid { grid-template-columns: 1fr !important; }
  }
  @media all and (display-mode: standalone) {
    .app-main { padding-top: calc(env(safe-area-inset-top, 48px) + 12px) !important; }
  }
`;

// ── Static data ────────────────────────────────────────────────────────────
// Dashboard picks are now generated live by Claude — see /api/dashboard/picks

const NEWS_MOCK = [
  { time:"2h ago",  tag:"US TECH",    headline:"Fed minutes signal two rate cuts possible — growth stocks begin re-rating",          sentiment:"BULLISH", impact:"HIGH",   affected:["NVDA","MSFT","AAPL"],        commentary:"Rate-sensitive growth stocks are the primary beneficiary. Confirms macro tailwind underpinning our NVDA BUY thesis." },
  { time:"4h ago",  tag:"ASX MINING", headline:"China PMI beats expectations — iron ore +3.2%, copper surges to 3-month high",       sentiment:"BULLISH", impact:"MEDIUM", affected:["BHP.AX","RIO.AX","FMG.AX"],  commentary:"Short-term positive for ASX majors. Boosts sentiment across resources broadly." },
  { time:"6h ago",  tag:"CRYPTO",     headline:"BlackRock Bitcoin ETF crosses $15B AUM — fastest ETF growth in history",             sentiment:"BULLISH", impact:"HIGH",   affected:["BTC","ETH"],                 commentary:"Structural, not speculative. Institutional allocation now systematic." },
  { time:"9h ago",  tag:"ASX MINING", headline:"Lithium spot price stabilises for second consecutive week — floor forming?",         sentiment:"NEUTRAL", impact:"MEDIUM", affected:["PLS.AX","LTR.AX","AKE.AX"],  commentary:"Rate of decline has slowed. Supports WATCH on PLS.AX." },
];

// ── Glossary ───────────────────────────────────────────────────────────────
const GLOSSARY = [
  { term:"Double Bottom",              def:"A bullish reversal chart pattern forming a 'W' shape, where price hits the same low twice before reversing upward. Signals seller exhaustion and a potential trend change to the upside." },
  { term:"Double Top",                 def:"A bearish reversal chart pattern forming an 'M' shape, where price hits the same high twice before reversing downward. Signals buyer exhaustion and a potential trend change to the downside." },
  { term:"Head and Shoulders",         def:"A bearish reversal pattern with three peaks: a higher middle peak (the head) flanked by two lower peaks (the shoulders). A decisive break below the neckline confirms the reversal." },
  { term:"Inverse Head and Shoulders", def:"The bullish mirror of Head and Shoulders: three troughs with the middle deepest. A break above the neckline signals upward momentum and a potential trend reversal." },
  { term:"Bull Flag",                  def:"A bullish continuation pattern: a sharp surge (the flagpole) followed by a brief downward consolidation (the flag). A breakout above the flag typically resumes the uptrend." },
  { term:"Bear Flag",                  def:"A bearish continuation pattern: a sharp decline followed by a brief upward consolidation. A breakdown below the flag typically resumes the downtrend." },
  { term:"Cup and Handle",             def:"A bullish continuation pattern resembling a teacup: a rounded bottom (cup) followed by a short consolidation (handle). A breakout above the handle is the buy signal." },
  { term:"Ascending Triangle",         def:"A bullish pattern with a flat resistance line and a rising support trendline. Price coils upward — a breakout above resistance signals continued strength." },
  { term:"Descending Triangle",        def:"A bearish pattern with a flat support line and a falling resistance trendline. A breakdown below support signals continued weakness." },
  { term:"Symmetrical Triangle",       def:"A neutral consolidation pattern with converging trendlines, reflecting indecision. The breakout direction determines the next significant move." },
  { term:"Falling Wedge",              def:"A bullish pattern where price consolidates between two downward-sloping, converging trendlines. A breakout above the upper line signals a reversal or continuation upward." },
  { term:"Rising Wedge",               def:"A bearish pattern where price consolidates between two upward-sloping, converging trendlines. Despite rising price, weakening momentum often precedes a breakdown." },
  { term:"Pennant",                    def:"A short-term continuation pattern following a strong move: price consolidates in a small symmetrical triangle before typically continuing in the original direction." },
  { term:"Hammer",                     def:"A bullish reversal candlestick with a small body and long lower wick. Signals that sellers pushed price down intraday but buyers recovered strongly by the close." },
  { term:"Shooting Star",              def:"A bearish reversal candlestick with a small body and long upper wick. Signals that buyers pushed price up intraday but sellers overwhelmed them by the close." },
  { term:"Doji",                       def:"A candlestick where open and close are nearly equal, leaving a very small body. Signals market indecision — a Doji after a strong trend may indicate a reversal is near." },
  { term:"Engulfing",                  def:"A two-candle reversal pattern. Bullish Engulfing: a large green candle fully covers the prior red candle. Bearish Engulfing: the reverse. Both signal potential trend reversals." },
  { term:"Support",                    def:"A price level where buying interest has historically been strong enough to halt a decline. Acts as a floor — the more times price bounces off it, the stronger the level." },
  { term:"Resistance",                 def:"A price level where selling pressure has historically been strong enough to halt an advance. Acts as a ceiling — once broken, resistance often flips to become support." },
  { term:"Breakout",                   def:"When price moves decisively above a key resistance level, often on elevated volume. Signals that buyers have overcome supply and a new upward move is likely." },
  { term:"Breakdown",                  def:"When price moves decisively below a key support level, often on elevated volume. Signals that sellers have overcome demand and a new downward move is likely." },
  { term:"Consolidation",              def:"A period of sideways price movement within a defined range, reflecting a balance between buyers and sellers. Often precedes a significant breakout or breakdown." },
  { term:"Moving Average",             def:"The average closing price over a set number of periods (e.g. 50-day or 200-day MA). Smooths price noise and identifies trend direction. Price above a moving average is generally bullish." },
  { term:"RSI",                        def:"Relative Strength Index. A momentum oscillator scaled 0–100 measuring the speed of price changes. Above 70 suggests overbought conditions; below 30 suggests oversold." },
  { term:"MACD",                       def:"Moving Average Convergence Divergence. A momentum indicator using two moving averages. A bullish crossover (MACD above signal line) suggests upward momentum; a bearish crossover suggests the reverse." },
  { term:"Momentum",                   def:"The rate of acceleration in price movement. Strong momentum confirms a trend; fading momentum often precedes a reversal. Measured by RSI, MACD, and rate of change." },
  { term:"Overbought",                 def:"A condition where a security has risen sharply and may be due for a pullback. Typically indicated by RSI above 70. Overbought assets can remain overbought in strong trends." },
  { term:"Oversold",                   def:"A condition where a security has fallen sharply and may be due for a bounce. Typically indicated by RSI below 30. Oversold assets can remain oversold in strong downtrends." },
  { term:"Retracement",                def:"A temporary price reversal against the prevailing trend before it resumes. Fibonacci retracement levels (38.2%, 50%, 61.8%) are common areas where price finds support during a pullback." },
  { term:"Neckline",                   def:"In Head and Shoulders patterns, the trendline connecting the reaction lows (or highs). A decisive break of the neckline confirms the pattern and sets a price target." },
  { term:"Higher High",                def:"When the most recent price peak exceeds the prior peak. A series of higher highs (and higher lows) defines an uptrend." },
  { term:"Lower Low",                  def:"When the most recent trough falls below the prior trough. A series of lower lows (and lower highs) defines a downtrend." },
  { term:"Stop Loss",                  def:"A pre-defined price at which a position is closed to cap losses. Critical risk management — prevents a small loss from becoming a large one if the trade moves against you." },
  { term:"Volatility",                 def:"The degree of price variation over time. High volatility means larger swings and higher uncertainty; low volatility means more stable, predictable price action. As a portfolio risk metric, annualised volatility measures the standard deviation of daily returns scaled to a year — below 15% is considered low risk, above 25% is high risk." },
  { term:"Volume",                     def:"The total number of shares or coins traded in a given period. Volume confirms price moves — a breakout on high volume is more reliable than one on low volume." },
  { term:"Candlestick",                def:"A chart bar showing the open, high, low, and close for a time period. Green candles close higher than they opened; red close lower. Patterns signal reversals or continuations." },
  { term:"OHLCV",                      def:"Open, High, Low, Close, Volume — the five data points that make up each candlestick. The foundation of all technical chart analysis." },
  { term:"Market Cap",                 def:"Total market capitalisation: share price × total shares outstanding. Used to classify companies — large-cap (>$10B), mid-cap ($2–10B), small-cap (<$2B)." },
  { term:"Dollar Cost Averaging",      def:"Investing a fixed amount at regular intervals regardless of price. Reduces timing risk — you buy more units when cheap and fewer when expensive." },
  { term:"P&L",                        def:"Profit and Loss. Unrealised P&L exists while the position is open; realised P&L is locked in when closed. Calculated as (current price − average cost) / average cost × 100%." },
  { term:"Beta",                       def:"A measure of a portfolio's or stock's sensitivity to market movements relative to a benchmark (typically the S&P 500). Beta < 1 means less volatile than the market (lower risk); Beta > 1 means more volatile (higher risk); Beta < 0 means it tends to move inversely to the market." },
  { term:"Sharpe Ratio",               def:"A measure of risk-adjusted return. Calculated as (portfolio return − risk-free rate) / portfolio volatility. A Sharpe above 1.0 is considered good — it means you are being adequately compensated for the risk taken. Below 0 means the portfolio underperformed the risk-free rate." },
  { term:"Maximum Drawdown",           def:"The largest peak-to-trough decline in portfolio value over a given period, expressed as a percentage. A Max Drawdown of −20% means the portfolio fell 20% from its highest point before recovering. It measures downside risk and the worst loss an investor could have experienced." },
  { term:"Short Term",                 def:"An investment horizon of up to 3 months. Short-term calls focus on near-term catalysts such as earnings releases, technical breakouts, macro events, or momentum. Suitable for more active traders willing to monitor positions closely." },
  { term:"Medium Term",                def:"An investment horizon of 3 months to 1 year. Medium-term calls look for a confluence of improving fundamentals, sector tailwinds, and supportive technicals to play out over multiple quarters." },
  { term:"Long Term",                  def:"An investment horizon of 1 year or more. Long-term calls are driven by structural growth themes, durable competitive advantages, or deep value — where patience is rewarded as the thesis plays out over an extended period." },
];

const TABS = [
  { id:"dashboard", label:"Dashboard", icon:"◈" },
  { id:"explorer",  label:"Explorer",  icon:"◎" },
  { id:"screener",  label:"Screener",  icon:"◰" },
  { id:"portfolio", label:"Portfolio", icon:"◑" },
  { id:"coach",     label:"Coach",     icon:"◭" },
  { id:"news",      label:"News",      icon:"◉" },
  { id:"macro",     label:"Macro",     icon:"◮" },
  { id:"watchlist", label:"Watchlist", icon:"◇" },
  { id:"earnings",  label:"Earnings",  icon:"◬" },
  { id:"ipo",       label:"IPO",       icon:"◆" },
  { id:"calls",     label:"Calls",     icon:"◐" },
  { id:"journal",   label:"Journal",   icon:"◫" },
  { id:"indices",   label:"Indices",   icon:"◳" },
];

const MOBILE_BAR_IDS = ["dashboard","explorer","screener","portfolio","coach","watchlist"];
const MOBILE_MORE_TABS = TABS.filter(t => !MOBILE_BAR_IDS.includes(t.id));

const PORT_TABS = [
  { id:"all",      label:"All",        color:"var(--text2)" },
  { id:"coinbase", label:"Coinbase",   color:"var(--amber)" },
  { id:"binance",  label:"Binance",    color:"#F0B90B" },
  { id:"tiger",    label:"Tiger",      color:"#FF6600" },
  { id:"ledger",   label:"Ledger",     color:"#41a5f5"      },
  { id:"cmc",      label:"CMC Invest", color:"var(--blue)"  },
];

// ── CMC CSV parser ─────────────────────────────────────────────────────────
function parseCMCcsv(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) throw new Error("Empty CSV");
  const headers = lines[0].replace(/^\uFEFF/, "").split(",").map(h => h.replace(/"/g, "").trim());
  const idx  = n => headers.findIndex(h => h === n);
  const idxi = (...names) => { for (const n of names) { const i = headers.findIndex(h => h.toLowerCase() === n.toLowerCase()); if (i !== -1) return i; } return -1; };
  const iCode = idx("Security Code"), iSector = idx("Sector"), iName = idx("Company Name");
  const iQty  = idx("Quantity"),       iAvg    = idx("Average Cost $");
  const iDivC = idxi("Current Dividend c","Dividend (c)","Dividend c","Current Div c","Div (c)");
  const iDivY = idxi("Dividend yield %","Dividend Yield %","Dividend Yield","Yield %","Div Yield %");
  const iFrank= idxi("Franking %","Franking","Franking Pct","Frank %");
  if (iCode === -1 || iQty === -1 || iAvg === -1)
    throw new Error("Unrecognised format — expected CMC Invest Portfolio Report CSV");

  const holdings = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim(); if (!line) continue;
    const cols = []; let cur = "", inQ = false;
    for (const ch of line) {
      if (ch === '"') { inQ = !inQ; }
      else if (ch === "," && !inQ) { cols.push(cur.trim()); cur = ""; }
      else cur += ch;
    }
    cols.push(cur.trim());

    const rawCode  = cols[iCode] || "";
    const qty      = parseFloat(cols[iQty]);
    const avgAUD   = parseFloat(cols[iAvg]);

    if (!rawCode || isNaN(qty) || isNaN(avgAUD) || qty <= 0) continue;

    const isUS = /:\s*US$/i.test(rawCode);
    let sym = rawCode.replace(/:US$/i, "").trim();
    if (!isUS && !sym.endsWith(".AX")) sym += ".AX";

    const divCents   = iDivC  !== -1 ? parseFloat(cols[iDivC])  || 0 : 0;
    const divYieldPct= iDivY  !== -1 ? parseFloat(cols[iDivY])  || 0 : 0;
    const frankingPct= iFrank !== -1 ? parseFloat(cols[iFrank]) || 0 : 0;
    holdings.push({
      sym, name: cols[iName] || sym, qty,
      avg: avgAUD, avgCurrency: "AUD",
      priceCurrency: isUS ? "USD" : "AUD",
      sector: cols[iSector] || "Unknown",
      horizon: "Medium", priceType: "stock", source: "cmc", rawCode,
      divCents, divYieldPct, frankingPct,
    });
  }
  if (!holdings.length) throw new Error("No valid holdings found in CSV");
  return holdings;
}

// ── Currency helpers ───────────────────────────────────────────────────────
function toDisplay(amount, fromCcy, displayCcy, audUsd) {
  if (amount == null || isNaN(amount)) return null;
  if (fromCcy === displayCcy) return amount;
  if (fromCcy === "USD" && displayCcy === "AUD") return amount / audUsd;
  if (fromCcy === "AUD" && displayCcy === "USD") return amount * audUsd;
  return amount;
}

function fmtMoney(v, ccy) {
  if (v == null || isNaN(v)) return "—";
  const s = ccy === "AUD" ? "A$" : "$";
  if (Math.abs(v) >= 1_000_000) return `${s}${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000)     return `${s}${v.toLocaleString("en", { maximumFractionDigits: 0 })}`;
  if (Math.abs(v) >= 1)         return `${s}${v.toFixed(2)}`;
  return `${s}${v.toFixed(4)}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { const d = new Date(typeof iso === "string" && iso.length === 10 ? iso + "T00:00:00" : iso); const dd = String(d.getDate()).padStart(2,"0"); const mm = String(d.getMonth()+1).padStart(2,"0"); return `${dd}-${mm}-${d.getFullYear()}`; }
  catch { return String(iso); }
}

function calcPnl(holding, livePriceData, audUsd) {
  const livePrice = livePriceData?.price;
  if (!livePrice || !holding.avg) return { pct: "—", up: true, raw: 0, valid: false };
  const priceCcy = holding.priceCurrency || livePriceData?.currency || "USD";
  const priceUSD = toDisplay(livePrice, priceCcy, "USD", audUsd);
  const avgUSD   = toDisplay(holding.avg, holding.avgCurrency || "USD", "USD", audUsd);
  if (!priceUSD || !avgUSD) return { pct: "—", up: true, raw: 0, valid: false };
  const p = ((priceUSD - avgUSD) / avgUSD) * 100;
  return { pct: (p >= 0 ? "+" : "") + p.toFixed(1) + "%", up: p >= 0, raw: p, valid: true };
}

function parseTargetNum(targetStr) {
  if (!targetStr) return null;
  const s = targetStr.replace(/[$,\s]/g, "");
  if (/k$/i.test(s)) return parseFloat(s) * 1000;
  if (/m$/i.test(s)) return parseFloat(s) * 1_000_000;
  return parseFloat(s) || null;
}

const nowTime = () => new Date().toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });

// ── Glossary context & linkifier ───────────────────────────────────────────
const GlossaryCtx = createContext({ allGlossary: GLOSSARY, openGlossary: () => {} });

function linkifyText(text, onTermClick, glossary) {
  if (!text || !onTermClick) return text;
  const gl = glossary || GLOSSARY;
  const sorted = [...gl].sort((a, b) => b.term.length - a.term.length);
  const escaped = sorted.map(g => g.term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const regex = new RegExp(`\\b(${escaped.join("|")})\\b`, "gi");
  const parts = text.split(regex);
  return parts.map((part, i) => {
    const match = gl.find(g => g.term.toLowerCase() === part.toLowerCase());
    if (match) {
      return (
        <span key={i} onClick={() => onTermClick(match.term)}
          style={{ color:"var(--amber)", borderBottom:"1px dotted #ffab4070", cursor:"pointer" }}
          title={`Click to define: ${match.term}`}>
          {part}
        </span>
      );
    }
    return part;
  });
}

// Consume context — drop-in wrapper for any text that should have linked terms
function LinkedText({ children }) {
  const { allGlossary, openGlossary } = useContext(GlossaryCtx);
  if (!children) return null;
  return <>{linkifyText(String(children), openGlossary, allGlossary)}</>;
}

// ── Atoms ──────────────────────────────────────────────────────────────────
function VerdictBadge({ v }) {
  const m = { BUY:{bg:"#00e67620",c:"#00e676",b:"#00e67640"}, WATCH:{bg:"#ffab4020",c:"#ffab40",b:"#ffab4040"}, AVOID:{bg:"#ff525220",c:"#ff5252",b:"#ff525240"}, SELL:{bg:"#ff525220",c:"#ff5252",b:"#ff525240"}, HOLD:{bg:"#ffffff10",c:"#a89ec0",b:"#3d3760"}, BULLISH:{bg:"#00e67620",c:"#00e676",b:"#00e67640"}, NEUTRAL:{bg:"#ffab4020",c:"#ffab40",b:"#ffab4040"}, BEARISH:{bg:"#ff525220",c:"#ff5252",b:"#ff525240"} };
  const s = m[v] || m.HOLD;
  return <span className="badge" style={{ background:s.bg, color:s.c, border:`1px solid ${s.b}` }}>{v}</span>;
}

function ConvictionDots({ level }) {
  const n = { HIGH:3, MEDIUM:2, LOW:1 }[level] || 1;
  return (
    <span style={{ display:"flex", gap:4, alignItems:"center" }}>
      {[0,1,2].map(i => <span key={i} style={{ width:6, height:6, borderRadius:"50%", background:i<n?"var(--green)":"var(--border2)" }}/>)}
      <span style={{ fontSize:10, color:"var(--muted2)", fontFamily:"var(--ff-mono)", marginLeft:2 }}>{level}</span>
    </span>
  );
}

function SourceBadge({ source }) {
  const m = { coinbase:{l:"CB",c:"var(--amber)"}, binance:{l:"BN",c:"#F0B90B"}, ledger:{l:"LDR",c:"#41a5f5"}, cmc:{l:"CMC",c:"var(--blue)"} };
  const s = m[source] || { l:"?", c:"var(--muted)" };
  return <span className="badge" style={{ background:`${s.c}20`, color:s.c, border:`1px solid ${s.c}40` }}>{s.l}</span>;
}

function SectorBadge({ sector }) {
  const m = { "US Tech":"#448aff","Technology":"#448aff","ASX Mining":"#ffab40","Materials":"#ffab40","Crypto":"#e040fb","Energy":"#ff6e40","Health Care":"#00e5ff","Communications":"#69f0ae" };
  const c = m[sector] || "var(--muted2)";
  return <span className="badge" style={{ background:`${c}18`, color:c, border:`1px solid ${c}35` }}>{sector}</span>;
}

function CurrencyToggle({ value, onChange }) {
  return (
    <div className="pill-toggle">
      <button className={value==="USD"?"active":""} onClick={()=>onChange("USD")}>USD</button>
      <button className={value==="AUD"?"active":""} onClick={()=>onChange("AUD")}>AUD</button>
    </div>
  );
}

// ── Summary strip ──────────────────────────────────────────────────────────
function SummaryStrip({ holdings, livePrices, displayCcy, audUsd }) {
  let totalValueUSD = 0, totalCostUSD = 0;
  let cryptoValueUSD = 0, cryptoCostUSD = 0;
  let stocksValueUSD = 0, stocksCostUSD = 0;
  let todayPnlUSD = 0;
  let best = null, worst = null;

  for (const h of holdings) {
    const lp = livePrices[h.sym];
    const priceCcy = h.priceCurrency || lp?.currency || "USD";
    const livePrice = lp?.price;
    const priceUSD = livePrice ? toDisplay(livePrice, priceCcy, "USD", audUsd) : null;
    const avgUSD   = toDisplay(h.avg, h.avgCurrency || "USD", "USD", audUsd);
    const valueUSD = priceUSD != null ? h.qty * priceUSD : h.qty * avgUSD;
    const costUSD  = h.qty * avgUSD;
    totalValueUSD += valueUSD; totalCostUSD += costUSD;
    if (h.priceType === "crypto") { cryptoValueUSD += valueUSD; cryptoCostUSD += costUSD; }
    else                          { stocksValueUSD += valueUSD; stocksCostUSD += costUSD; }
    // Today's P&L: qty × price × (change% / 100)
    if (priceUSD != null && lp?.change != null) {
      todayPnlUSD += h.qty * priceUSD * (lp.change / 100);
    }
    // Best / worst by unrealised %
    if (priceUSD != null && avgUSD) {
      const pnlPct = ((priceUSD - avgUSD) / avgUSD) * 100;
      if (best  == null || pnlPct > best.pct)  best  = { sym: h.sym, pct: pnlPct };
      if (worst == null || pnlPct < worst.pct) worst = { sym: h.sym, pct: pnlPct };
    }
  }

  const totalPnl    = totalCostUSD  > 0 ? ((totalValueUSD  - totalCostUSD)  / totalCostUSD)  * 100 : 0;
  const cryptoPnl   = cryptoCostUSD > 0 ? ((cryptoValueUSD - cryptoCostUSD) / cryptoCostUSD) * 100 : null;
  const stocksPnl   = stocksCostUSD > 0 ? ((stocksValueUSD - stocksCostUSD) / stocksCostUSD) * 100 : null;
  const totalValue    = toDisplay(totalValueUSD,               "USD", displayCcy, audUsd);
  const totalInvested = toDisplay(totalCostUSD,                "USD", displayCcy, audUsd);
  const pnlAbsolute   = toDisplay(totalValueUSD - totalCostUSD,"USD", displayCcy, audUsd);
  const todayDisp     = toDisplay(Math.abs(todayPnlUSD),       "USD", displayCcy, audUsd);

  const row1 = [
    { l:"TOTAL VALUE",    v:fmtMoney(totalValue, displayCcy),                                    c:"var(--text2)",  sub:`invested ${fmtMoney(totalInvested, displayCcy)}` },
    { l:"TOTAL P&L",      v:`${totalPnl>=0?"+":""}${totalPnl.toFixed(1)}%`,                      c:totalPnl>=0?"var(--green)":"var(--red)", sub:fmtMoney(Math.abs(pnlAbsolute), displayCcy) },
    { l:"TODAY'S P&L",    v:todayPnlUSD!==0?`${todayPnlUSD>=0?"+":"−"}${fmtMoney(todayDisp,displayCcy)}`:"—", c:todayPnlUSD>=0?"var(--green)":"var(--red)", sub:null },
    { l:"BEST",           v:best  ? `${best.sym}`  :"—", c:"var(--green)", sub:best  ? `${best.pct>=0?"+":""}${best.pct.toFixed(1)}%`  : null },
    { l:"WORST",          v:worst ? `${worst.sym}` :"—", c:"var(--red)",   sub:worst ? `${worst.pct>=0?"+":""}${worst.pct.toFixed(1)}%` : null },
  ];
  const row2 = [
    { l:"CRYPTO P&L", v:cryptoPnl!=null?`${cryptoPnl>=0?"+":""}${cryptoPnl.toFixed(1)}%`:"—", c:cryptoPnl==null?"var(--muted)":cryptoPnl>=0?"var(--green)":"var(--red)", sub:null },
    { l:"STOCKS P&L", v:stocksPnl!=null?`${stocksPnl>=0?"+":""}${stocksPnl.toFixed(1)}%`:"—", c:stocksPnl==null?"var(--muted)":stocksPnl>=0?"var(--green)":"var(--red)", sub:null },
    { l:"POSITIONS",  v:`${holdings.length}`, c:"var(--text2)", sub:null },
  ];

  const Card = ({ m, large }) => (
    <div key={m.l} className="stat-card">
      <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.12em", marginBottom:10 }}>{m.l}</div>
      <div style={{ fontSize:large?22:18, fontFamily:"var(--ff-head)", fontWeight:800, color:m.c, lineHeight:1 }}>{m.v}</div>
      {m.sub && <div style={{ fontSize:11, color:"var(--muted2)", marginTop:5, fontFamily:"var(--ff-mono)" }}>{m.sub}</div>}
    </div>
  );

  return (
    <div style={{ marginBottom:24 }}>
      <div className="summary-grid" style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:12, marginBottom:12 }}>
        {row1.map(m => <Card key={m.l} m={m} large />)}
      </div>
      <div className="summary-grid" style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }}>
        {row2.map(m => <Card key={m.l} m={m} />)}
      </div>
    </div>
  );
}

// ── Allocation Panel ───────────────────────────────────────────────────────
const SECTOR_COLOURS = [
  "#448aff","#00e676","#ffab40","#ff5252","#e040fb","#00bcd4","#8bc34a",
  "#ff9800","#9c27b0","#f06292","#26c6da","#d4e157",
];

// ── Portfolio Performance Chart ─────────────────────────────────────────────
function PortfolioChart({ holdings, displayCcy, audUsd, authFetch }) {
  const canvasRef    = useRef(null);
  const containerRef = useRef(null);
  const [canvasW,  setCanvasW]  = useState(0);
  const [history,  setHistory]  = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [range,    setRange]    = useState("1m");
  const [expanded, setExpanded] = useState(true);

  // Track container width for responsive canvas
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver(() => setCanvasW(el.clientWidth));
    obs.observe(el);
    setCanvasW(el.clientWidth);
    return () => obs.disconnect();
  }, []);

  const holdingsKey = holdings.map(h => `${h.sym}:${h.qty}`).join(",");

  useEffect(() => {
    if (!holdingsKey) return;
    setLoading(true);
    setHistory(null);
    const payload = holdings.map(h => ({ sym: h.sym, qty: h.qty, priceCurrency: h.priceCurrency || "USD" }));
    authFetch("/api/portfolio/history", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ holdings: payload, range }),
    })
      .then(r => r.json()).then(d => setHistory(d)).catch(() => {})
      .finally(() => setLoading(false));
  }, [holdingsKey, range]);

  // Draw line chart
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !history?.series?.length || !canvasW || loading) return;

    const series = history.series.map(d => ({
      t: d.t,
      v: displayCcy === "AUD" ? d.v / audUsd : d.v,
    }));

    const isUp  = series[series.length - 1].v >= series[0].v;
    const W = canvasW, H = 200;
    const PAD = { t: 16, r: 16, b: 36, l: 72 };
    const dpr = window.devicePixelRatio || 1;
    canvas.width = W * dpr; canvas.height = H * dpr;
    canvas.style.width = "100%"; canvas.style.height = H + "px";
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    const vals  = series.map(d => d.v);
    const minV  = Math.min(...vals), maxV = Math.max(...vals);
    const vPad  = (maxV - minV) * 0.12 || maxV * 0.05 || 1;
    const vLow  = minV - vPad, vHigh = maxV + vPad;
    const vSpan = vHigh - vLow;
    const CW = W - PAD.l - PAD.r;
    const CH = H - PAD.t - PAD.b;

    const xS = i => PAD.l + (i / Math.max(series.length - 1, 1)) * CW;
    const yS = v => PAD.t + (1 - (v - vLow) / vSpan) * CH;

    const lineColor = isUp ? "#00e676" : "#ff5252";

    // Gradient fill below line
    const grad = ctx.createLinearGradient(0, PAD.t, 0, PAD.t + CH);
    grad.addColorStop(0, isUp ? "#00e67628" : "#ff525228");
    grad.addColorStop(1, "transparent");
    ctx.beginPath();
    series.forEach((d, i) => i === 0 ? ctx.moveTo(xS(i), yS(d.v)) : ctx.lineTo(xS(i), yS(d.v)));
    ctx.lineTo(xS(series.length - 1), PAD.t + CH);
    ctx.lineTo(PAD.l, PAD.t + CH);
    ctx.closePath();
    ctx.fillStyle = grad;
    ctx.fill();

    // Y gridlines + labels
    ctx.font = "11px 'DM Mono',monospace";
    ctx.textAlign = "right";
    const yTicks = 4;
    for (let i = 0; i <= yTicks; i++) {
      const v = vLow + (i / yTicks) * vSpan;
      const y = yS(v);
      ctx.strokeStyle = "#332e5025"; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(PAD.l, y); ctx.lineTo(W - PAD.r, y); ctx.stroke();
      ctx.fillStyle = "#7b7599";
      ctx.fillText(fmtMoney(v, displayCcy), PAD.l - 5, y + 4);
    }

    // X-axis date labels (3 evenly spaced)
    ctx.textAlign = "center"; ctx.fillStyle = "#7b7599";
    [0, Math.floor((series.length - 1) / 2), series.length - 1].forEach(i => {
      if (!series[i]) return;
      const label = new Date(series[i].t).toLocaleDateString("en-AU", { day:"numeric", month:"short" });
      ctx.fillText(label, xS(i), H - PAD.b + 14);
    });

    // Line
    ctx.beginPath();
    series.forEach((d, i) => i === 0 ? ctx.moveTo(xS(i), yS(d.v)) : ctx.lineTo(xS(i), yS(d.v)));
    ctx.strokeStyle = lineColor; ctx.lineWidth = 2; ctx.lineJoin = "round";
    ctx.stroke();

    // Latest value dot
    const li = series.length - 1;
    ctx.beginPath(); ctx.arc(xS(li), yS(series[li].v), 4, 0, 2 * Math.PI);
    ctx.fillStyle = lineColor; ctx.fill();
  }, [history, displayCcy, audUsd, canvasW, loading]);

  if (!holdingsKey) return null;

  const changePct = history?.changePct;
  const first = history?.first != null ? (displayCcy === "AUD" ? history.first / audUsd : history.first) : null;
  const last  = history?.last  != null ? (displayCcy === "AUD" ? history.last  / audUsd : history.last)  : null;

  return (
    <div style={{ background:"var(--card)", border:"1px solid var(--border)", borderRadius:14, padding:20, marginBottom:24 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: expanded ? 16 : 0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.12em" }}>PORTFOLIO PERFORMANCE</div>
          {!loading && changePct != null && (
            <span style={{ fontSize:13, fontWeight:700, fontFamily:"var(--ff-mono)",
              color: changePct >= 0 ? "var(--green)" : "var(--red)" }}>
              {changePct >= 0 ? "+" : ""}{changePct.toFixed(2)}%
            </span>
          )}
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <div className="pill-toggle">
            {["1m","3m","1y"].map(r => (
              <button key={r} onClick={() => setRange(r)} className={range === r ? "active" : ""}>{r.toUpperCase()}</button>
            ))}
          </div>
          <button onClick={() => setExpanded(e => !e)} style={{ background:"none", border:"none", color:"var(--muted)", fontSize:14, padding:"2px 6px", cursor:"pointer" }}>
            {expanded ? "▾" : "▸"}
          </button>
        </div>
      </div>

      {expanded && (
        <div ref={containerRef}>
          {loading ? (
            <div className="shimmer-el" style={{ height:200, borderRadius:8 }}/>
          ) : !history?.series?.length ? (
            <p style={{ fontSize:12, color:"var(--muted)", textAlign:"center", padding:"24px 0" }}>
              No price history available for your holdings.
            </p>
          ) : (
            <div style={{ position:"relative" }}>
              <canvas ref={canvasRef} style={{ display:"block" }}/>
              {first != null && last != null && (
                <div style={{ display:"flex", justifyContent:"space-between", marginTop:4, paddingLeft:72, paddingRight:16 }}>
                  <span style={{ fontSize:10, color:"var(--muted)", fontFamily:"var(--ff-mono)" }}>
                    Start: {fmtMoney(first, displayCcy)}
                  </span>
                  <span style={{ fontSize:10, color:"var(--muted)", fontFamily:"var(--ff-mono)" }}>
                    Now: {fmtMoney(last, displayCcy)}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Benchmark Strip ─────────────────────────────────────────────────────────
function BenchmarkStrip({ holdings, livePrices, audUsd, authFetch }) {
  const [benchmarks, setBenchmarks] = useState(null);
  const [period,     setPeriod]     = useState("1d");

  useEffect(() => {
    authFetch("/api/benchmarks").then(r => r.json()).then(d => setBenchmarks(d)).catch(() => {});
  }, []);

  // Compute portfolio today's weighted % change from live prices
  let totalVal = 0, totalChange = 0;
  for (const h of holdings) {
    const lp = livePrices[h.sym];
    if (!lp?.price || lp.change == null) continue;
    const priceCcy = h.priceCurrency || lp.currency || "USD";
    const priceUSD = toDisplay(lp.price, priceCcy, "USD", audUsd) || 0;
    const val = h.qty * priceUSD;
    totalVal    += val;
    totalChange += val * (lp.change / 100);
  }
  const portTodayPct = totalVal > 0 ? (totalChange / totalVal) * 100 : null;

  if (!benchmarks && portTodayPct === null) return null;

  const KEY = { "1d":"change1d", "1w":"change1w", "1m":"change1m", "3m":"change3m", "1y":"change1y" };
  const PLABEL = { "1d":"Today", "1w":"1 Week", "1m":"1 Month", "3m":"3 Months", "1y":"1 Year" };

  const rows = [
    { label:"Your Portfolio", sub:"today", pct: portTodayPct,                                        color:"var(--purple)" },
    benchmarks?.sp500  && { label:benchmarks.sp500.name,  sub:PLABEL[period].toLowerCase(), pct: benchmarks.sp500[KEY[period]]  * 100, color:"var(--blue)"  },
    benchmarks?.asx200 && { label:benchmarks.asx200.name, sub:PLABEL[period].toLowerCase(), pct: benchmarks.asx200[KEY[period]] * 100, color:"var(--amber)" },
  ].filter(Boolean);

  const maxAbs = Math.max(...rows.map(r => Math.abs(r.pct ?? 0)), 0.01);

  return (
    <div style={{ background:"var(--card)", border:"1px solid var(--border)", borderRadius:14, padding:20, marginBottom:24 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.12em" }}>BENCHMARK COMPARISON</div>
        <div className="pill-toggle">
          {["1d","1w","1m","3m","1y"].map(p => (
            <button key={p} onClick={() => setPeriod(p)} className={period === p ? "active" : ""}>
              {p.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display:"grid", gap:10 }}>
        {rows.map(row => (
          <div key={row.label} className="bench-row" style={{ display:"grid", gridTemplateColumns:"140px 76px 1fr", gap:12, alignItems:"center" }}>
            <div>
              <div style={{ fontSize:12, fontWeight:600, color:"var(--text2)" }}>{row.label}</div>
              <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"var(--ff-mono)" }}>{row.sub}</div>
            </div>
            <div style={{ fontSize:15, fontWeight:700, fontFamily:"var(--ff-mono)", textAlign:"right",
              color: row.pct == null ? "var(--muted)" : row.pct >= 0 ? "var(--green)" : "var(--red)" }}>
              {row.pct != null ? (row.pct >= 0 ? "+" : "") + row.pct.toFixed(2) + "%" : "—"}
            </div>
            <div className="bench-bar" style={{ position:"relative", height:6, background:"var(--surface)", borderRadius:4, overflow:"hidden" }}>
              {row.pct != null && (
                <div style={{
                  position:"absolute", height:"100%", borderRadius:4,
                  background: row.pct >= 0 ? "var(--green)" : "var(--red)",
                  width: `${Math.abs(row.pct) / maxAbs * 45}%`,
                  left:  row.pct >= 0 ? "50%" : `${50 - Math.abs(row.pct) / maxAbs * 45}%`,
                  transition: "all 0.4s ease",
                }}/>
              )}
              <div style={{ position:"absolute", left:"50%", top:0, width:1, height:"100%", background:"var(--border2)" }}/>
            </div>
          </div>
        ))}
      </div>

      {period !== "1d" && (
        <p style={{ fontSize:10, color:"var(--muted)", marginTop:10, fontFamily:"var(--ff-mono)" }}>
          * Portfolio shows today's return. Benchmarks show {PLABEL[period].toLowerCase()}.
        </p>
      )}
    </div>
  );
}

// ── Risk Metrics Panel ──────────────────────────────────────────────────────
function RiskPanel({ holdings, audUsd, authFetch }) {
  const { openGlossary } = useContext(GlossaryCtx);
  const [risk,       setRisk]       = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [expanded,   setExpanded]   = useState(true);

  const holdingsKey = holdings.map(h => `${h.sym}:${h.qty}`).join(",");

  useEffect(() => {
    if (!holdingsKey) return;
    setLoading(true);
    setHasFetched(false);
    const payload = holdings.map(h => ({ sym: h.sym, qty: h.qty, priceCurrency: h.priceCurrency || "USD" }));
    authFetch("/api/portfolio/risk", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ holdings: payload }),
    })
      .then(r => r.json()).then(d => setRisk(d)).catch(() => {})
      .finally(() => { setLoading(false); setHasFetched(true); });
  }, [holdingsKey]);

  if (!holdingsKey) return null;
  if (hasFetched && !loading && risk === null) return null;

  const stats = risk ? [
    {
      label: "Beta", glossaryTerm: "Beta",
      sub:   "vs S&P 500",
      value: risk.beta != null ? risk.beta.toFixed(2) : "—",
      color: risk.beta == null ? "var(--muted)"
           : risk.beta < 0.8  ? "var(--green)"
           : risk.beta > 1.2  ? "var(--red)"   : "var(--amber)",
    },
    {
      label: "Volatility", glossaryTerm: "Volatility",
      sub:   "annualised",
      value: risk.volatility != null ? `${risk.volatility.toFixed(1)}%` : "—",
      color: risk.volatility == null   ? "var(--muted)"
           : risk.volatility < 15      ? "var(--green)"
           : risk.volatility > 25      ? "var(--red)"   : "var(--amber)",
    },
    {
      label: "Sharpe", glossaryTerm: "Sharpe Ratio",
      sub:   "4.5% rf",
      value: risk.sharpe != null ? risk.sharpe.toFixed(2) : "—",
      color: risk.sharpe == null ? "var(--muted)"
           : risk.sharpe > 1    ? "var(--green)"
           : risk.sharpe > 0    ? "var(--amber)" : "var(--red)",
    },
    {
      label: "Max DD", glossaryTerm: "Maximum Drawdown",
      sub:   "1 year",
      value: risk.maxDrawdown != null ? `${risk.maxDrawdown.toFixed(1)}%` : "—",
      color: risk.maxDrawdown == null     ? "var(--muted)"
           : risk.maxDrawdown > -10       ? "var(--green)"
           : risk.maxDrawdown > -20       ? "var(--amber)" : "var(--red)",
    },
  ] : [];

  return (
    <div style={{ background:"var(--card)", border:"1px solid var(--border)", borderRadius:14, padding:20, marginBottom:24 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: expanded ? 16 : 0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.12em" }}>RISK METRICS</div>
          {!loading && risk?.nDays && (
            <span style={{ fontSize:10, color:"var(--muted)", fontFamily:"var(--ff-mono)" }}>{risk.nDays}d data</span>
          )}
        </div>
        <button onClick={() => setExpanded(e => !e)} style={{ background:"none", border:"none", color:"var(--muted)", fontSize:14, padding:"2px 6px", cursor:"pointer" }}>
          {expanded ? "▾" : "▸"}
        </button>
      </div>

      {expanded && (
        loading ? (
          <div className="risk-stats-grid" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
            {[1,2,3,4].map(i => <div key={i} className="shimmer-el" style={{ height:76 }}/>)}
          </div>
        ) : stats.length > 0 ? (
          <>
            <div className="risk-stats-grid" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
              {stats.map(s => (
                <div key={s.label} style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:10, padding:"14px 16px" }}>
                  <button onClick={() => openGlossary(s.glossaryTerm)} style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em", marginBottom:6, background:"none", border:"none", padding:0, cursor:"pointer", textDecoration:"underline dotted", textUnderlineOffset:3 }}>{s.label}</button>
                  <div className="risk-value" style={{ fontSize:22, fontWeight:800, fontFamily:"var(--ff-mono)", color:s.color, lineHeight:1 }}>{s.value}</div>
                  <div style={{ fontSize:10, color:"var(--muted)", marginTop:4 }}>{s.sub}</div>
                </div>
              ))}
            </div>
            <p style={{ fontSize:10, color:"var(--muted)", fontFamily:"var(--ff-mono)", marginTop:10 }}>
              Beta: sensitivity vs S&P 500 (&lt;1 = lower risk). Sharpe &gt;1 = good risk-adjusted return. Max DD = largest peak-to-trough drop.
            </p>
          </>
        ) : null
      )}
    </div>
  );
}

function AllocationPanel({ holdings, livePrices, audUsd, displayCcy }) {
  const canvasRef = useRef(null);

  // Compute sector breakdown and top holdings
  let totalUSD = 0;
  const sectorMap = {};
  const holdingValues = [];

  for (const h of holdings) {
    const lp = livePrices[h.sym];
    const priceCcy = h.priceCurrency || lp?.currency || "USD";
    const livePrice = lp?.price;
    const priceUSD = livePrice ? toDisplay(livePrice, priceCcy, "USD", audUsd) : null;
    const avgUSD   = toDisplay(h.avg, h.avgCurrency || "USD", "USD", audUsd);
    const valueUSD = priceUSD != null ? h.qty * priceUSD : h.qty * avgUSD;
    totalUSD += valueUSD;
    const sector = h.sector || (h.priceType === "crypto" ? "Crypto" : "Other");
    sectorMap[sector] = (sectorMap[sector] || 0) + valueUSD;
    holdingValues.push({ sym: h.sym, valueUSD, pct: 0 });
  }

  // Finalise holding weights
  holdingValues.forEach(h => { h.pct = totalUSD > 0 ? (h.valueUSD / totalUSD) * 100 : 0; });
  holdingValues.sort((a, b) => b.valueUSD - a.valueUSD);
  const top5 = holdingValues.slice(0, 5);

  // Sector data sorted descending
  const sectors = Object.entries(sectorMap)
    .map(([name, usd]) => ({ name, pct: totalUSD > 0 ? (usd / totalUSD) * 100 : 0 }))
    .sort((a, b) => b.pct - a.pct);

  // Draw donut on mount / data change — hooks must be before early returns
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || sectors.length === 0) return;
    const size = 160;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr; canvas.height = size * dpr;
    canvas.style.width = size + "px"; canvas.style.height = size + "px";
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size, size);
    const cx = size / 2, cy = size / 2;
    const outerR = size * 0.44, innerR = size * 0.26;
    let angle = -Math.PI / 2;
    sectors.forEach((s, i) => {
      const slice = (s.pct / 100) * 2 * Math.PI;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, outerR, angle, angle + slice);
      ctx.closePath();
      ctx.fillStyle = SECTOR_COLOURS[i % SECTOR_COLOURS.length];
      ctx.fill();
      angle += slice;
    });
    // Punch inner hole
    ctx.beginPath();
    ctx.arc(cx, cy, innerR, 0, 2 * Math.PI);
    ctx.fillStyle = "#1a1630";
    ctx.fill();
    // Centre label
    ctx.fillStyle = "#e8e0ff";
    ctx.font = `800 ${Math.round(size * 0.1)}px 'Outfit',sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(`${sectors.length}`, cx, cy - size * 0.04);
    ctx.fillStyle = "#8a80aa";
    ctx.font = `500 ${Math.round(size * 0.07)}px 'DM Sans',sans-serif`;
    ctx.fillText("sectors", cx, cy + size * 0.07);
  }, [sectors]);

  if (totalUSD === 0) return null;

  return (
    <div style={{ background:"var(--card)", border:"1px solid var(--border)", borderRadius:14, padding:20, marginBottom:24 }}>
      <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.12em", marginBottom:16 }}>ALLOCATION</div>
      <div className="alloc-panel-grid" style={{ display:"grid", gridTemplateColumns:"160px 1fr 1fr", gap:24, alignItems:"start" }}>

        {/* Donut */}
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:10 }}>
          <canvas ref={canvasRef} style={{ borderRadius:"50%", width:160, height:160 }}/>
        </div>

        {/* Sector bars */}
        <div>
          <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em", marginBottom:10 }}>BY SECTOR</div>
          {sectors.map((s, i) => (
            <div key={s.name} style={{ marginBottom:8 }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <span style={{ width:8, height:8, borderRadius:2, background:SECTOR_COLOURS[i % SECTOR_COLOURS.length], display:"inline-block", flexShrink:0 }}/>
                  <span style={{ fontSize:11, color:"var(--text2)", fontFamily:"var(--ff-mono)" }}>{s.name}</span>
                </div>
                <span style={{ fontSize:11, color:"var(--muted2)", fontFamily:"var(--ff-mono)" }}>{s.pct.toFixed(1)}%</span>
              </div>
              <div style={{ height:4, borderRadius:2, background:"var(--surface)", overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${s.pct}%`, background:SECTOR_COLOURS[i % SECTOR_COLOURS.length], borderRadius:2, transition:"width 0.4s ease" }}/>
              </div>
            </div>
          ))}
        </div>

        {/* Top holdings */}
        <div>
          <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em", marginBottom:10 }}>TOP HOLDINGS</div>
          {top5.map((h, i) => (
            <div key={h.sym} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
              <span style={{ fontSize:10, color:"var(--muted)", fontFamily:"var(--ff-mono)", width:14 }}>{i + 1}</span>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                  <span style={{ fontSize:11, color:"var(--text2)", fontFamily:"var(--ff-mono)", fontWeight:600 }}>{h.sym}</span>
                  <span style={{ fontSize:11, color:"var(--muted2)", fontFamily:"var(--ff-mono)" }}>{h.pct.toFixed(1)}%</span>
                </div>
                <div style={{ height:4, borderRadius:2, background:"var(--surface)", overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${h.pct}%`, background:"var(--purple)", borderRadius:2, transition:"width 0.4s ease" }}/>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Dividend Panel ──────────────────────────────────────────────────────────
function DividendPanel({ holdings, livePrices, audUsd, displayCcy, authFetch }) {
  const [divData,  setDivData]  = useState({});
  const [loading,  setLoading]  = useState(false);
  const [expanded, setExpanded] = useState(true);

  const stockSymKey = holdings.filter(h => h.priceType === "stock").map(h => h.sym).join(",");

  useEffect(() => {
    if (!stockSymKey) return;
    setLoading(true);
    authFetch("/api/portfolio/dividends", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ symbols: stockSymKey.split(",") }),
    })
      .then(r => r.json()).then(d => setDivData(d)).catch(() => {})
      .finally(() => setLoading(false));
  }, [stockSymKey]);

  if (!stockSymKey) return null;

  // uses global fmtDate helper

  // Build rows: one per dividend-paying stock, normalised to USD
  let totalIncomeUSD = 0;
  let totalStockUSD  = 0;
  const rows = holdings.filter(h => h.priceType === "stock").map(h => {
    const lp       = livePrices[h.sym];
    const priceCcy = h.priceCurrency || lp?.currency || "USD";
    const priceUSD = lp?.price ? toDisplay(lp.price, priceCcy, "USD", audUsd) : null;
    const avgUSD   = toDisplay(h.avg, h.avgCurrency || "USD", "USD", audUsd);
    const valueUSD = priceUSD != null ? h.qty * priceUSD : h.qty * avgUSD;
    totalStockUSD += valueUSD || 0;

    const dd          = divData[h.sym] || {};
    const yieldPct    = (dd.yield || 0) * 100;
    const divPerShare = dd.annualDivPerShare || 0;
    const incomeNative = divPerShare * h.qty;
    const incomeUSD    = toDisplay(incomeNative, priceCcy, "USD", audUsd) || incomeNative;
    const divUSD       = toDisplay(divPerShare, priceCcy, "USD", audUsd) || 0;
    const yieldOnCost  = avgUSD && divUSD ? (divUSD / avgUSD) * 100 : 0;
    totalIncomeUSD += incomeUSD || 0;
    return { h, yieldPct, incomeUSD, yieldOnCost, exDivDate: dd.exDivDate };
  }).filter(r => r.yieldPct > 0).sort((a, b) => b.yieldPct - a.yieldPct);

  const portfolioYieldPct = totalStockUSD > 0 ? (totalIncomeUSD / totalStockUSD) * 100 : 0;

  // Hide panel if data is loaded and no dividend payers found
  if (!loading && Object.keys(divData).length > 0 && rows.length === 0) return null;

  return (
    <div style={{ background:"var(--card)", border:"1px solid var(--border)", borderRadius:14, padding:20, marginBottom:24 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: expanded ? 16 : 0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.12em" }}>DIVIDENDS</div>
          {loading && <div className="shimmer-el" style={{ width:60, height:12, borderRadius:4 }}/>}
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:20 }}>
          {!loading && rows.length > 0 && (
            <>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em" }}>PROJECTED ANNUAL</div>
                <div style={{ fontSize:14, fontWeight:700, color:"var(--green)", fontFamily:"var(--ff-mono)" }}>
                  {fmtMoney(toDisplay(totalIncomeUSD, "USD", displayCcy, audUsd), displayCcy)}
                </div>
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em" }}>PORTFOLIO YIELD</div>
                <div style={{ fontSize:14, fontWeight:700, color:"var(--blue)", fontFamily:"var(--ff-mono)" }}>
                  {portfolioYieldPct.toFixed(2)}%
                </div>
              </div>
            </>
          )}
          <button onClick={() => setExpanded(e => !e)} style={{ background:"none", border:"none", color:"var(--muted)", fontSize:14, padding:"2px 6px", cursor:"pointer" }}>
            {expanded ? "▾" : "▸"}
          </button>
        </div>
      </div>

      {expanded && (
        loading ? (
          <div style={{ display:"grid", gap:8 }}>
            {[1,2,3].map(i => <div key={i} className="shimmer-el" style={{ height:48 }}/>)}
          </div>
        ) : rows.length > 0 ? (
          <div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 72px 110px 72px 105px", gap:8, padding:"0 4px 8px", borderBottom:"1px solid var(--border)" }}>
              {["HOLDING","YIELD","ANNUAL INCOME","YOC","EX-DIV DATE"].map(label => (
                <div key={label} style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em" }}>{label}</div>
              ))}
            </div>
            {rows.map(({ h, yieldPct, incomeUSD, yieldOnCost, exDivDate }) => (
              <div key={h.sym} style={{ display:"grid", gridTemplateColumns:"1fr 72px 110px 72px 105px", gap:8, padding:"10px 4px", borderBottom:"1px solid var(--border)" }}>
                <div>
                  <span style={{ fontSize:13, fontWeight:600, color:"var(--text2)", fontFamily:"var(--ff-mono)" }}>{h.sym}</span>
                  {h.name !== h.sym && <span style={{ fontSize:11, color:"var(--muted2)", marginLeft:8 }}>{h.name}</span>}
                </div>
                <div style={{ fontSize:13, color:"var(--green)", fontFamily:"var(--ff-mono)", fontWeight:600 }}>{yieldPct.toFixed(2)}%</div>
                <div style={{ fontSize:13, color:"var(--text2)", fontFamily:"var(--ff-mono)" }}>
                  {fmtMoney(toDisplay(incomeUSD, "USD", displayCcy, audUsd), displayCcy)}
                </div>
                <div style={{ fontSize:13, color:"var(--amber)", fontFamily:"var(--ff-mono)" }}>
                  {yieldOnCost > 0 ? `${yieldOnCost.toFixed(2)}%` : "—"}
                </div>
                <div style={{ fontSize:11, color:"var(--muted2)", fontFamily:"var(--ff-mono)" }}>{fmtDate(exDivDate)}</div>
              </div>
            ))}
          </div>
        ) : null
      )}
    </div>
  );
}

// ── Holding row ────────────────────────────────────────────────────────────
function HoldingRow({ holding, livePrice, expanded, onToggle, onRemove, onViewChart, onTrade, displayCcy, audUsd, portWeight, dividendInfo }) {
  const priceCcy  = holding.priceCurrency || livePrice?.currency || "USD";
  const rawPrice  = livePrice?.price || null;
  const dispPrice = rawPrice ? toDisplay(rawPrice, priceCcy, displayCcy, audUsd) : null;
  const dispAvg   = toDisplay(holding.avg, holding.avgCurrency || "USD", displayCcy, audUsd);
  const dispValue = dispPrice != null ? holding.qty * dispPrice : null;
  const pnl = calcPnl(holding, livePrice, audUsd);
  const accent = pnl.raw > 5 ? "var(--green)" : pnl.raw < -5 ? "var(--red)" : "var(--amber)";

  return (
    <div className="holding-row" style={{ borderLeft:`3px solid ${rawPrice ? accent : "var(--border)"}` }} onClick={onToggle}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, flexWrap:"wrap" }}>
        <div style={{ display:"flex", gap:14, alignItems:"center", minWidth:0 }}>
          <div style={{ width:40, height:40, borderRadius:10, background:`${accent}18`, border:`1px solid ${accent}30`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <span style={{ fontFamily:"var(--ff-head)", fontSize:11, fontWeight:800, color:accent }}>{holding.sym.replace(".AX","").slice(0,3)}</span>
          </div>
          <div>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4, flexWrap:"wrap" }}>
              <span style={{ fontFamily:"var(--ff-head)", fontSize:15, fontWeight:700, color:"var(--text2)", cursor: onViewChart ? "pointer" : "default", textDecoration: onViewChart ? "underline dotted" : "none", textDecorationColor:"var(--muted)" }} onClick={e => { if(onViewChart){ e.stopPropagation(); onViewChart(); } }}>{holding.sym}</span>
              <SourceBadge source={holding.source}/>
              <SectorBadge sector={holding.sector}/>
              {portWeight != null && portWeight >= 20 && (
                <span style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--amber)", background:"#ffab4015", border:"1px solid #ffab4040", borderRadius:4, padding:"2px 6px", letterSpacing:"0.06em" }}>⚠ CONCENTRATED</span>
              )}
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:12, color:"var(--muted2)" }}>{holding.name}</span>
              {portWeight != null && <span style={{ fontSize:10, color:"var(--muted)", fontFamily:"var(--ff-mono)" }}>{portWeight.toFixed(1)}% of portfolio</span>}
            </div>
          </div>
        </div>
        <div style={{ display:"flex", gap:24, alignItems:"flex-start" }}>
          <div style={{ textAlign:"right" }}>
            <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em", marginBottom:4 }}>VALUE ({displayCcy})</div>
            {dispValue != null
              ? <div style={{ fontFamily:"var(--ff-mono)", fontSize:15, fontWeight:500, color:"var(--text2)" }}>{fmtMoney(dispValue, displayCcy)}</div>
              : <div className="shimmer-el" style={{ width:72, height:15 }}/>}
            <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"var(--ff-mono)", marginTop:2 }}>
              {holding.qty % 1 === 0 ? holding.qty : holding.qty.toFixed(4)} × {dispPrice ? fmtMoney(dispPrice, displayCcy) : dispAvg ? fmtMoney(dispAvg, displayCcy) : "—"}
            </div>
          </div>
          <div style={{ textAlign:"right", minWidth:70 }}>
            <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em", marginBottom:4 }}>P&L</div>
            {pnl.valid && holding.avg > 0 && dispPrice != null && dispAvg != null && (() => { const pnlDollar = holding.qty * (dispPrice - dispAvg); return <div style={{ fontFamily:"var(--ff-mono)", fontSize:15, fontWeight:600, color:pnlDollar>=0?"var(--green)":"var(--red)" }}>{pnlDollar>=0?"+":"−"}{fmtMoney(Math.abs(pnlDollar), displayCcy)}</div>; })()}
            <div style={{ fontSize:10, color:pnl.up?"var(--green)":"var(--red)", marginTop:2, fontFamily:"var(--ff-mono)" }}>{pnl.pct}</div>
            {livePrice && <div style={{ fontSize:10, color:livePrice.up?"var(--green)":"var(--red)", marginTop:2, fontFamily:"var(--ff-mono)" }}>{livePrice.changeStr} today</div>}
          </div>
        </div>
      </div>
      {expanded && (
        <div style={{ marginTop:16, paddingTop:16, borderTop:"1px solid var(--border)" }} onClick={e => e.stopPropagation()}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", gap:12 }}>
          <div style={{ display:"flex", gap:24, flexWrap:"wrap" }}>
            {[
              { l:"LIVE PRICE",   v:dispPrice ? fmtMoney(dispPrice, displayCcy) : "Loading…" },
              { l:"AVG COST",     v:dispAvg   ? fmtMoney(dispAvg, displayCcy)   : "—" },
              { l:"QUANTITY",     v:holding.qty % 1 === 0 ? `${holding.qty}` : holding.qty.toFixed(6) },
              { l:"MARKET VALUE", v:fmtMoney(dispValue, displayCcy) },
              { l:"TODAY",        v:livePrice?.changeStr || "—" },
              { l:"PRICE CCY",    v:priceCcy },
            ].map(f => (
              <div key={f.l}>
                <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em", marginBottom:4 }}>{f.l}</div>
                <div style={{ fontSize:13, fontFamily:"var(--ff-mono)", color:f.color||"var(--text2)", fontWeight:500 }}>{f.v}</div>
              </div>
            ))}
          </div>
          <div style={{ display:"flex", gap:8, flexShrink:0 }}>
            {onViewChart && (
              <button onClick={e=>{e.stopPropagation();onViewChart();}} style={{ background:"none", border:"1px solid var(--border)", borderRadius:8, padding:"7px 14px", color:"var(--muted2)", fontSize:11, fontFamily:"var(--ff-mono)", letterSpacing:"0.06em" }}>
                CHART
              </button>
            )}
            {(holding.source === "coinbase" || holding.source === "binance" || holding.source === "tiger") && onTrade && (
              <>
                <button onClick={e=>{e.stopPropagation();onTrade(holding.source==="tiger"?{sym:holding.sym,name:holding.name,side:"BUY",priceType:"stock",exchange:"tiger"}:{sym:holding.sym,name:holding.name,productId:`${holding.sym}-USD`,side:"BUY",priceType:"crypto",exchange:holding.source});}} style={{ background:"#00e67610", border:"1px solid #00e67640", borderRadius:8, padding:"7px 14px", color:"var(--green)", fontSize:11, fontFamily:"var(--ff-mono)", letterSpacing:"0.06em" }}>
                  BUY
                </button>
                <button onClick={e=>{e.stopPropagation();onTrade(holding.source==="tiger"?{sym:holding.sym,name:holding.name,side:"SELL",priceType:"stock",exchange:"tiger"}:{sym:holding.sym,name:holding.name,productId:`${holding.sym}-USD`,side:"SELL",priceType:"crypto",exchange:holding.source});}} style={{ background:"#ff525218", border:"1px solid #ff525240", borderRadius:8, padding:"7px 14px", color:"var(--red)", fontSize:11, fontFamily:"var(--ff-mono)", letterSpacing:"0.06em" }}>
                  SELL
                </button>
              </>
            )}
            {onRemove && (
              <button onClick={e=>{e.stopPropagation();onRemove();}} style={{ background:"#ff525218", border:"1px solid #ff525240", borderRadius:8, padding:"7px 14px", color:"var(--red)", fontSize:11, fontFamily:"var(--ff-mono)", letterSpacing:"0.06em" }}>
                REMOVE
              </button>
            )}
          </div>
          </div>
          {dividendInfo && dividendInfo.yield > 0 && (
            <div style={{ marginTop:16, paddingTop:14, borderTop:"1px solid var(--border)" }}>
              <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.12em", marginBottom:12 }}>DIVIDENDS</div>
              <div style={{ display:"flex", gap:24, flexWrap:"wrap" }}>
                {dividendInfo.mostRecentDividend > 0 && (
                  <div>
                    <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em", marginBottom:4 }}>MOST RECENT</div>
                    <div style={{ fontSize:13, fontFamily:"var(--ff-mono)", color:"var(--text2)", fontWeight:500 }}>{fmtMoney(dividendInfo.mostRecentDividend, priceCcy)}</div>
                  </div>
                )}
                <div>
                  <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em", marginBottom:4 }}>YIELD</div>
                  <div style={{ fontSize:13, fontFamily:"var(--ff-mono)", color:"var(--green)", fontWeight:600 }}>{(dividendInfo.yield * 100).toFixed(2)}%</div>
                </div>
                {dividendInfo.frankingPct != null && dividendInfo.frankingPct > 0 && (
                  <div>
                    <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em", marginBottom:4 }}>FRANKING</div>
                    <div style={{ fontSize:13, fontFamily:"var(--ff-mono)", color:"var(--blue)", fontWeight:500 }}>{dividendInfo.frankingPct.toFixed(0)}%</div>
                  </div>
                )}
                {dividendInfo.annualDivPerShare > 0 && (
                  <div>
                    <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em", marginBottom:4 }}>ANNUAL DPS</div>
                    <div style={{ fontSize:13, fontFamily:"var(--ff-mono)", color:"var(--text2)", fontWeight:500 }}>{fmtMoney(dividendInfo.annualDivPerShare, priceCcy)}</div>
                  </div>
                )}
                {dividendInfo.exDivDate && (
                  <div>
                    <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em", marginBottom:4 }}>{new Date(dividendInfo.exDivDate) < new Date() ? "LAST EX-DIV" : "EX-DIV DATE"}</div>
                    <div style={{ fontSize:13, fontFamily:"var(--ff-mono)", color:"var(--amber)", fontWeight:500 }}>{fmtDate(dividendInfo.exDivDate)}</div>
                  </div>
                )}
                {dividendInfo.paymentDate && (
                  <div>
                    <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em", marginBottom:4 }}>{new Date(dividendInfo.paymentDate) < new Date() ? "LAST PAY DATE" : "PAY DATE"}</div>
                    <div style={{ fontSize:13, fontFamily:"var(--ff-mono)", color:"var(--text2)", fontWeight:500 }}>{fmtDate(dividendInfo.paymentDate)}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Source panel ───────────────────────────────────────────────────────────
function SourcePanel({ label, color, holdings, livePrices, syncing, lastSync, error, onSync, onRemove, onViewChart, onTrade, displayCcy, audUsd }) {
  const [portExp, setPortExp] = useState(null);
  let totalValueUSD = 0, totalCostUSD = 0;
  for (const h of holdings) {
    const lp = livePrices[h.sym];
    const priceCcy = h.priceCurrency || lp?.currency || "USD";
    const priceUSD = lp?.price ? toDisplay(lp.price, priceCcy, "USD", audUsd) : null;
    const avgUSD   = toDisplay(h.avg, h.avgCurrency || "USD", "USD", audUsd);
    totalValueUSD += priceUSD != null ? h.qty * priceUSD : h.qty * avgUSD;
    totalCostUSD  += h.qty * avgUSD;
  }
  const pnl = totalCostUSD > 0 ? ((totalValueUSD - totalCostUSD) / totalCostUSD) * 100 : 0;
  const totalDisp = toDisplay(totalValueUSD, "USD", displayCcy, audUsd);

  return (
    <div style={{ marginBottom:28 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, flexWrap:"wrap", gap:10 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
          <span style={{ width:10, height:10, borderRadius:"50%", background:color, boxShadow:`0 0 8px ${color}80` }}/>
          <span style={{ fontSize:13, fontFamily:"var(--ff-head)", fontWeight:700, color }}>{label}</span>
          {holdings.length > 0 && (
            <span style={{ fontSize:11, fontFamily:"var(--ff-mono)", color:"var(--muted2)" }}>
              {holdings.length} positions · {fmtMoney(totalDisp, displayCcy)} · <span style={{ color:pnl>=0?"var(--green)":"var(--red)" }}>{pnl>=0?"+":""}{pnl.toFixed(1)}%</span>
            </span>
          )}
          {lastSync && <span style={{ fontSize:10, fontFamily:"var(--ff-mono)", color:"var(--muted)" }}>synced {lastSync}</span>}
        </div>
        {onSync && (
          <button onClick={onSync} disabled={syncing} style={{ background:syncing?"none":"#00e67610", border:`1px solid ${syncing?"var(--border)":"#00e67640"}`, borderRadius:8, padding:"7px 16px", fontSize:10, color:syncing?"var(--muted)":"var(--green)", fontFamily:"var(--ff-mono)", letterSpacing:"0.06em", opacity:syncing?.5:1 }}>
            {syncing ? "↻ SYNCING…" : "↻ SYNC"}
          </button>
        )}
      </div>
      {error && (
        <div style={{ background:"#ff525212", border:"1px solid #ff525230", borderRadius:10, padding:"14px 18px", marginBottom:14 }}>
          <div style={{ fontSize:10, fontFamily:"var(--ff-mono)", color:"var(--red)", letterSpacing:"0.08em", marginBottom:5 }}>CONNECTION ERROR</div>
          <p style={{ fontSize:12, color:"var(--muted2)", lineHeight:1.6 }}>{error}</p>
        </div>
      )}
      {holdings.length > 0 ? (
        <div style={{ display:"grid", gap:8 }}>
          {holdings.map(h => (
            <HoldingRow key={h.sym} holding={h} livePrice={livePrices[h.sym]} expanded={portExp===h.sym} onToggle={()=>setPortExp(p=>p===h.sym?null:h.sym)} onRemove={()=>onRemove(h.sym)} onViewChart={onViewChart?()=>onViewChart(h):null} onTrade={onTrade} displayCcy={displayCcy} audUsd={audUsd}/>
          ))}
        </div>
      ) : !error && (
        <div style={{ background:"var(--card)", border:"1px solid var(--border)", borderRadius:12, padding:"32px", textAlign:"center" }}>
          <p style={{ color:"var(--muted2)", fontSize:13 }}>{syncing ? "Connecting…" : lastSync ? "No open positions" : "No holdings found. Check your API keys in .env"}</p>
        </div>
      )}
    </div>
  );
}

// ── Ledger address manager ────────────────────────────────────────────────
function LedgerAddressManager({ addresses, onAdd, onRemove }) {
  const [chain, setChain] = useState("BTC");
  const [addr, setAddr] = useState("");
  const [error, setError] = useState("");

  function validate(chain, addr) {
    if (chain === "BTC") return /^(1|3|bc1)[a-zA-HJ-NP-Z0-9]{25,62}$/.test(addr);
    if (chain === "ETH") return /^0x[a-fA-F0-9]{40}$/.test(addr);
    if (chain === "SOL") return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(addr);
    return false;
  }

  function handleAdd() {
    setError("");
    const trimmed = addr.trim();
    if (!trimmed) return;
    if (!validate(chain, trimmed)) { setError(`Invalid ${chain} address`); return; }
    if (addresses.some(a => a.address === trimmed && a.chain === chain)) { setError("Address already added"); return; }
    onAdd({ address: trimmed, chain });
    setAddr("");
  }

  const chains = ["BTC", "ETH", "SOL"];
  const chainColors = { BTC: "#f7931a", ETH: "#627eea", SOL: "#9945ff" };

  return (
    <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, padding: 24, marginTop: 18 }}>
      <div style={{ fontSize: 11, fontWeight: 700, fontFamily: "var(--ff-head)", color: "var(--muted2)", letterSpacing: 1, marginBottom: 16 }}>ADD WALLET ADDRESS</div>
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        {chains.map(c => (
          <button key={c} onClick={() => setChain(c)} style={{ background: chain === c ? `${chainColors[c]}20` : "none", border: `1px solid ${chain === c ? chainColors[c] : "var(--border)"}`, borderRadius: 8, padding: "6px 16px", fontSize: 12, fontWeight: chain === c ? 700 : 400, color: chain === c ? chainColors[c] : "var(--muted2)", fontFamily: "var(--ff-mono)" }}>{c}</button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <input value={addr} onChange={e => setAddr(e.target.value)} onKeyDown={e => e.key === "Enter" && handleAdd()} placeholder={`Paste your ${chain} public address…`} style={{ flex: 1, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "var(--text)", fontFamily: "var(--ff-mono)" }} />
        <button onClick={handleAdd} style={{ background: "#41a5f518", border: "1px solid #41a5f550", borderRadius: 8, padding: "10px 18px", fontSize: 12, fontWeight: 600, color: "#41a5f5" }}>+ Add</button>
      </div>
      {error && <div style={{ fontSize: 12, color: "var(--red)", marginBottom: 10 }}>{error}</div>}
      {addresses.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, fontFamily: "var(--ff-head)", color: "var(--muted2)", letterSpacing: 1, marginBottom: 10 }}>SAVED ADDRESSES</div>
          {addresses.map((a, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: chainColors[a.chain] }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: chainColors[a.chain], fontFamily: "var(--ff-mono)", minWidth: 32 }}>{a.chain}</span>
              <span style={{ fontSize: 12, color: "var(--text2)", fontFamily: "var(--ff-mono)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.address}</span>
              <button onClick={() => onRemove(i)} style={{ background: "none", border: "none", color: "var(--muted)", fontSize: 16, cursor: "pointer", padding: "2px 6px" }}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── CMC import ─────────────────────────────────────────────────────────────
function CMCImport({ onImport }) {
  const [drag,setDrag]=useState(false), [error,setError]=useState(""), [busy,setBusy]=useState(false);
  const ref = useRef(null);
  function process(file) {
    if (!file) return;
    if (!file.name.endsWith(".csv")) { setError("Please upload a .csv file"); return; }
    setBusy(true); setError("");
    const r = new FileReader();
    r.onload = e => { try { onImport(parseCMCcsv(e.target.result)); } catch(err) { setError(err.message); } setBusy(false); };
    r.readAsText(file);
  }
  return (
    <div>
      <div className={`drop-zone${drag?" drag":""}`} onDragOver={e=>{e.preventDefault();setDrag(true);}} onDragLeave={()=>setDrag(false)} onDrop={e=>{e.preventDefault();setDrag(false);process(e.dataTransfer.files[0]);}} onClick={()=>ref.current?.click()}>
        <input ref={ref} type="file" accept=".csv" style={{display:"none"}} onChange={e=>process(e.target.files[0])}/>
        <div style={{fontSize:28,color:"var(--green)",marginBottom:10}}>↑</div>
        <div style={{fontSize:14,fontWeight:700,color:"var(--text2)",fontFamily:"var(--ff-head)",marginBottom:5}}>{busy?"Importing…":"Tap to select or drop your CMC CSV"}</div>
        <div style={{fontSize:12,color:"var(--muted2)"}}>Account → Reports → Portfolio Report → Download CSV</div>
      </div>
      {error && <div style={{marginTop:10,background:"#ff525212",border:"1px solid #ff525230",borderRadius:8,padding:"10px 14px",fontSize:12,color:"var(--red)"}}>{error}</div>}
    </div>
  );
}

// ── Reasoning chain ────────────────────────────────────────────────────────
function ReasoningChain({ stock }) {
  const [open,setOpen]=useState(null);
  const layers=[
    {key:"macro",       icon:"◎",label:"MACRO ENVIRONMENT"},
    {key:"fundamental", icon:"◈",label:"FUNDAMENTALS"},
    {key:"technical",   icon:"◇",label:"TECHNICAL ANALYSIS"},
    {key:"sentiment",   icon:"◉",label:"MARKET SENTIMENT"},
    {key:"insider",     icon:"◐",label:"INSIDER ACTIVITY"},
    {key:"portfolio",   icon:"◑",label:"PORTFOLIO FIT"},
  ];
  return (
    <div style={{marginTop:20,paddingTop:20,borderTop:"1px solid var(--border)"}}>
      <div className="section-label">REASONING CHAIN</div>
      {layers.map((l,i)=>(
        <div key={l.key} style={{marginBottom:4}}>
          {i>0&&<div style={{width:2,height:8,background:"var(--border2)",marginLeft:18,marginBottom:4}}/>}
          <button onClick={()=>setOpen(open===l.key?null:l.key)} style={{width:"100%",background:open===l.key?"#00e67608":"none",border:`1px solid ${open===l.key?"#00e67640":"var(--border)"}`,borderRadius:10,padding:"10px 16px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:14,color:open===l.key?"var(--green)":"var(--muted2)"}}>{l.icon}</span>
              <span style={{fontSize:11,fontFamily:"var(--ff-mono)",color:open===l.key?"var(--green)":"var(--muted2)",letterSpacing:"0.08em"}}>{l.label}</span>
            </span>
            <span style={{fontSize:12,color:"var(--muted)",transform:open===l.key?"rotate(180deg)":"none",transition:"transform .2s",display:"inline-block"}}>▾</span>
          </button>
          {open===l.key&&(
            <div style={{background:"var(--surface)",border:"1px solid #00e67625",borderTop:"none",borderRadius:"0 0 10px 10px",padding:"14px 18px"}}>
              <p style={{fontSize:13,color:"var(--muted2)",lineHeight:1.7}}><LinkedText>{stock[l.key]}</LinkedText></p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Stock card ─────────────────────────────────────────────────────────────
function StockCard({ stock, expanded, onToggle, livePrices, displayCcy, audUsd, onAddWatchlist, inWatchlist, onViewChart, onTrade }) {
  const accent = {BUY:"var(--green)",WATCH:"var(--amber)",AVOID:"var(--red)",HOLD:"var(--border2)"}[stock.verdict]||"var(--border2)";
  const ld = livePrices?.[stock.sym];
  const priceCcy = ld?.currency || stock.priceCurrency || "USD";
  const rawPrice = ld?.price || stock.priceStatic;
  const dispPrice = toDisplay(rawPrice, priceCcy, displayCcy, audUsd);

  return (
    <div className="card" style={{borderLeft:`3px solid ${accent}`,padding:22,marginBottom:10}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6,flexWrap:"wrap"}}>
            <span style={{fontFamily:"var(--ff-head)",fontSize:20,fontWeight:800,color:"var(--text2)"}}>{stock.sym}</span>
            <span style={{fontSize:13,color:"var(--muted2)"}}>{stock.name}</span>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",marginBottom:10}}>
            <SectorBadge sector={stock.sector}/>
            <ConvictionDots level={stock.conviction}/>
            <span className="badge" style={{background:"var(--surface)",color:"var(--muted2)",border:"1px solid var(--border)"}}>{stock.horizon} term</span>
          </div>
          <VerdictBadge v={stock.verdict}/>
        </div>
        <div style={{textAlign:"right",minWidth:0}}>
          <div style={{fontFamily:"var(--ff-mono)",fontSize:20,fontWeight:600,color:"var(--text2)",marginBottom:3,whiteSpace:"nowrap"}}>{fmtMoney(dispPrice,displayCcy)}</div>
          {ld && !ld.error
            ? <div style={{fontFamily:"var(--ff-mono)",fontSize:12,color:ld.up?"var(--green)":"var(--red)"}}>{ld.changeStr}</div>
            : <div style={{fontFamily:"var(--ff-mono)",fontSize:12,color:stock.up?"var(--green)":"var(--red)"}}>{stock.upside}</div>}
          <div style={{fontSize:10,color:"var(--muted)",marginTop:4,fontFamily:"var(--ff-mono)"}}>tgt {(() => { const tn = parseTargetNum(stock.target); if (tn == null) return stock.target; return fmtMoney(toDisplay(tn, priceCcy, displayCcy, audUsd), displayCcy); })()}</div>
        </div>
      </div>
      <p style={{fontSize:13,color:"var(--muted2)",lineHeight:1.7,marginTop:14}}><LinkedText>{stock.summary}</LinkedText></p>
      {expanded&&<ReasoningChain stock={stock}/>}
      <div style={{marginTop:14,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <button onClick={onToggle} style={{background:"none",border:"none",fontSize:10,color:"var(--green)",fontFamily:"var(--ff-mono)",padding:0,letterSpacing:"0.08em",display:"flex",alignItems:"center",gap:5}}>
          <span style={{transform:expanded?"rotate(180deg)":"none",transition:"transform .2s",display:"inline-block"}}>▾</span>
          {expanded?"COLLAPSE":"REASONING CHAIN"}
        </button>
        {onAddWatchlist && (
          <button onClick={onAddWatchlist} style={{background:inWatchlist?"#00e67612":"none",border:`1px solid ${inWatchlist?"#00e67640":"var(--border)"}`,borderRadius:7,padding:"5px 14px",fontSize:10,color:inWatchlist?"var(--green)":"var(--muted2)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em"}}>
            {inWatchlist ? "✓ WATCHLIST" : "+ WATCHLIST"}
          </button>
        )}
        {onViewChart && (
          <button onClick={onViewChart} style={{background:"none",border:"1px solid var(--border)",borderRadius:7,padding:"5px 14px",fontSize:10,color:"var(--muted2)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em"}}>
            CHART →
          </button>
        )}
        {onTrade && (
          <button onClick={onTrade} style={{background:"#00e67610",border:"1px solid #00e67640",borderRadius:7,padding:"5px 14px",fontSize:10,color:"var(--green)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em",fontWeight:700}}>
            ↔ TRADE
          </button>
        )}
      </div>
    </div>
  );
}

// ── News card ──────────────────────────────────────────────────────────────
function NewsCard({ item }) {
  const [open,setOpen]=useState(false);
  const tagC={"US TECH":"var(--blue)","ASX MINING":"var(--amber)","CRYPTO":"var(--purple)","MARKETS":"var(--muted2)"};
  const sentC={BULLISH:"var(--green)",BEARISH:"var(--red)",NEUTRAL:"var(--muted2)"};
  return (
    <div className="card" style={{padding:20,marginBottom:10}}>
      <div style={{display:"flex",justifyContent:"space-between",gap:12,alignItems:"flex-start"}}>
        <div style={{flex:1}}>
          <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:10,flexWrap:"wrap"}}>
            <span className="badge" style={{background:`${tagC[item.tag]||"var(--muted)"}20`,color:tagC[item.tag]||"var(--muted2)",border:`1px solid ${tagC[item.tag]||"var(--border)"}40`}}>{item.tag}</span>
            <span className="badge" style={{background:`${sentC[item.sentiment]}15`,color:sentC[item.sentiment],border:`1px solid ${sentC[item.sentiment]}35`}}>{item.sentiment}</span>
            <span style={{fontSize:10,color:"var(--muted)",fontFamily:"var(--ff-mono)"}}>{item.time}</span>
            {item.live && <span className="badge" style={{background:"#00e67612",color:"var(--green)",border:"1px solid #00e67630"}}>LIVE</span>}
          </div>
          <p style={{fontSize:14,fontWeight:600,fontFamily:"var(--ff-head)",lineHeight:1.45,color:"var(--text2)",marginBottom:10}}>{item.headline}</p>
          {item.affected.length > 0 && (
            <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>{item.affected.map(s=><span key={s} className="badge" style={{background:"var(--surface)",color:"var(--muted2)",border:"1px solid var(--border)"}}>{s}</span>)}</div>
          )}
        </div>
        <span className="badge" style={{background:"var(--surface)",color:"var(--muted2)",border:"1px solid var(--border)",flexShrink:0}}>IMPACT {item.impact}</span>
      </div>
      {item.commentary && (
        <>
          {open&&<div style={{marginTop:16,paddingTop:16,borderTop:"1px solid var(--border)"}}><div className="section-label">AI ANALYSIS</div><p style={{fontSize:13,color:"var(--muted2)",lineHeight:1.7}}><LinkedText>{item.commentary}</LinkedText></p></div>}
          <button onClick={()=>setOpen(!open)} style={{marginTop:14,background:"none",border:"none",fontSize:10,color:"var(--green)",fontFamily:"var(--ff-mono)",padding:0,letterSpacing:"0.08em",display:"flex",alignItems:"center",gap:5}}>
            <span style={{transform:open?"rotate(180deg)":"none",transition:"transform .2s",display:"inline-block"}}>▾</span>
            {open?"HIDE ANALYSIS":"AI ANALYSIS"}
          </button>
        </>
      )}
      {item.link && (
        <a href={item.link} target="_blank" rel="noopener noreferrer" style={{display:"inline-block",marginTop:item.commentary?8:14,fontSize:10,color:"var(--muted)",fontFamily:"var(--ff-mono)",textDecoration:"none",letterSpacing:"0.06em"}}>READ FULL STORY →</a>
      )}
    </div>
  );
}


// ── IPO Card ───────────────────────────────────────────────────────────────
function IpoCard({ ipo, onAnalyse }) {
  const statusColor = { expected:"var(--green)", priced:"var(--blue)", filed:"var(--amber)", withdrawn:"var(--red)" };
  const statusLabel = { expected:"EXPECTED", priced:"PRICED", filed:"FILED", withdrawn:"WITHDRAWN" };
  const sc = statusColor[ipo.status] || "var(--muted2)";
  const sl = statusLabel[ipo.status] || (ipo.status||"").toUpperCase();
  const isASX = ipo.exchange && ipo.exchange.toUpperCase().includes("ASX");
  const exchColor = isASX ? "var(--amber)" : "var(--blue)";
  const exchLabel = isASX ? "ASX" : "US";

  function relDate(dateStr) {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = d - now;
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Tomorrow";
    if (diffDays === -1) return "Yesterday";
    if (diffDays > 0 && diffDays <= 30) return `in ${diffDays} days`;
    if (diffDays < 0 && diffDays >= -30) return `${Math.abs(diffDays)} days ago`;
    return fmtDate(dateStr);
  }

  const shares = ipo.numberOfShares ? (ipo.numberOfShares / 1e6).toFixed(1) + "M shares" : null;
  const raise = ipo.totalSharesValue && ipo.totalSharesValue > 1e8
    ? "$" + (ipo.totalSharesValue / 1e9).toFixed(2) + "B raise"
    : null;

  return (
    <div className="card" style={{padding:20,marginBottom:10,borderLeft:`3px solid ${sc}`}}>
      <div style={{display:"flex",justifyContent:"space-between",gap:12,alignItems:"flex-start",flexWrap:"wrap"}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:8,flexWrap:"wrap"}}>
            <span style={{fontFamily:"var(--ff-head)",fontSize:15,fontWeight:700,color:"var(--text2)"}}>{ipo.name || ipo.symbol || "Unknown"}</span>
            {ipo.symbol && <span className="badge" style={{background:"var(--surface)",color:"var(--muted2)",border:"1px solid var(--border)",fontFamily:"var(--ff-mono)"}}>{ipo.symbol}</span>}
            <span className="badge" style={{background:`${sc}20`,color:sc,border:`1px solid ${sc}40`}}>{sl}</span>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
            <span className="badge" style={{background:`${exchColor}20`,color:exchColor,border:`1px solid ${exchColor}40`}}>{exchLabel}</span>
            <span style={{fontSize:11,color:"var(--muted2)",fontFamily:"var(--ff-mono)"}}>{relDate(ipo.date)}</span>
            {ipo.price && <span style={{fontSize:11,color:"var(--muted2)",fontFamily:"var(--ff-mono)"}}>@ {ipo.price}</span>}
            {shares && <span style={{fontSize:11,color:"var(--muted)",fontFamily:"var(--ff-mono)"}}>{shares}</span>}
            {raise  && <span style={{fontSize:11,color:"var(--green)",fontFamily:"var(--ff-mono)",fontWeight:600}}>{raise}</span>}
          </div>
        </div>
        {ipo.symbol && (
          <button onClick={()=>onAnalyse(ipo.symbol)} style={{background:"none",border:"1px solid var(--green)50",borderRadius:8,padding:"7px 14px",fontSize:10,color:"var(--green)",fontFamily:"var(--ff-mono)",letterSpacing:"0.08em",flexShrink:0,whiteSpace:"nowrap"}}>
            ANALYSE →
          </button>
        )}
      </div>
    </div>
  );
}

// ── Call Card ──────────────────────────────────────────────────────────────
function CallCard({ record, currentPriceData, onAnalyse, onRemove, priceFetching }) {
  const verdictAccent = { BUY:"var(--green)", WATCH:"var(--amber)", AVOID:"var(--red)", HOLD:"var(--muted2)", SELL:"var(--red)" };
  const accent = verdictAccent[record.verdict] || "var(--muted2)";
  const sourceColor = record.source === "explorer" ? "var(--blue)" : "var(--purple)";
  const sourceLabel = record.source === "explorer" ? "EXPLORER" : "DASHBOARD";

  const currentPrice = currentPriceData?.price ?? null;
  const priceAtCall  = record.priceAtCall;
  const returnPct    = (currentPrice != null && priceAtCall != null && priceAtCall > 0)
    ? ((currentPrice - priceAtCall) / priceAtCall) * 100
    : null;

  const isWin = returnPct != null && (
    (["BUY","HOLD"].includes(record.verdict) && returnPct > 0) ||
    (["AVOID","SELL"].includes(record.verdict) && returnPct < 0)
  );

  function ageSince(isoStr) {
    const ms = Date.now() - new Date(isoStr).getTime();
    const mins  = Math.floor(ms / 60000);
    const hours = Math.floor(ms / 3600000);
    const days  = Math.floor(ms / 86400000);
    if (mins  <  60) return `${mins}m ago`;
    if (hours <  24) return `${hours}h ago`;
    if (days  < 365) return `${days}d ago`;
    return `${Math.floor(days / 365)}y ago`;
  }

  function fmtP(p) {
    if (p == null) return "—";
    if (p >= 100000) return `$${(p/1000).toFixed(0)}k`;
    if (p >= 10000)  return `$${(p/1000).toFixed(1)}k`;
    if (p >= 1000)   return `$${p.toLocaleString("en",{maximumFractionDigits:0})}`;
    if (p >= 1)      return `$${p.toFixed(2)}`;
    return `$${p.toFixed(4)}`;
  }

  return (
    <div className="card" style={{ padding:18, marginBottom:10, borderLeft:`3px solid ${accent}` }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, flexWrap:"wrap" }}>
        {/* Left */}
        <div style={{ display:"flex", gap:12, alignItems:"center", minWidth:0, flex:1 }}>
          <div style={{ width:40, height:40, borderRadius:10, background:`${accent}18`, border:`1px solid ${accent}30`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <span style={{ fontFamily:"var(--ff-head)", fontSize:11, fontWeight:800, color:accent }}>{record.sym.replace(".AX","").slice(0,3)}</span>
          </div>
          <div style={{ minWidth:0 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4, flexWrap:"wrap" }}>
              <span style={{ fontFamily:"var(--ff-head)", fontSize:15, fontWeight:700, color:"var(--text2)" }}>{record.sym}</span>
              <VerdictBadge v={record.verdict}/>
              <ConvictionDots level={record.conviction}/>
              <span className="badge" style={{ background:"var(--surface)", color:"var(--muted2)", border:"1px solid var(--border)" }}>{record.horizon} term</span>
              <span className="badge" style={{ background:`${sourceColor}18`, color:sourceColor, border:`1px solid ${sourceColor}35` }}>{sourceLabel}</span>
            </div>
            <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
              <span style={{ fontSize:12, color:"var(--muted2)" }}>{record.name}</span>
              {record.target && <span style={{ fontSize:10, color:"var(--muted)", fontFamily:"var(--ff-mono)" }}>tgt {record.target}</span>}
            </div>
          </div>
        </div>
        {/* Right */}
        <div style={{ display:"flex", gap:20, alignItems:"flex-start", flexShrink:0, flexWrap:"wrap" }}>
          <div style={{ textAlign:"right" }}>
            <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em", marginBottom:3 }}>CALLED</div>
            <div style={{ fontFamily:"var(--ff-mono)", fontSize:14, fontWeight:500, color:"var(--text2)" }}>{ageSince(record.calledAt)}</div>
            <div style={{ fontFamily:"var(--ff-mono)", fontSize:10, color:"var(--muted)", marginTop:2 }}>
              {fmtDate(record.calledAt)}
            </div>
          </div>
          <div style={{ textAlign:"right" }}>
            <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em", marginBottom:3 }}>CALLED AT</div>
            <div style={{ fontFamily:"var(--ff-mono)", fontSize:14, fontWeight:500, color:"var(--text2)" }}>{fmtP(priceAtCall)}</div>
          </div>
          <div style={{ textAlign:"right" }}>
            <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em", marginBottom:3 }}>NOW</div>
            {priceFetching && currentPrice == null
              ? <div className="shimmer-el" style={{ width:60, height:14 }}/>
              : <div style={{ fontFamily:"var(--ff-mono)", fontSize:14, fontWeight:500, color:"var(--text2)" }}>{fmtP(currentPrice)}</div>
            }
          </div>
          <div style={{ textAlign:"right", minWidth:70 }}>
            <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em", marginBottom:3 }}>RETURN</div>
            {returnPct != null ? (
              <div>
                <div style={{ fontFamily:"var(--ff-mono)", fontSize:14, fontWeight:600, color:returnPct >= 0 ? "var(--green)" : "var(--red)" }}>
                  {returnPct >= 0 ? "+" : ""}{returnPct.toFixed(1)}%
                </div>
                {isWin && <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--green)", letterSpacing:"0.06em", marginTop:2 }}>WIN</div>}
              </div>
            ) : (
              <div style={{ fontFamily:"var(--ff-mono)", fontSize:14, color:"var(--muted)" }}>—</div>
            )}
          </div>
          <button onClick={() => onAnalyse(record.sym)} style={{ background:"none", border:"1px solid var(--border)", borderRadius:8, padding:"7px 14px", fontSize:10, color:"var(--muted2)", fontFamily:"var(--ff-mono)", letterSpacing:"0.06em", whiteSpace:"nowrap" }}>
            ANALYSE →
          </button>
          <button onClick={() => onRemove(record.sym)} title="Remove this call record" style={{ background:"none", border:"1px solid var(--border)", borderRadius:8, padding:"7px 10px", fontSize:12, color:"var(--muted)", fontFamily:"var(--ff-mono)", lineHeight:1 }}>
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Chart canvas ───────────────────────────────────────────────────────────
function ChartCanvas({ candles, analysis, range, currency, indicators, displayCcy, audUsd, priceType }) {
  const canvasRef   = useRef(null);
  const containerRef = useRef(null);
  const [canvasW, setCanvasW] = useState(0);
  const [hovIdx, setHovIdx]   = useState(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver(() => setCanvasW(el.clientWidth));
    obs.observe(el);
    setCanvasW(el.clientWidth);
    return () => obs.disconnect();
  }, []);

  useEffect(() => { draw(hovIdx); }, [candles, analysis, canvasW, hovIdx, indicators, displayCcy, audUsd]);

  const isIndex = priceType === "index";

  function convert(p) {
    if (p == null || isIndex) return p;
    if (!displayCcy || !audUsd) return p;
    return toDisplay(p, currency || "USD", displayCcy, audUsd);
  }

  const ccyPrefix = isIndex ? "" : (displayCcy === "AUD" ? "A$" : displayCcy === "USD" ? "$" : "");

  function fmt(p) {
    if (!p && p !== 0) return "—";
    const prefix = ccyPrefix;
    if (isIndex) {
      // Show full numbers for indices (no k abbreviation, no $ prefix)
      if (p >= 10000) return p.toLocaleString("en",{maximumFractionDigits:0});
      if (p >= 1000)  return p.toLocaleString("en",{maximumFractionDigits:0});
      if (p >= 100)   return p.toFixed(0);
      return p.toFixed(2);
    }
    if (p >= 100000) return `${prefix}${(p/1000).toFixed(0)}k`;
    if (p >= 10000)  return `${prefix}${(p/1000).toFixed(1)}k`;
    if (p >= 1000)   return `${prefix}${(p/1000).toFixed(2)}k`;
    if (p >= 100)    return `${prefix}${p.toFixed(0)}`;
    if (p >= 10)     return `${prefix}${p.toFixed(1)}`;
    if (p >= 1)      return `${prefix}${p.toFixed(2)}`;
    return `${prefix}${p.toFixed(4)}`;
  }

  function draw(hi) {
    const canvas = canvasRef.current;
    if (!canvas || !candles?.length || !canvasW) return;

    // Determine if we have meaningful indicator data to show sub-panels
    const hasIndicators = !!(indicators && (
      indicators.rsi?.some(v => v != null) ||
      indicators.macd?.macd?.some(v => v != null)
    ));

    const W = canvasW;
    const PL = 8, PR = 68, PT = 14, CHART_H = 290, VOL_H = 58, GAP = 12, PB = 28;
    const RSI_H = 90, MACD_H = 90, GAP2 = 10, GAP3 = 8;
    const H = hasIndicators
      ? PT + CHART_H + GAP + VOL_H + GAP2 + RSI_H + GAP3 + MACD_H + PB
      : 420;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = W * dpr; canvas.height = H * dpr;
    canvas.style.width = "100%"; canvas.style.height = H + "px";
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    const chartW  = W - PL - PR;
    const volTop  = PT + CHART_H + GAP;
    const rsiTop  = volTop + VOL_H + GAP2;
    const macdTop = rsiTop + RSI_H + GAP3;

    // currency-converted candles (OHLC only; volume stays raw)
    const cc = candles.map(c => ({ ...c, o: convert(c.o), h: convert(c.h), l: convert(c.l), c: convert(c.c) }));

    // data bounds
    const hs = cc.map(c=>c.h).filter(Boolean);
    const ls = cc.map(c=>c.l).filter(Boolean);
    if (!hs.length) return;
    const maxP = Math.max(...hs) * 1.003;
    const minP = Math.min(...ls) * 0.997;
    const pRange = maxP - minP;
    const maxVol = Math.max(...candles.map(c=>c.v||0));
    const n = cc.length;
    const toX = i => PL + (n > 1 ? (i / (n-1)) * chartW : chartW/2);
    const toY = p => PT + ((maxP - p) / pRange) * CHART_H;
    const cW  = Math.max(1.5, (chartW / n) * 0.65);

    // bg
    ctx.fillStyle = "#1a1630"; ctx.fillRect(0, 0, W, H);

    // sub-panel backgrounds
    if (hasIndicators) {
      ctx.fillStyle = "#13112600";
      ctx.fillRect(0, rsiTop - 1, W, RSI_H + 2);
      ctx.fillRect(0, macdTop - 1, W, MACD_H + 2);
      // Subtle border between panels
      ctx.strokeStyle = "#332e5045"; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(0, rsiTop - 1); ctx.lineTo(W, rsiTop - 1); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, macdTop - 1); ctx.lineTo(W, macdTop - 1); ctx.stroke();
    }

    // grid + price axis
    const GRID = 5;
    for (let i = 0; i <= GRID; i++) {
      const p = minP + pRange * (i / GRID);
      const y = toY(p);
      ctx.strokeStyle = "#332e5028"; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(PL, y); ctx.lineTo(W - PR, y); ctx.stroke();
      ctx.fillStyle = "#7b7599"; ctx.font = "9px DM Mono,monospace"; ctx.textAlign = "left";
      ctx.fillText(fmt(p), W - PR + 5, y + 3);
    }

    // ── BB + EMA overlays (drawn behind candles) ──
    if (hasIndicators) {
      ctx.save();
      ctx.beginPath(); ctx.rect(PL, PT, chartW, CHART_H); ctx.clip();

      const bb   = indicators.bb;
      const ema50  = indicators.ema50;
      const ema200 = indicators.ema200;

      // Bollinger Bands fill + lines
      if (bb?.upper?.some(v => v != null)) {
        const upperPts = [], lowerPts = [];
        for (let i = 0; i < n; i++) {
          if (bb.upper[i] != null && bb.lower[i] != null) {
            upperPts.push([toX(i), toY(convert(bb.upper[i]))]);
            lowerPts.push([toX(i), toY(convert(bb.lower[i]))]);
          }
        }
        if (upperPts.length >= 2) {
          // Fill
          ctx.beginPath();
          ctx.moveTo(upperPts[0][0], upperPts[0][1]);
          for (let i = 1; i < upperPts.length; i++) ctx.lineTo(upperPts[i][0], upperPts[i][1]);
          for (let i = lowerPts.length-1; i >= 0; i--) ctx.lineTo(lowerPts[i][0], lowerPts[i][1]);
          ctx.closePath(); ctx.fillStyle = "#448aff0c"; ctx.fill();
          // Upper line
          ctx.beginPath();
          ctx.moveTo(upperPts[0][0], upperPts[0][1]);
          for (let i = 1; i < upperPts.length; i++) ctx.lineTo(upperPts[i][0], upperPts[i][1]);
          ctx.strokeStyle = "#448aff50"; ctx.lineWidth = 1; ctx.setLineDash([]); ctx.stroke();
          // Lower line
          ctx.beginPath();
          ctx.moveTo(lowerPts[0][0], lowerPts[0][1]);
          for (let i = 1; i < lowerPts.length; i++) ctx.lineTo(lowerPts[i][0], lowerPts[i][1]);
          ctx.strokeStyle = "#448aff50"; ctx.lineWidth = 1; ctx.stroke();
          // Middle dashed
          ctx.setLineDash([4, 3]);
          ctx.beginPath(); let _s = false;
          for (let i = 0; i < n; i++) {
            if (bb.middle[i] == null) { _s = false; continue; }
            const x = toX(i), y = toY(convert(bb.middle[i]));
            if (!_s) { ctx.moveTo(x, y); _s = true; } else ctx.lineTo(x, y);
          }
          ctx.strokeStyle = "#448aff30"; ctx.lineWidth = 1; ctx.stroke();
          ctx.setLineDash([]);
        }
      }

      // EMA50
      if (ema50?.some(v => v != null)) {
        ctx.beginPath(); let _s = false;
        for (let i = 0; i < n; i++) {
          if (ema50[i] == null) { _s = false; continue; }
          const x = toX(i), y = toY(convert(ema50[i]));
          if (!_s) { ctx.moveTo(x, y); _s = true; } else ctx.lineTo(x, y);
        }
        ctx.strokeStyle = "#448aff99"; ctx.lineWidth = 1; ctx.stroke();
      }

      // EMA200
      if (ema200?.some(v => v != null)) {
        ctx.beginPath(); let _s = false;
        for (let i = 0; i < n; i++) {
          if (ema200[i] == null) { _s = false; continue; }
          const x = toX(i), y = toY(convert(ema200[i]));
          if (!_s) { ctx.moveTo(x, y); _s = true; } else ctx.lineTo(x, y);
        }
        ctx.strokeStyle = "#ffab4099"; ctx.lineWidth = 1; ctx.stroke();
      }

      ctx.restore();

      // Indicator legend (top-left of price chart)
      ctx.font = "7px DM Mono,monospace"; ctx.textAlign = "left";
      let lx = PL + 8;
      if (bb?.upper?.some(v => v != null)) {
        ctx.fillStyle = "#448aff70"; ctx.fillRect(lx, PT + 6, 8, 1.5);
        ctx.fillStyle = "#7b7599cc"; ctx.fillText("BB", lx + 11, PT + 12); lx += 30;
      }
      if (ema50?.some(v => v != null)) {
        ctx.fillStyle = "#448affcc"; ctx.fillRect(lx, PT + 6, 8, 1.5);
        ctx.fillStyle = "#7b7599cc"; ctx.fillText("EMA50", lx + 11, PT + 12); lx += 52;
      }
      if (ema200?.some(v => v != null)) {
        ctx.fillStyle = "#ffab40cc"; ctx.fillRect(lx, PT + 6, 8, 1.5);
        ctx.fillStyle = "#7b7599cc"; ctx.fillText("EMA200", lx + 11, PT + 12);
      }
    }

    // support lines
    ctx.setLineDash([5, 4]);
    (analysis?.support || []).forEach(s => {
      const cp = convert(s.price);
      if (!cp || cp < minP || cp > maxP) return;
      const y = toY(cp);
      ctx.strokeStyle = s.strength === "STRONG" ? "#00e676aa" : "#00e67660";
      ctx.lineWidth = s.strength === "STRONG" ? 1.5 : 1;
      ctx.beginPath(); ctx.moveTo(PL, y); ctx.lineTo(W - PR, y); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = "#00e676bb"; ctx.font = "8px DM Mono,monospace"; ctx.textAlign = "right";
      ctx.fillText(`S ${fmt(cp)}`, W - PR - 3, y - 2);
      ctx.setLineDash([5, 4]);
    });
    // resistance lines
    (analysis?.resistance || []).forEach(r => {
      const cp = convert(r.price);
      if (!cp || cp < minP || cp > maxP) return;
      const y = toY(cp);
      ctx.strokeStyle = r.strength === "STRONG" ? "#ff5252aa" : "#ff525260";
      ctx.lineWidth = r.strength === "STRONG" ? 1.5 : 1;
      ctx.beginPath(); ctx.moveTo(PL, y); ctx.lineTo(W - PR, y); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = "#ff5252bb"; ctx.font = "8px DM Mono,monospace"; ctx.textAlign = "right";
      ctx.fillText(`R ${fmt(cp)}`, W - PR - 3, y - 2);
      ctx.setLineDash([5, 4]);
    });
    ctx.setLineDash([]);

    // volume bars
    for (let i = 0; i < n; i++) {
      const c = candles[i]; if (!c.v) continue;
      const x = toX(i), up = cc[i].c >= cc[i].o;
      const vH = (c.v / maxVol) * VOL_H;
      ctx.fillStyle = hi === i ? (up ? "#00e676aa" : "#ff5252aa") : (up ? "#00e67630" : "#ff525230");
      ctx.fillRect(x - cW/2, volTop + VOL_H - vH, cW, vH);
    }

    // candles
    for (let i = 0; i < n; i++) {
      const c = cc[i]; if (!c.o || !c.h || !c.l || !c.c) continue;
      const x = toX(i), up = c.c >= c.o;
      const isHov = hi === i;
      const color = up ? (isHov ? "#33ffaa" : "#00e676") : (isHov ? "#ff7070" : "#ff5252");
      ctx.strokeStyle = color + (isHov ? "" : "bb"); ctx.lineWidth = isHov ? 1.5 : 1;
      ctx.beginPath(); ctx.moveTo(x, toY(c.h)); ctx.lineTo(x, toY(c.l)); ctx.stroke();
      const bT = toY(Math.max(c.o, c.c)), bB = toY(Math.min(c.o, c.c));
      ctx.fillStyle = color; ctx.fillRect(x - cW/2, bT, cW, Math.max(1, bB - bT));
    }

    // hover crosshair
    if (hi !== null && hi >= 0 && hi < n) {
      const x = toX(hi);
      ctx.strokeStyle = "#ffffff18"; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(x, PT); ctx.lineTo(x, PT + CHART_H); ctx.stroke();
    }

    // time axis
    const tCount = Math.min(6, n);
    ctx.fillStyle = "#7b7599"; ctx.font = "9px DM Mono,monospace"; ctx.textAlign = "center";
    for (let i = 0; i < tCount; i++) {
      const idx = Math.round(i / (tCount - 1) * (n - 1));
      const c = candles[idx]; if (!c) continue;
      const x = toX(idx), d = new Date(c.t);
      let label;
      if (range === "1d")  label = d.toLocaleTimeString("en-AU", {hour:"2-digit",minute:"2-digit"});
      else if (range === "7d") label = d.toLocaleDateString("en-AU", {weekday:"short",day:"numeric"});
      else label = d.toLocaleDateString("en-AU", {day:"numeric",month:"short"});
      ctx.fillText(label, x, H - PB + 14);
    }

    // pattern label
    if (analysis?.pattern?.name && analysis.pattern.name !== "null") {
      ctx.fillStyle = "#ffab40cc"; ctx.font = "bold 10px DM Mono,monospace"; ctx.textAlign = "left";
      ctx.fillText(`◈ ${analysis.pattern.name}`, PL + 10, PT + 18);
    }

    if (!hasIndicators) return;

    // ── RSI panel ──────────────────────────────────────────────────────────
    const rsi = indicators.rsi;
    if (rsi?.some(v => v != null)) {
      ctx.strokeStyle = "#332e5060"; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(0, rsiTop); ctx.lineTo(W, rsiTop); ctx.stroke();

      ctx.fillStyle = "#7b7599"; ctx.font = "8px DM Mono,monospace"; ctx.textAlign = "left";
      ctx.fillText("RSI(14)", PL, rsiTop + 10);

      const rsiToY = v => rsiTop + ((100 - v) / 100) * RSI_H;

      // Reference lines
      ctx.setLineDash([3, 3]);
      ctx.strokeStyle = "#ff525265"; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(PL, rsiToY(70)); ctx.lineTo(W - PR, rsiToY(70)); ctx.stroke();
      ctx.fillStyle = "#ff5252cc"; ctx.font = "8px DM Mono,monospace"; ctx.textAlign = "left";
      ctx.fillText("70", W - PR + 3, rsiToY(70) + 3);

      ctx.strokeStyle = "#00e67665"; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(PL, rsiToY(30)); ctx.lineTo(W - PR, rsiToY(30)); ctx.stroke();
      ctx.fillStyle = "#00e676cc";
      ctx.fillText("30", W - PR + 3, rsiToY(30) + 3);
      ctx.setLineDash([]);

      // Per-bar fills for overbought/oversold zones
      const barW = Math.max(1, (chartW / n) * 0.9);
      for (let i = 0; i < n; i++) {
        if (rsi[i] == null) continue;
        const x = toX(i);
        if (rsi[i] > 70) {
          ctx.fillStyle = "#ff525235";
          ctx.fillRect(x - barW/2, rsiToY(Math.min(100, rsi[i])), barW, rsiToY(70) - rsiToY(Math.min(100, rsi[i])));
        } else if (rsi[i] < 30) {
          ctx.fillStyle = "#00e67635";
          ctx.fillRect(x - barW/2, rsiToY(30), barW, rsiToY(Math.max(0, rsi[i])) - rsiToY(30));
        }
      }

      // RSI line (clipped)
      ctx.save(); ctx.beginPath(); ctx.rect(PL, rsiTop, chartW, RSI_H); ctx.clip();
      ctx.beginPath(); let _s = false;
      for (let i = 0; i < n; i++) {
        if (rsi[i] == null) { _s = false; continue; }
        const x = toX(i), y = rsiToY(rsi[i]);
        if (!_s) { ctx.moveTo(x, y); _s = true; } else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = "#e8e4f0"; ctx.lineWidth = 2; ctx.stroke();
      ctx.restore();

      // Current RSI value label
      for (let i = n-1; i >= 0; i--) {
        if (rsi[i] != null) {
          const rsiVal = rsi[i];
          const labelColor = rsiVal > 70 ? "#ff5252" : rsiVal < 30 ? "#00e676" : "#e8e4f0";
          ctx.fillStyle = labelColor; ctx.font = "8px DM Mono,monospace"; ctx.textAlign = "left";
          ctx.fillText(rsiVal.toFixed(1), W - PR + 3, rsiToY(Math.max(0, Math.min(100, rsiVal))) + 3);
          break;
        }
      }
    }

    // ── MACD panel ─────────────────────────────────────────────────────────
    const macd = indicators.macd;
    if (macd?.macd?.some(v => v != null)) {
      ctx.strokeStyle = "#332e5060"; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(0, macdTop); ctx.lineTo(W, macdTop); ctx.stroke();

      ctx.fillStyle = "#7b7599"; ctx.font = "8px DM Mono,monospace"; ctx.textAlign = "left";
      ctx.fillText("MACD", PL, macdTop + 10);

      const allVals = [...(macd.macd||[]), ...(macd.signal||[]), ...(macd.histogram||[])].filter(v => v != null);
      if (allVals.length > 0) {
        const mMax = Math.max(...allVals);
        const mMin = Math.min(...allVals);
        const mRange = mMax - mMin || 0.0001;
        const macdToY = v => macdTop + ((mMax - v) / mRange) * MACD_H;
        const zeroY = macdToY(0);

        // Zero line
        ctx.setLineDash([3, 3]);
        ctx.strokeStyle = "#ffffff40"; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(PL, zeroY); ctx.lineTo(W - PR, zeroY); ctx.stroke();
        ctx.setLineDash([]);

        ctx.save(); ctx.beginPath(); ctx.rect(PL, macdTop, chartW, MACD_H); ctx.clip();

        // Histogram bars
        const bw2 = Math.max(1, (chartW / n) * 0.65);
        for (let i = 0; i < n; i++) {
          if (macd.histogram[i] == null) continue;
          const x = toX(i), y = macdToY(macd.histogram[i]);
          const h = Math.abs(y - zeroY);
          ctx.fillStyle = macd.histogram[i] >= 0 ? "#00e67675" : "#ff525275";
          ctx.fillRect(x - bw2/2, Math.min(y, zeroY), bw2, h);
        }

        // MACD line (blue)
        ctx.beginPath(); let _s = false;
        for (let i = 0; i < n; i++) {
          if (macd.macd[i] == null) { _s = false; continue; }
          const x = toX(i), y = macdToY(macd.macd[i]);
          if (!_s) { ctx.moveTo(x, y); _s = true; } else ctx.lineTo(x, y);
        }
        ctx.strokeStyle = "#448aff"; ctx.lineWidth = 2; ctx.stroke();

        // Signal line (amber)
        ctx.beginPath(); _s = false;
        for (let i = 0; i < n; i++) {
          if (macd.signal[i] == null) { _s = false; continue; }
          const x = toX(i), y = macdToY(macd.signal[i]);
          if (!_s) { ctx.moveTo(x, y); _s = true; } else ctx.lineTo(x, y);
        }
        ctx.strokeStyle = "#ffab40"; ctx.lineWidth = 2; ctx.stroke();

        ctx.restore();

        // MACD legend
        ctx.font = "8px DM Mono,monospace"; ctx.textAlign = "left";
        ctx.fillStyle = "#448aff"; ctx.fillRect(PL + 40, macdTop + 5, 10, 2);
        ctx.fillStyle = "#a89ec0"; ctx.fillText("MACD", PL + 53, macdTop + 10);
        ctx.fillStyle = "#ffab40"; ctx.fillRect(PL + 95, macdTop + 5, 10, 2);
        ctx.fillStyle = "#a89ec0"; ctx.fillText("Signal", PL + 108, macdTop + 10);
      }
    }
  }

  function handleMouseMove(e) {
    const canvas = canvasRef.current; if (!canvas || !candles?.length) return;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const PL = 8, PR = 68, n = candles.length;
    const chartW = canvasW - PL - PR;
    const idx = Math.round((mx - PL) / chartW * (n - 1));
    setHovIdx(Math.max(0, Math.min(n - 1, idx)));
  }

  const hovRaw = hovIdx !== null ? candles[hovIdx] : null;
  const hov = hovRaw ? { ...hovRaw, o: convert(hovRaw.o), h: convert(hovRaw.h), l: convert(hovRaw.l), c: convert(hovRaw.c) } : null;

  return (
    <div ref={containerRef} style={{width:"100%",position:"relative",borderRadius:10,overflow:"hidden"}}>
      <canvas ref={canvasRef} style={{display:"block",cursor:"crosshair"}}
        onMouseMove={handleMouseMove} onMouseLeave={()=>setHovIdx(null)}/>
      {hov && (
        <div style={{position:"absolute",top:10,left:10,background:"#1e1a2eed",border:"1px solid var(--border2)",borderRadius:8,padding:"8px 12px",fontSize:11,fontFamily:"var(--ff-mono)",pointerEvents:"none",minWidth:120}}>
          <div style={{color:"var(--muted)",marginBottom:5,fontSize:9,letterSpacing:"0.08em"}}>
            {new Date(hov.t).toLocaleDateString("en-AU",{day:"numeric",month:"short",year:"2-digit"})}
            {range==="1d"&&" "+new Date(hov.t).toLocaleTimeString("en-AU",{hour:"2-digit",minute:"2-digit"})}
          </div>
          {[{l:"O",v:hov.o,c:"var(--muted2)"},{l:"H",v:hov.h,c:"var(--green)"},{l:"L",v:hov.l,c:"var(--red)"},{l:"C",v:hov.c,c:hov.c>=hov.o?"var(--green)":"var(--red)"}].map(f=>(
            <div key={f.l} style={{display:"flex",gap:8,color:f.c,lineHeight:1.7}}>
              <span style={{color:"var(--muted)",width:10}}>{f.l}</span>
              <span>{fmt(f.v)}</span>
            </div>
          ))}
          {hov.v>0 && <div style={{color:"var(--muted)",marginTop:4,fontSize:9}}>Vol {hov.v>=1e6?(hov.v/1e6).toFixed(1)+"M":(hov.v/1e3).toFixed(0)+"K"}</div>}
        </div>
      )}
    </div>
  );
}

// ── Trade Modal ─────────────────────────────────────────────────────────────
function TradeModal({ config, onClose, onSuccess, displayCcy, audUsd, authFetch }) {
  const { sym, name } = config;
  const [exchange, setExchange] = useState(config.exchange || null);
  const isBinance = exchange === "binance";
  const isTiger = exchange === "tiger";
  const isAX = sym.endsWith(".AX") || sym.endsWith(".AU");
  const tradeCcy = isTiger ? (isAX ? "AUD" : "USD") : "USD";
  const ccySymbol = tradeCcy === "AUD" ? "A$" : "$";
  const tigerMarket = isAX ? "ASX" : "US";

  const [side, setSide] = useState(config.side || "BUY");
  const [step, setStep] = useState(1);
  const [availBal, setAvailBal] = useState(null);
  const [tigerBalCcy, setTigerBalCcy] = useState("USD");
  const [usdcBal, setUsdcBal] = useState(null);
  const [quoteAmount, setQuoteAmount] = useState("");
  const [cryptoQty, setCryptoQty] = useState("");
  const [shareQty, setShareQty] = useState("");
  const [tigerOrderType, setTigerOrderType] = useState("MKT");
  const [limitPrice, setLimitPrice] = useState("");
  const [livePrice, setLivePrice] = useState(null);
  const [result, setResult] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [bnTicker, setBnTicker] = useState(null);

  // Fetch prices
  useEffect(() => {
    if (!isTiger) {
      authFetch(`/api/binance/ticker/${sym}`)
        .then(r => r.json())
        .then(d => { if (d.price) setBnTicker(d); })
        .catch(() => {});
    }
    authFetch(`/api/chart/${sym}?range=1d`)
      .then(r => r.json())
      .then(d => { if (d.currentPrice) setLivePrice(d.currentPrice); })
      .catch(() => {});
  }, [sym, isTiger]);

  // Fetch balance when exchange changes
  useEffect(() => {
    if (!exchange) return;
    setAvailBal(null); setUsdcBal(null);
    if (isTiger) {
      authFetch(`/api/tiger/assets?currency=${tradeCcy}`)
        .then(r => r.json())
        .then(d => { setAvailBal(d.buyingPower ?? null); setTigerBalCcy(d.currency || tradeCcy); })
        .catch(() => setAvailBal(null));
    } else if (isBinance) {
      authFetch("/api/binance/usdt-balance")
        .then(r => r.json())
        .then(d => { setAvailBal(d.available ?? null); })
        .catch(() => setAvailBal(null));
    } else {
      authFetch("/api/coinbase/usd-balance")
        .then(r => r.json())
        .then(d => { setAvailBal(d.available ?? null); setUsdcBal(d.availableUSDC ?? null); })
        .catch(() => setAvailBal(null));
    }
  }, [exchange, isBinance, isTiger, tradeCcy]);

  // Auto-select cheaper exchange when none specified (crypto only)
  useEffect(() => {
    if (exchange) return;
    if (bnTicker && livePrice) {
      const bnEff = bnTicker.price * 1.001;
      const cbEff = livePrice * 1.006;
      setExchange(bnEff <= cbEff ? "binance" : "coinbase");
    } else if (bnTicker && !livePrice) {
      setExchange("binance");
    } else if (!bnTicker && livePrice) {
      setExchange("coinbase");
    }
  }, [bnTicker, livePrice, exchange]);

  // Determine which exchange is cheaper (for badge)
  const cheaper = (() => {
    if (isTiger || !bnTicker?.price || !livePrice) return null;
    const bnEff = bnTicker.price * 1.001;
    const cbEff = livePrice * 1.006;
    return side === "BUY" ? (bnEff <= cbEff ? "binance" : "coinbase") : (bnEff >= cbEff ? "binance" : "coinbase");
  })();

  const livePriceDisplay = isBinance && bnTicker?.price ? bnTicker.price : livePrice;
  const estQty = !isTiger && side === "BUY" && livePriceDisplay && parseFloat(quoteAmount) > 0
    ? (parseFloat(quoteAmount) / livePriceDisplay).toFixed(6) : null;
  const tigerEstCost = isTiger && livePriceDisplay && parseInt(shareQty) > 0
    ? (parseInt(shareQty) * livePriceDisplay) : null;

  const canReview = isTiger
    ? parseInt(shareQty) > 0
    : exchange && (side === "BUY" ? !!parseFloat(quoteAmount) : !!parseFloat(cryptoQty));

  async function placeOrder() {
    setPlacing(true);
    try {
      let r, data;
      if (isTiger) {
        const body = { sym, side, qty: parseInt(shareQty), orderType: tigerOrderType, livePrice: livePriceDisplay };
        if (tigerOrderType === "LMT" && parseFloat(limitPrice) > 0) body.limitPrice = parseFloat(limitPrice);
        r = await authFetch("/api/tiger/order", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        data = await r.json();
        if (r.ok && data.success) {
          setResult({ ok: true, orderId: String(data.orderId), productId: sym, status: data.status || "SUBMITTED" });
        } else {
          setResult({ ok: false, error: data.error || JSON.stringify(data) });
        }
      } else if (isBinance) {
        const body = { sym, side };
        if (side === "BUY") body.quoteAmount = parseFloat(quoteAmount);
        else body.qty = parseFloat(cryptoQty);
        r = await authFetch("/api/binance/order", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        data = await r.json();
        if (r.ok && data.success) {
          setResult({
            ok: true,
            orderId: String(data.orderId),
            productId: data.symbol,
            status: data.status || "FILLED",
          });
        } else {
          setResult({ ok: false, error: data.error || JSON.stringify(data) });
        }
      } else {
        const body = { sym, side, tradeCcy: "USD", orderType: "market", audUsd };
        if (side === "BUY") body.quoteSize = parseFloat(quoteAmount);
        else body.baseSize = parseFloat(cryptoQty);
        r = await authFetch("/api/coinbase/order", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        data = await r.json();
        if (r.ok && data.success && data.success_response?.order_id) {
          setResult({
            ok: true,
            orderId: data.success_response.order_id,
            productId: data._productId,
            status: data.order?.status || "OPEN",
          });
        } else {
          const errMsg = data.error_response?.message || data.message || data.errors?.[0]?.message || data.error || JSON.stringify(data);
          setResult({ ok: false, error: errMsg });
        }
      }
    } catch (e) {
      setResult({ ok: false, error: e.message });
    }
    setPlacing(false);
    setStep(3);
  }

  const btnBase = { border:"none", borderRadius:8, padding:"9px 22px", fontSize:12, fontFamily:"var(--ff-mono)", letterSpacing:"0.06em", cursor:"pointer", fontWeight:700 };
  const inputStyle = { width:"100%", background:"var(--card)", border:"1px solid var(--border2)", borderRadius:8, padding:"10px 14px", color:"var(--text2)", fontFamily:"var(--ff-mono)", fontSize:15, boxSizing:"border-box" };
  const exchangeLabel = isTiger ? "Tiger Brokers" : isBinance ? "Binance" : "Coinbase";

  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"#00000090", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:"20px" }}>
      <div onClick={e => e.stopPropagation()} style={{ background:"var(--surface)", border:"1px solid var(--border2)", borderRadius:16, padding:"28px 32px", width:"100%", maxWidth:460 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:22 }}>
          <div>
            <h2 style={{ fontFamily:"var(--ff-head)", fontSize:20, fontWeight:800, color:"var(--text2)", margin:0 }}>Trade {sym}</h2>
            <p style={{ fontSize:11, color:"var(--muted)", fontFamily:"var(--ff-mono)", margin:"4px 0 0", letterSpacing:"0.06em" }}>
              {name} · {exchange ? exchangeLabel : "Select exchange"}
              {isTiger && <span style={{ marginLeft:6, background:"#FF660018", border:"1px solid #FF660040", borderRadius:4, padding:"1px 6px", fontSize:9, color:"#FF6600" }}>{tigerMarket}</span>}
            </p>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:"var(--muted2)", fontSize:20, cursor:"pointer", lineHeight:1 }}>✕</button>
        </div>
        <div style={{ background:"#ffab4015", border:"1px solid #ffab4040", borderRadius:8, padding:"8px 14px", marginBottom:20, display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:13 }}>⚠</span>
          <span style={{ fontSize:11, fontFamily:"var(--ff-mono)", color:"var(--amber)", letterSpacing:"0.06em" }}>LIVE TRADING · REAL FUNDS</span>
        </div>

        {step === 1 && (
          <>
            {isTiger ? (
              <div style={{ marginBottom:18 }}>
                <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em", marginBottom:6 }}>EXCHANGE</div>
                <div style={{ background:"#FF660018", border:"1px solid #FF660060", borderRadius:10, padding:"10px 14px" }}>
                  <div style={{ fontSize:11, fontWeight:700, color:"#FF6600", fontFamily:"var(--ff-head)" }}>Tiger Brokers</div>
                  <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em", marginTop:4 }}>{tigerMarket} MARKET · STOCKS</div>
                </div>
              </div>
            ) : (
            <div style={{ marginBottom:18 }}>
              <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em", marginBottom:6 }}>EXCHANGE</div>
              <div style={{ display:"flex", gap:8 }}>
                {[
                  { id:"coinbase", label:"Coinbase", color:"var(--amber)", price:livePrice, fee:"0.60" },
                  { id:"binance", label:"Binance", color:"#F0B90B", price:bnTicker?.price, fee:"0.10" },
                ].map(ex => {
                  const isActive = exchange === ex.id;
                  const isCheaper = cheaper === ex.id;
                  const priceStr = ex.price ? `$${ex.price.toLocaleString("en",{maximumFractionDigits:2})}` : "—";
                  return (
                    <button key={ex.id} onClick={() => setExchange(ex.id)} style={{
                      flex:1, background:isActive?`${ex.color}18`:"var(--card)",
                      border:`1px solid ${isActive?`${ex.color}60`:"var(--border)"}`,
                      borderRadius:10, padding:"10px 14px", cursor:"pointer", textAlign:"left", position:"relative"
                    }}>
                      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                        <span style={{ fontSize:11, fontWeight:700, color:isActive?ex.color:"var(--muted2)", fontFamily:"var(--ff-head)" }}>{ex.label}</span>
                        {isCheaper && <span style={{ fontSize:8, fontFamily:"var(--ff-mono)", color:"var(--green)", background:"#00e67618", border:"1px solid #00e67640", borderRadius:4, padding:"1px 5px", letterSpacing:"0.06em" }}>CHEAPER</span>}
                      </div>
                      <div style={{ fontSize:14, fontFamily:"var(--ff-mono)", color:"var(--text2)", fontWeight:600, marginBottom:2 }}>{priceStr}</div>
                      <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em" }}>FEE: {ex.fee}%</div>
                    </button>
                  );
                })}
              </div>
            </div>
            )}
            <div style={{ display:"flex", gap:8, marginBottom:18 }}>
              {["BUY","SELL"].map(s => (
                <button key={s} onClick={() => setSide(s)} style={{ ...btnBase, flex:1,
                  background: s === side ? (s==="BUY" ? "var(--green)" : "var(--red)") : "var(--card)",
                  color: s === side ? "#0a0a14" : "var(--muted2)",
                  border:`1px solid ${s === side ? (s==="BUY" ? "var(--green)" : "var(--red)") : "var(--border)"}` }}>
                  {s}
                </button>
              ))}
            </div>
            <div style={{ marginBottom:18 }}>
              <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em", marginBottom:6 }}>{isTiger ? "BUYING POWER" : isBinance ? "USDT" : "USD"} AVAILABLE</div>
              <div style={{ fontSize:16, fontFamily:"var(--ff-mono)", color:"var(--text2)", fontWeight:600 }}>
                {!exchange ? "—" : availBal != null ? `${ccySymbol}${availBal.toLocaleString("en",{minimumFractionDigits:2,maximumFractionDigits:2})}` : "Loading…"}
              </div>
              {!isBinance && !isTiger && usdcBal != null && <div style={{ fontSize:10, fontFamily:"var(--ff-mono)", color:"var(--muted)", marginTop:4 }}>
                USDC: ${usdcBal.toLocaleString("en",{minimumFractionDigits:2,maximumFractionDigits:2})}
              </div>}
            </div>
            {side === "BUY" && !isBinance && !isTiger && usdcBal != null && usdcBal <= 0 && availBal != null && availBal <= 0 && (
              <div style={{ background:"#ffab4010", border:"1px solid #ffab4040", borderRadius:10, padding:"10px 14px", fontSize:11, color:"var(--amber)", marginBottom:18, lineHeight:1.5 }}>
                No USDC or USD available. Coinbase Advanced Trade uses USDC/USD for buying. Buy some USDC in the Coinbase app first, then sync and retry.
              </div>
            )}
            {side === "BUY" && isBinance && availBal != null && availBal <= 0 && (
              <div style={{ background:"#ffab4010", border:"1px solid #ffab4040", borderRadius:10, padding:"10px 14px", fontSize:11, color:"var(--amber)", marginBottom:18, lineHeight:1.5 }}>
                No USDT available. Deposit or buy USDT on Binance to start trading.
              </div>
            )}
            {isTiger ? (
              <>
                <div style={{ marginBottom:18 }}>
                  <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em", marginBottom:6 }}>SHARES</div>
                  <input value={shareQty} onChange={e => setShareQty(e.target.value.replace(/\D/g,""))} type="number" min="1" step="1" placeholder="e.g. 100" style={inputStyle}/>
                  {tigerEstCost != null && <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"var(--ff-mono)", marginTop:6 }}>Est. cost: {ccySymbol}{tigerEstCost.toLocaleString("en",{minimumFractionDigits:2,maximumFractionDigits:2})} {tradeCcy}</div>}
                </div>
                <div style={{ marginBottom:18 }}>
                  <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em", marginBottom:6 }}>ORDER TYPE</div>
                  <div style={{ display:"flex", gap:8 }}>
                    {["MKT","LMT"].map(t => (
                      <button key={t} onClick={() => setTigerOrderType(t)} style={{ ...btnBase, flex:1,
                        background: t === tigerOrderType ? "#FF660018" : "var(--card)",
                        color: t === tigerOrderType ? "#FF6600" : "var(--muted2)",
                        border:`1px solid ${t === tigerOrderType ? "#FF660060" : "var(--border)"}` }}>
                        {t === "MKT" ? "MARKET" : "LIMIT"}
                      </button>
                    ))}
                  </div>
                </div>
                {tigerOrderType === "LMT" && (
                  <div style={{ marginBottom:18 }}>
                    <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em", marginBottom:6 }}>LIMIT PRICE ({tradeCcy})</div>
                    <input value={limitPrice} onChange={e => setLimitPrice(e.target.value)} type="number" min="0" step="0.01" placeholder={livePriceDisplay ? livePriceDisplay.toFixed(2) : "0.00"} style={inputStyle}/>
                  </div>
                )}
              </>
            ) : (
              <>
                {side === "BUY" && (
                  <div style={{ marginBottom:18 }}>
                    <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em", marginBottom:6 }}>AMOUNT ({tradeCcy})</div>
                    <input value={quoteAmount} onChange={e => setQuoteAmount(e.target.value)} type="number" min="0" placeholder="e.g. 100" style={inputStyle}/>
                    {estQty && <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"var(--ff-mono)", marginTop:6 }}>≈ {estQty} {sym}</div>}
                  </div>
                )}
                {side === "SELL" && (
                  <div style={{ marginBottom:18 }}>
                    <div style={{ fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", letterSpacing:"0.1em", marginBottom:6 }}>QUANTITY ({sym})</div>
                    <input value={cryptoQty} onChange={e => setCryptoQty(e.target.value)} type="number" min="0" placeholder="e.g. 0.005" style={inputStyle}/>
                  </div>
                )}
              </>
            )}
            <button onClick={() => setStep(2)} disabled={!canReview}
              style={{ ...btnBase, width:"100%", background: side === "BUY" ? "var(--green)" : "var(--red)", color:"#0a0a14", opacity: canReview ? 1 : 0.4 }}>
              REVIEW ORDER →
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <div style={{ background:"var(--card)", border:"1px solid var(--border)", borderRadius:12, padding:"18px 20px", marginBottom:20 }}>
              {(isTiger ? (() => {
                const brokerage = isAX ? (tigerEstCost != null && tigerEstCost >= 20000 ? 6 : 3) : 2;
                const total = tigerEstCost != null ? tigerEstCost + brokerage : null;
                return [
                { l:"EXCHANGE", v:"Tiger Brokers" },
                { l:"SYMBOL", v:sym },
                { l:"MARKET", v:tigerMarket },
                { l:"SIDE", v:side },
                { l:"SHARES", v:shareQty },
                tigerEstCost != null && { l:"EST. COST", v:`${ccySymbol}${tigerEstCost.toLocaleString("en",{minimumFractionDigits:2,maximumFractionDigits:2})}` },
                { l:"TYPE", v:tigerOrderType === "MKT" ? "MARKET" : `LIMIT @ ${ccySymbol}${limitPrice}` },
                { l:"BROKERAGE", v:`${ccySymbol}${brokerage.toFixed(2)}` },
                total != null && { l:"TOTAL", v:`${ccySymbol}${total.toLocaleString("en",{minimumFractionDigits:2,maximumFractionDigits:2})} ${tradeCcy}`, bold:true },
                ];
              })() : [
                { l:"EXCHANGE", v:isBinance ? "Binance" : "Coinbase" },
                { l:"ASSET", v:sym },
                { l:"SIDE", v:side },
                side === "BUY" && { l:"SPEND", v:`${ccySymbol}${parseFloat(quoteAmount).toFixed(2)} ${tradeCcy}` },
                side === "SELL" && { l:"SELL QTY", v:`${cryptoQty} ${sym}` },
                { l:"TYPE", v:"MARKET" },
              ]).filter(Boolean).map(f => (
                <div key={f.l} style={{ display:"flex", justifyContent:"space-between", marginBottom:12, ...(f.bold ? {borderTop:"1px solid var(--border)",paddingTop:12,marginTop:4} : {}) }}>
                  <span style={{ fontSize:10, fontFamily:"var(--ff-mono)", color:f.bold?"var(--text2)":"var(--muted)", letterSpacing:"0.1em", fontWeight:f.bold?700:400 }}>{f.l}</span>
                  <span style={{ fontSize:f.bold?15:13, fontFamily:"var(--ff-mono)", color:"var(--text2)", fontWeight:f.bold?800:600 }}>{f.v}</span>
                </div>
              ))}
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={() => setStep(1)} style={{ ...btnBase, flex:1, background:"none", color:"var(--muted2)", border:"1px solid var(--border)" }}>← BACK</button>
              <button onClick={placeOrder} disabled={placing}
                style={{ ...btnBase, flex:2, background: side === "BUY" ? "var(--green)" : "var(--red)", color:"#0a0a14", opacity:placing?0.6:1 }}>
                {placing ? "PLACING…" : "PLACE ORDER"}
              </button>
            </div>
          </>
        )}

        {step === 3 && result && (
          <>
            <div style={{ textAlign:"center", padding:"20px 0 28px" }}>
              {result.ok ? (
                <>
                  <div style={{ fontSize:36, marginBottom:12 }}>✓</div>
                  <div style={{ fontFamily:"var(--ff-head)", fontSize:18, fontWeight:800, color:"var(--green)", marginBottom:8 }}>Order Placed</div>
                  {result.productId && <div style={{ fontSize:12, fontFamily:"var(--ff-mono)", color:"var(--muted2)", marginBottom:6 }}>{isTiger ? "Symbol" : "Pair"}: {result.productId}</div>}
                  <div style={{ fontSize:11, fontFamily:"var(--ff-mono)", color:"var(--muted)", marginBottom:4 }}>Order ID: {result.orderId}</div>
                  {result.status && <div style={{ fontSize:10, fontFamily:"var(--ff-mono)", color:"var(--muted)" }}>Status: {result.status}</div>}
                </>
              ) : (
                <>
                  <div style={{ fontSize:36, marginBottom:12 }}>✕</div>
                  <div style={{ fontFamily:"var(--ff-head)", fontSize:18, fontWeight:800, color:"var(--red)", marginBottom:8 }}>Order Failed</div>
                  <div style={{ fontSize:12, fontFamily:"var(--ff-mono)", color:"var(--muted2)", lineHeight:1.5 }}>{result.error}</div>
                </>
              )}
            </div>
            <button onClick={() => { result.ok ? onSuccess(exchange) : onClose(); }}
              style={{ ...btnBase, width:"100%", background: result.ok ? "var(--green)" : "var(--card)", color: result.ok ? "#0a0a14" : "var(--muted2)", border: result.ok ? "none" : "1px solid var(--border)" }}>
              DONE
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Glossary modal ─────────────────────────────────────────────────────────
function GlossaryModal({ open, onClose, focusTerm, allGlossary }) {
  const termRefs = useRef({});
  const [glossarySearch, setGlossarySearch] = useState("");
  useEffect(() => {
    if (open && focusTerm && termRefs.current[focusTerm]) {
      setTimeout(() => termRefs.current[focusTerm].scrollIntoView({ behavior:"smooth", block:"center" }), 120);
    }
    if (!open) setGlossarySearch("");
  }, [open, focusTerm]);
  if (!open) return null;
  const gl = allGlossary || GLOSSARY;
  const sorted = [...gl].sort((a, b) => a.term.localeCompare(b.term));
  const builtInTerms = new Set(GLOSSARY.map(g => g.term));
  const customCount = gl.filter(g => !builtInTerms.has(g.term)).length;
  const q = glossarySearch.toLowerCase().trim();
  const filtered = q ? sorted.filter(g => g.term.toLowerCase().includes(q) || (g.def || g.definition || "").toLowerCase().includes(q)) : sorted;
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"#00000090", zIndex:1000, display:"flex", alignItems:"flex-start", justifyContent:"center", padding:"5vh 20px", overflowY:"auto" }}>
      <div onClick={e => e.stopPropagation()} style={{ background:"var(--surface)", border:"1px solid var(--border2)", borderRadius:16, padding:"28px 32px", maxWidth:660, width:"100%", marginBottom:40 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
          <div>
            <h2 style={{ fontFamily:"var(--ff-head)", fontSize:22, fontWeight:800, color:"var(--text2)", letterSpacing:"-0.02em" }}>Glossary</h2>
            <p style={{ fontSize:11, color:"var(--muted)", fontFamily:"var(--ff-mono)", letterSpacing:"0.06em", marginTop:4 }}>
              {gl.length} TERMS{customCount > 0 ? ` · ${GLOSSARY.length} BUILT-IN · ${customCount} AI-EXTRACTED` : ""} · CLICK ANY HIGHLIGHTED TERM
            </p>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"1px solid var(--border)", borderRadius:8, padding:"7px 16px", color:"var(--muted2)", fontSize:11, fontFamily:"var(--ff-mono)", letterSpacing:"0.06em", flexShrink:0 }}>CLOSE ✕</button>
        </div>
        <input value={glossarySearch} onChange={e => setGlossarySearch(e.target.value)} placeholder="Search terms…" autoFocus={!focusTerm} style={{ width:"100%", background:"var(--card)", border:"1px solid var(--border)", borderRadius:10, padding:"10px 16px", color:"var(--text2)", fontSize:13, fontFamily:"var(--ff-body)", marginBottom:16, boxSizing:"border-box" }}/>
        <div style={{ maxHeight:"60vh", overflowY:"auto", paddingRight:4 }}>
          {filtered.length === 0 && <p style={{ fontSize:13, color:"var(--muted)", textAlign:"center", padding:"20px 0" }}>No terms matching "{glossarySearch}"</p>}
          {filtered.map(g => {
            const isCustom = !builtInTerms.has(g.term);
            return (
              <div key={g.term} ref={el => { termRefs.current[g.term] = el; }}
                style={{ padding:"14px", borderRadius:10, marginBottom:4, background: focusTerm===g.term ? "#ffab4012" : "none", border:`1px solid ${focusTerm===g.term ? "#ffab4040" : "transparent"}`, transition:"background .25s" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                  <span style={{ fontFamily:"var(--ff-head)", fontSize:14, fontWeight:700, color: focusTerm===g.term ? "var(--amber)" : "var(--text2)" }}>{g.term}</span>
                  {isCustom && <span className="badge" style={{ background:"#448aff18", color:"var(--blue)", border:"1px solid #448aff35", fontSize:8 }}>AI</span>}
                </div>
                <p style={{ fontSize:13, color:"var(--muted2)", lineHeight:1.7, margin:0 }}>{g.def || g.definition}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Login / Register Screen ────────────────────────────────────────────────
function LoginScreen({ onAuth }) {
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [authConfig, setAuthConfig] = useState(null);
  const googleBtnRef = useRef(null);

  // Fetch auth config and load Google SDK on mount
  useEffect(() => {
    fetch("/api/auth/config").then(r => r.json()).then(cfg => {
      setAuthConfig(cfg);
      if (cfg.googleClientId) {
        // Load Google Identity Services SDK
        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
    }).catch(() => {});
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const endpoint = mode === "register" ? "/api/auth/register" : "/api/auth/login";
      const body = mode === "register" ? { email, password, name } : { email, password };
      const r = await fetch(endpoint, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) { setError(d.error || "Authentication failed"); setLoading(false); return; }
      onAuth(d);
    } catch { setError("Network error — please try again"); }
    setLoading(false);
  }

  // Initialize Google SDK and render button once loaded
  const googleInitRef = useRef(false);
  useEffect(() => {
    if (!authConfig?.googleClientId || googleInitRef.current) return;
    const interval = setInterval(() => {
      if (window.google?.accounts?.id && googleBtnRef.current) {
        clearInterval(interval);
        googleInitRef.current = true;
        window.google.accounts.id.initialize({
          client_id: authConfig.googleClientId,
          callback: async (response) => {
            setLoading(true);
            try {
              const r = await fetch("/api/auth/google", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ credential: response.credential }) });
              const d = await r.json();
              if (!r.ok) { setError(d.error || "Google sign-in failed"); setLoading(false); return; }
              onAuth(d);
            } catch { setError("Google sign-in failed"); }
            setLoading(false);
          },
        });
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          type: "standard",
          theme: "filled_black",
          size: "large",
          width: googleBtnRef.current.offsetWidth,
          text: "continue_with",
          shape: "pill",
        });
      }
    }, 200);
    return () => clearInterval(interval);
  }, [authConfig]);

  async function handleApple() {
    setError("Apple Sign-In coming soon — use email/password or Google for now.");
  }

  const inputStyle = { width:"100%", background:"var(--bg)", border:"1px solid var(--border)", borderRadius:10, padding:"13px 16px", fontSize:14, color:"var(--text2)", fontFamily:"var(--ff-body)", boxSizing:"border-box" };
  const btnPrimary = { width:"100%", background:"var(--green)", color:"#0a0a14", border:"none", borderRadius:10, padding:"14px", fontSize:15, fontWeight:700, fontFamily:"var(--ff-head)", cursor:"pointer", letterSpacing:"-0.01em" };
  const btnOAuth = { width:"100%", background:"var(--surface)", border:"1px solid var(--border)", borderRadius:10, padding:"13px", fontSize:14, fontWeight:600, fontFamily:"var(--ff-body)", cursor:"pointer", color:"var(--text2)", display:"flex", alignItems:"center", justifyContent:"center", gap:10 };

  return (
    <>
      <style>{css}</style>
      <div style={{ minHeight:"100vh", background:"var(--bg)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
        <div style={{ width:"100%", maxWidth:420 }}>
          {/* Logo */}
          <div style={{ textAlign:"center", marginBottom:40 }}>
            <div style={{ fontFamily:"var(--ff-head)", fontSize:32, fontWeight:900, letterSpacing:"-0.03em", color:"var(--text2)", marginBottom:8 }}>
              INTEL<span style={{ color:"var(--green)" }}>IQ</span>
            </div>
            <div style={{ fontSize:13, color:"var(--muted2)", fontFamily:"var(--ff-body)" }}>Investment Intelligence Platform</div>
          </div>

          <div className="card" style={{ padding:"32px 28px", borderRadius:16 }}>
            {/* Header with toggle */}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:28 }}>
              <h2 style={{ fontFamily:"var(--ff-head)", fontSize:22, fontWeight:800, color:"var(--text2)" }}>
                {mode === "login" ? "Sign in" : "Create account"}
              </h2>
              <button onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} style={{ background:"none", border:"none", color:"var(--green)", fontSize:13, fontFamily:"var(--ff-body)", cursor:"pointer", textDecoration:"underline" }}>
                {mode === "login" ? "Create account" : "Sign in"}
              </button>
            </div>

            {/* OAuth buttons */}
            <div style={{ display:"grid", gap:10, marginBottom:24 }}>
              <div ref={googleBtnRef} style={{ width:"100%", minHeight:44, borderRadius:10, overflow:"hidden" }} />
              <button onClick={handleApple} style={btnOAuth}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
                Continue with Apple
              </button>
            </div>

            {/* Divider */}
            <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:24 }}>
              <div style={{ flex:1, height:1, background:"var(--border)" }}/>
              <span style={{ fontSize:12, color:"var(--muted)", fontFamily:"var(--ff-mono)" }}>or</span>
              <div style={{ flex:1, height:1, background:"var(--border)" }}/>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} style={{ display:"grid", gap:16 }}>
              {mode === "register" && (
                <div>
                  <label style={{ display:"block", fontSize:13, fontWeight:600, color:"var(--text2)", fontFamily:"var(--ff-body)", marginBottom:6 }}>Name</label>
                  <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" style={inputStyle}/>
                </div>
              )}
              <div>
                <label style={{ display:"block", fontSize:13, fontWeight:600, color:"var(--text2)", fontFamily:"var(--ff-body)", marginBottom:6 }}>Email<span style={{ color:"var(--red)" }}>*</span></label>
                <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="Email" required style={inputStyle}/>
              </div>
              <div>
                <label style={{ display:"block", fontSize:13, fontWeight:600, color:"var(--text2)", fontFamily:"var(--ff-body)", marginBottom:6 }}>Password<span style={{ color:"var(--red)" }}>*</span></label>
                <input value={password} onChange={e => setPassword(e.target.value)} type="password" placeholder="Password" required style={inputStyle}/>
                {mode === "register" && (
                  <div style={{ fontSize:11, color:"var(--muted)", marginTop:6, fontFamily:"var(--ff-body)" }}>
                    15+ characters, or 8+ with a number and lowercase letter.
                  </div>
                )}
              </div>
              {error && (
                <div style={{ background:"#ff525218", border:"1px solid #ff525240", borderRadius:8, padding:"10px 14px", fontSize:12, color:"var(--red)", fontFamily:"var(--ff-body)" }}>
                  {error}
                </div>
              )}
              <button type="submit" disabled={loading} style={{ ...btnPrimary, opacity:loading?0.6:1 }}>
                {loading ? "Please wait..." : mode === "login" ? "Sign in" : "Create account"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Main App ───────────────────────────────────────────────────────────────
export default function App() {
  // ── Auth state ──
  const [authUser, setAuthUser] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const refreshTokenRef = useRef(localStorage.getItem("inteliq_refresh_token"));

  // authFetch: auto-attaches JWT, auto-refreshes on 401 with lock to prevent race conditions
  const refreshPromiseRef = useRef(null);
  const authFetch = useCallback(async (url, options = {}) => {
    const doFetch = (token) => fetch(url, { ...options, headers: { ...options.headers, ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
    let r = await doFetch(authToken);
    if (r.status === 401 && refreshTokenRef.current) {
      // Use a shared promise so only one refresh runs at a time
      if (!refreshPromiseRef.current) {
        refreshPromiseRef.current = fetch("/api/auth/refresh", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ refreshToken: refreshTokenRef.current }) })
          .then(rr => {
            if (!rr.ok) throw new Error("refresh failed");
            return rr.json();
          })
          .then(d => {
            setAuthToken(d.accessToken);
            refreshTokenRef.current = d.refreshToken;
            localStorage.setItem("inteliq_refresh_token", d.refreshToken);
            return d.accessToken;
          })
          .catch((err) => {
            console.log("[AUTH] Refresh failed:", err.message, "— NOT logging out");
            return null;
          })
          .finally(() => { refreshPromiseRef.current = null; });
      }
      const newToken = await refreshPromiseRef.current;
      if (newToken) {
        r = await doFetch(newToken);
      }
    }
    return r;
  }, [authToken]);

  // On mount: try to restore session from refresh token
  useEffect(() => {
    const rt = localStorage.getItem("inteliq_refresh_token");
    if (!rt) { setAuthLoading(false); return; }
    fetch("/api/auth/refresh", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ refreshToken: rt }) })
      .then(r => { if (!r.ok) throw new Error("expired"); return r.json(); })
      .then(d => {
        setAuthUser(d.user);
        setAuthToken(d.accessToken);
        refreshTokenRef.current = d.refreshToken;
        localStorage.setItem("inteliq_refresh_token", d.refreshToken);
      })
      .catch(() => { console.log("[AUTH] Mount refresh failed — keeping token for retry"); })
      .finally(() => setAuthLoading(false));
  }, []);

  function handleAuth(data) {
    console.log("[AUTH] Login success for:", data.user?.email);
    setAuthUser(data.user);
    setAuthToken(data.accessToken);
    refreshTokenRef.current = data.refreshToken;
    localStorage.setItem("inteliq_refresh_token", data.refreshToken);
  }

  function handleLogout() {
    console.log("[AUTH] LOGOUT called from:", new Error().stack?.split("\n")[2]?.trim());
    if (authToken) {
      fetch("/api/auth/logout", { method:"POST", headers:{ Authorization:`Bearer ${authToken}` } }).catch(()=>{});
    }
    setAuthUser(null); setAuthToken(null);
    refreshTokenRef.current = null;
    localStorage.removeItem("inteliq_refresh_token");
  }

  // Auth loading splash
  if (authLoading) {
    return (
      <>
        <style>{css}</style>
        <div style={{ minHeight:"100vh", background:"var(--bg)", display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ fontFamily:"var(--ff-head)", fontSize:28, fontWeight:900, color:"var(--text2)" }}>
            INTEL<span style={{ color:"var(--green)" }}>IQ</span>
          </div>
        </div>
      </>
    );
  }

  // Not authenticated — show login screen
  if (!authUser) {
    return <LoginScreen onAuth={handleAuth}/>;
  }

  // Authenticated — render main app
  return <AppMain user={authUser} authFetch={authFetch} onLogout={handleLogout}/>;
}

function AppMain({ user, authFetch, onLogout }) {
  const [tab,setTab]           = useState("dashboard");
  const [mobileMore,setMobileMore] = useState(false);

  // Pull-to-refresh for PWA
  const [pullY, setPullY] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pullStart = useRef(null);
  const mainRef = useRef(null);
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;

  const onTouchStart = useCallback((e) => {
    if (!isMobile) return;
    const el = mainRef.current;
    if (el && el.scrollTop <= 0) pullStart.current = e.touches[0].clientY;
    else pullStart.current = null;
  }, [isMobile]);

  const onTouchMove = useCallback((e) => {
    if (pullStart.current === null || isRefreshing) return;
    const dy = e.touches[0].clientY - pullStart.current;
    if (dy > 0) setPullY(Math.min(dy * 0.4, 80));
    else setPullY(0);
  }, [isRefreshing]);

  const onTouchEnd = useCallback(() => {
    if (pullY > 60 && !isRefreshing) {
      setIsRefreshing(true);
      setPullY(50);
      setTimeout(() => { window.location.reload(); }, 300);
    } else {
      setPullY(0);
    }
    pullStart.current = null;
  }, [pullY, isRefreshing]);
  const [portTab,setPortTab]   = useState("all");
  const [expanded,setExp]      = useState(null);
  const [portExp,setPortExp]   = useState(null);
  const [livePrices,setLP]     = useState({});
  const [displayCcy,setDisplayCcy] = useState("AUD");
  const [audUsd,setAudUsd]     = useState(0.635);

  // Dashboard live picks
  const [dashPicks,setDashPicks]     = useState([]);
  const [dashLoading,setDashLoading] = useState(false);
  const [dashError,setDashError]     = useState(null);
  const [dashStatus,setDashStatus]   = useState(null);

  // Explorer
  const [searchQ,setSearchQ]       = useState("");
  const [searching,setSearching]   = useState(false);
  const [searchStatus,setSearchStatus] = useState(null);
  const [searchResult,setResult]   = useState(null);
  const [searchError,setSearchErr] = useState(null);
  const [searchHistory,setHistory] = useState([]);

  // Portfolio sources
  const [cbHoldings,setCb] = useState([]);
  const [cbSyncing,setCbS] = useState(false);
  const [cbLastSync,setCbL]= useState(null);
  const [cbError,setCbE]   = useState("");

  const [bnHoldings,setBn] = useState([]);
  const [bnSyncing,setBnS] = useState(false);
  const [bnLastSync,setBnL]= useState(null);
  const [bnError,setBnE]   = useState("");

  const [tigerHoldings,setTiger] = useState([]);
  const [tigerSyncing,setTigerS] = useState(false);
  const [tigerLastSync,setTigerL]= useState(null);
  const [tigerError,setTigerE]   = useState("");
  const [tigerOrders,setTigerOrders] = useState([]);
  const [tigerOrdersLoading,setTigerOL] = useState(false);

  const [ledgerAddrs, setLedgerAddrs] = useState([]);
  const [ldgHoldings,setLdg] = useState([]);
  const [ldgSyncing,setLdgS] = useState(false);
  const [ldgLastSync,setLdgL]= useState(null);
  const [ldgError,setLdgE]   = useState("");

  const [cmcHoldings,setCmc] = useState([]);
  const [cmcReimport,setCmcReimport] = useState(false);
  const [cmcDivDates, setCmcDivDates] = useState({});

  // Watchlist
  const [watchlist,setWatchlist] = useState([]);

  // News
  const [newsFilter,setNewsFilter] = useState("ALL");
  const [newsItems,setNewsItems]   = useState(NEWS_MOCK);
  const [newsLoading,setNewsLoading] = useState(false);

  // IPO Calendar
  const [ipoItems,   setIpoItems]   = useState([]);
  const [ipoLoading, setIpoLoading] = useState(false);
  const [ipoFilter,  setIpoFilter]  = useState("ALL");
  const [ipoError,   setIpoError]   = useState(null);

  // Watchlist Alerts
  const [alerts,         setAlerts]         = useState({});  // keyed by sym: { type, value, note, triggered, triggeredAt }
  const [triggeredAlerts,setTriggeredAlerts]= useState([]);  // sym list for in-session notifications
  const [alertEditSym,   setAlertEditSym]   = useState(null); // sym currently being edited
  const [alertFormVal,   setAlertFormVal]   = useState({ type:"price_above", value:"", note:"" });

  // Trade Journal
  const [journalEntries,  setJournalEntries]  = useState([]);
  const [journalAnalysis, setJournalAnalysis] = useState(null);
  const [journalAnalysing,setJournalAnalysing]= useState(false);
  const [journalAnalysisError, setJournalAnalysisError] = useState(null);
  const [journalForm,     setJournalForm]     = useState({ date: new Date().toISOString().slice(0,10), sym:"", action:"BUY", qty:"", price:"", currency:"USD", thesis:"" });
  const [journalFormOpen, setJournalFormOpen] = useState(false);
  const [journalExitForm, setJournalExitForm] = useState(null); // entry id being exited

  // Macro Calendar
  const [macroEvents,  setMacroEvents]  = useState([]);
  const [macroLoading, setMacroLoading] = useState(false);
  const [macroError,   setMacroError]   = useState(null);

  // Natural Language Screener
  const [screenerQ,       setScreenerQ]       = useState("");
  const [screenerResults, setScreenerResults] = useState(null);
  const [screenerLoading, setScreenerLoading] = useState(false);
  const [screenerError,   setScreenerError]   = useState(null);

  // Earnings Calendar
  const [earningsData,    setEarningsData]    = useState({});
  const [earningsLoading, setEarningsLoading] = useState(false);
  const [earningsBriefs,  setEarningsBriefs]  = useState({});  // keyed by sym
  const [earningsBriefing,setEarningsBriefing]= useState({});  // loading states

  // Portfolio Coach
  const [coachReport,  setCoachReport]  = useState(null);
  const [coachLoading, setCoachLoading] = useState(false);
  const [coachError,   setCoachError]   = useState(null);

  // AI Call Record
  const [callRecords,        setCallRecords]        = useState([]);
  const [callsFilter,        setCallsFilter]        = useState("ALL");
  const [callsPrices,        setCallsPrices]        = useState({});
  const [callsPriceFetching, setCallsPriceFetching] = useState(false);

  // Chart / Detail
  const [detailSym,setDetailSym]           = useState(null); // {sym,name,priceType,priceCurrency,sector}
  const [detailStock,setDetailStock]       = useState(null); // full card analysis object, if drilled from a card
  const [detailFrom,setDetailFrom]         = useState("explorer"); // which tab to go back to
  const [chartRange,setChartRange]         = useState("1mo");
  const [chartData,setChartData]           = useState(null);
  const [chartLoading,setChartLoading]     = useState(false);
  const [detailAnalysis,setDetailAnalysis] = useState(null);
  const [detailAnalysing,setDetailAnalysing] = useState(false);

  // Live chart polling
  const [liveMode,setLiveMode]             = useState(false);
  const [liveInterval,setLiveInterval]     = useState(30);
  const [liveLastUpdate,setLiveLastUpdate] = useState(null);
  const liveTimerRef = useRef(null);

  // Indices
  const [indicesData,setIndicesData]       = useState(null);
  const [indicesLoading,setIndicesLoading] = useState(false);

  // Explorer inline chart
  const [explorerChart,setExplorerChart]               = useState(null);
  const [explorerChartLoading,setExplorerChartLoading] = useState(false);
  const [explorerAnalysis,setExplorerAnalysis]         = useState(null);

  // Glossary
  const [glossaryOpen,setGlossaryOpen] = useState(false);
  const [glossaryTerm,setGlossaryTerm] = useState(null);
  const openGlossary = t => { setGlossaryTerm(t||null); setGlossaryOpen(true); };
  const [tradeModal,setTradeModal] = useState(null);
  const [customTerms,setCustomTerms] = useState([]);
  const allGlossary = [...GLOSSARY, ...customTerms];

  // Persist custom glossary terms (debounced to server)
  const userDataSave = useCallback((key, data) => {
    authFetch(`/api/user-data/${key}`, { method:"PUT", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ data }) }).catch(()=>{});
  }, [authFetch]);
  useEffect(() => { const t = setTimeout(() => userDataSave("glossary_custom", customTerms), 500); return () => clearTimeout(t); }, [customTerms, userDataSave]);

  const [glossaryExtracting,setGlossaryExtracting] = useState(false);

  async function extractGlossaryTerms(text) {
    if (!text || typeof text !== "string" || text.length < 50) return;
    try {
      const r = await authFetch("/api/glossary/extract", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      const d = await r.json();
      if (!Array.isArray(d) || d.length === 0) return;
      setCustomTerms(prev => {
        const existing = new Set([...GLOSSARY, ...prev].map(g => g.term.toLowerCase()));
        const newTerms = d
          .filter(t => t.term && t.definition && !existing.has(t.term.toLowerCase()))
          .map(t => ({ term: t.term, def: t.definition }));
        return newTerms.length > 0 ? [...prev, ...newTerms] : prev;
      });
    } catch {}
  }

  // Fetch dividend data for CMC stock holdings

  // Fetch ex-div / pay dates from Yahoo Finance for dividend-paying CMC holdings
  const cmcDivSymKey = cmcHoldings.map(h => h.sym).join(",");
  useEffect(() => {
    if (!cmcDivSymKey) return;
    authFetch("/api/dividends/dates", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ symbols: cmcDivSymKey.split(",") }) })
      .then(r => r.json()).then(d => {
        setCmcDivDates(d);
        // Merge YF yield data into holdings that have no CSV dividend data — persists to DB so it's available on next load
        setCmc(prev => {
          const updated = prev.map(h => {
            if ((h.divYieldPct || 0) > 0) return h; // CSV already has yield
            const yf = d[h.sym];
            if (!yf?.yield) return h;
            return { ...h, divYieldPct: yf.yield * 100, divCents: (yf.annualDPS || 0) * 100 };
          });
          return updated;
        });
      }).catch(() => {});
  }, [cmcDivSymKey]);

  // Persist to server (debounced)
  const dataLoadedRef = useRef(false);
  useEffect(() => { if (!dataLoadedRef.current) return; const t = setTimeout(() => userDataSave("cmc_holdings", cmcHoldings), 500); return () => clearTimeout(t); }, [cmcHoldings, userDataSave]);
  useEffect(() => { if (!dataLoadedRef.current) return; const t = setTimeout(() => userDataSave("search_history", searchHistory), 500); return () => clearTimeout(t); }, [searchHistory, userDataSave]);
  useEffect(() => { if (!dataLoadedRef.current) return; const t = setTimeout(() => userDataSave("watchlist", watchlist), 500); return () => clearTimeout(t); }, [watchlist, userDataSave]);
  useEffect(() => { if (!dataLoadedRef.current) return; const t = setTimeout(() => userDataSave("call_records", callRecords), 500); return () => clearTimeout(t); }, [callRecords, userDataSave]);
  useEffect(() => { if (!dataLoadedRef.current) return; const t = setTimeout(() => userDataSave("journal", journalEntries), 500); return () => clearTimeout(t); }, [journalEntries, userDataSave]);
  useEffect(() => { if (!dataLoadedRef.current) return; const t = setTimeout(() => userDataSave("alerts", alerts), 500); return () => clearTimeout(t); }, [alerts, userDataSave]);
  useEffect(() => { if (!dataLoadedRef.current) return; const t = setTimeout(() => userDataSave("display_currency", displayCcy), 500); return () => clearTimeout(t); }, [displayCcy, userDataSave]);

  // Force full reload when iOS restores page from BFCache (stale React state)
  useEffect(() => {
    const onPageShow = (e) => { if (e.persisted) window.location.reload(); };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  // Load user data from server on mount (or migrate from localStorage on first login)
  useEffect(() => {
    authFetch("/api/user-data/").then(r => r.json()).then(data => {
      if (data && Object.keys(data).length > 0) {
        // Server has data — load it
        if (data.watchlist) setWatchlist(data.watchlist);
        if (data.search_history) setHistory(data.search_history);
        if (data.ledger_addrs) setLedgerAddrs(data.ledger_addrs);
        if (data.cmc_holdings) setCmc(data.cmc_holdings);
        if (data.alerts) setAlerts(data.alerts);
        if (data.journal) setJournalEntries(data.journal);
        if (data.call_records) setCallRecords(data.call_records);
        if (data.glossary_custom) setCustomTerms(data.glossary_custom);
        if (data.display_currency) setDisplayCcy(data.display_currency);
      } else {
        // First login — check localStorage for migration
        const migrate = {};
        const tryParse = (key) => { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : null; } catch { return null; } };
        const wl = tryParse("inteliq_watchlist"); if (wl?.length) { migrate.watchlist = wl; setWatchlist(wl); }
        const hist = tryParse("inteliq_history"); if (hist?.length) { migrate.search_history = hist; setHistory(hist); }
        const addrs = tryParse("inteliq_ledger_addrs"); if (addrs?.length) { migrate.ledger_addrs = addrs; setLedgerAddrs(addrs); }
        const cmc = tryParse("inteliq_cmc"); if (cmc?.length) { migrate.cmc_holdings = cmc; setCmc(cmc); }
        const al = tryParse("inteliq_alerts"); if (al && Object.keys(al).length) { migrate.alerts = al; setAlerts(al); }
        const jn = tryParse("inteliq_journal"); if (jn?.length) { migrate.journal = jn; setJournalEntries(jn); }
        const cr = tryParse("inteliq_calls"); if (cr?.length) { migrate.call_records = cr; setCallRecords(cr); }
        const gt = tryParse("inteliq_glossary_custom"); if (gt?.length) { migrate.glossary_custom = gt; setCustomTerms(gt); }
        if (Object.keys(migrate).length > 0) {
          authFetch("/api/user-data/migrate", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify(migrate) }).catch(()=>{});
        }
      }
      dataLoadedRef.current = true;
    }).catch(() => { dataLoadedRef.current = true; });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch FX rate on mount
  useEffect(() => {
    authFetch("/api/fx/audusd").then(r => r.json()).then(d => { if (d.rate) setAudUsd(d.rate); }).catch(() => {});
  }, []);

  // Fetch live prices whenever holdings, watchlist, or dashboard picks change
  useEffect(() => {
    const all = [
      ...cbHoldings, ...bnHoldings, ...tigerHoldings, ...ldgHoldings, ...cmcHoldings,
      ...dashPicks.map(s => ({ sym:s.sym, priceType:s.priceType })),
      ...watchlist.map(w => ({ sym:w.sym, priceType:w.priceType })),
      ...callRecords.map(c => ({ sym:c.sym, priceType:c.priceType || "stock" })),
    ];
    const unique = all.filter((s,i,a) => a.findIndex(x=>x.sym===s.sym)===i).map(h=>({sym:h.sym,type:h.priceType}));
    if (!unique.length) return;
    authFetch("/api/prices", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({symbols:unique}) })
      .then(r=>r.json()).then(d=>setLP(p=>({...p,...d}))).catch(()=>{});
  }, [cbHoldings,bnHoldings,tigerHoldings,ldgHoldings,cmcHoldings,dashPicks,watchlist]);

  // Patch call records: fix null/zero prices, currency mismatches, or Claude's bad estimates for recent calls
  useEffect(() => {
    if (!Object.keys(livePrices).length || !callRecords.length) return;
    let patched = false;
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    const updated = callRecords.map(c => {
      if (c.pricePatched) return c;
      const lp = livePrices[c.sym];
      if (!lp?.price) return c;
      // Fix null/zero prices
      if (c.priceAtCall == null || c.priceAtCall <= 0) {
        patched = true;
        return { ...c, priceAtCall: lp.price, priceCurrency: lp.currency || c.priceCurrency, pricePatched: true };
      }
      // Fix currency mismatch
      if (lp.currency && c.priceCurrency && lp.currency !== c.priceCurrency) {
        patched = true;
        return { ...c, priceAtCall: lp.price, priceCurrency: lp.currency, pricePatched: true };
      }
      // Fix Claude's bad price estimates for recent calls (<7 days old, >50% off)
      const ageMs = Date.now() - new Date(c.calledAt).getTime();
      if (ageMs < sevenDaysMs) {
        const ratio = lp.price / c.priceAtCall;
        if (ratio > 1.5 || ratio < 0.67) {
          patched = true;
          return { ...c, priceAtCall: lp.price, priceCurrency: lp.currency || c.priceCurrency, pricePatched: true };
        }
      }
      return c;
    });
    if (patched) setCallRecords(updated);
  }, [livePrices]);

  // Persist ledger addresses (debounced to server)
  useEffect(() => {
    if (!dataLoadedRef.current) return;
    const t = setTimeout(() => userDataSave("ledger_addrs", ledgerAddrs), 500);
    return () => clearTimeout(t);
  }, [ledgerAddrs, userDataSave]);

  // Sync exchanges on mount
  useEffect(() => { syncCoinbase(); syncBinance(); syncTiger(); syncTigerOrders(); syncLedger(); }, []);

  // Fetch dashboard picks on mount
  useEffect(() => { fetchDashPicks(); }, []);

  // Fetch news on mount
  useEffect(() => { fetchNews(); }, []);


  // Refetch chart when range or display currency changes
  useEffect(() => {
    if (tab === "detail" && detailSym) fetchChart(detailSym.sym, chartRange);
  }, [chartRange, displayCcy]);

  // Fetch indices summary when tab activates
  useEffect(() => {
    if (tab === "indices" && !indicesData) {
      setIndicesLoading(true);
      authFetch("/api/indices/summary").then(r=>r.json()).then(d=>{setIndicesData(d);setIndicesLoading(false);}).catch(()=>setIndicesLoading(false));
    }
  }, [tab]);

  // Live chart polling
  useEffect(() => {
    if (liveTimerRef.current) { clearInterval(liveTimerRef.current); liveTimerRef.current = null; }
    if (!liveMode || chartRange !== "1d" || tab !== "detail" || !detailSym) return;
    const poll = async () => {
      try {
        const r = await authFetch(`/api/chart/${encodeURIComponent(detailSym.sym)}?range=1d&currency=${displayCcy}`);
        const d = await r.json();
        if (!d.error) { setChartData(d); setLiveLastUpdate(Date.now()); }
      } catch {}
    };
    liveTimerRef.current = setInterval(poll, liveInterval * 1000);
    return () => { if (liveTimerRef.current) clearInterval(liveTimerRef.current); };
  }, [liveMode, liveInterval, chartRange, tab, detailSym?.sym, displayCcy]);

  // Auto-disable live mode when leaving 1D or detail
  useEffect(() => {
    if (chartRange !== "1d" || tab !== "detail") setLiveMode(false);
  }, [chartRange, tab]);

  async function syncCoinbase() {
    setCbS(true); setCbE("");
    try {
      const r = await authFetch("/api/coinbase/balances"); const d = await r.json();
      if (d.error) setCbE(d.error); else { setCb(d.holdings||[]); setCbL(nowTime()); }
    } catch { setCbE("Could not connect — check COINBASE_API_KEY and COINBASE_API_SECRET in .env"); }
    setCbS(false);
  }

  async function syncBinance() {
    setBnS(true); setBnE("");
    try {
      const r = await authFetch("/api/binance/balances"); const d = await r.json();
      if (d.error) setBnE(d.error); else { setBn(d.holdings||[]); setBnL(nowTime()); }
    } catch { setBnE("Could not connect — check BINANCE_API_KEY and BINANCE_API_SECRET in .env"); }
    setBnS(false);
  }

  async function syncTiger() {
    setTigerS(true); setTigerE("");
    try {
      const r = await authFetch("/api/tiger/balances"); const d = await r.json();
      if (d.error) setTigerE(d.error); else { setTiger(d.holdings||[]); setTigerL(nowTime()); }
    } catch { setTigerE("Could not connect — check Tiger API config in .env"); }
    setTigerS(false);
  }

  async function syncTigerOrders() {
    setTigerOL(true);
    try {
      const r = await authFetch("/api/tiger/orders"); const d = await r.json();
      if (!d.error) setTigerOrders(d.orders || []);
    } catch {}
    setTigerOL(false);
  }

  async function syncLedger() {
    if (!ledgerAddrs.length) return;
    setLdgS(true); setLdgE("");
    try {
      const r = await authFetch("/api/ledger/balances", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ addresses: ledgerAddrs }),
      });
      const d = await r.json();
      if (d.error) setLdgE(d.error);
      else { setLdg(d.holdings || []); setLdgL(nowTime()); }
    } catch { setLdgE("Failed to fetch Ledger balances"); }
    setLdgS(false);
  }

  async function fetchDashPicks(force = false) {
    if (dashLoading) return;
    setDashLoading(true); setDashError(null); setDashStatus(null);
    if (force) setDashPicks([]);
    try {
      const url = force ? "/api/dashboard/picks/stream?force=1" : "/api/dashboard/picks/stream";
      const r = await authFetch(url);
      const reader = r.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let picks = null;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ") || line.includes("[DONE]")) continue;
          try {
            const evt = JSON.parse(line.slice(6));
            if (evt.error) throw new Error(evt.error);
            if (evt.status === "done" && evt.picks) { picks = evt.picks; }
            else if (evt.status) { setDashStatus(evt.status); }
          } catch (e) { if (e.message && !e.message.includes("JSON")) throw e; }
        }
      }
      setDashStatus(null);
      if (Array.isArray(picks) && picks.length > 0) {
        setDashPicks(picks);
        picks.filter(pick => ["BUY","WATCH"].includes(pick.verdict)).forEach(pick => recordCall({ ...pick, priceAtCall: pick.priceStatic }, "dashboard"));
        const allText = picks.map(p => [p.summary,p.macro,p.fundamental,p.technical,p.sentiment,p.portfolio].filter(Boolean).join(" ")).join(" ");
        extractGlossaryTerms(allText);
      } else setDashError("No picks returned");
    } catch (e) { setDashError(e.message || "Could not load picks — check your connection"); setDashStatus(null); }
    setDashLoading(false);
  }

  async function fetchNews() {
    setNewsLoading(true);
    try {
      const r = await authFetch("/api/news");
      const d = await r.json();
      if (Array.isArray(d) && d.length > 0) setNewsItems(d);
    } catch {}
    setNewsLoading(false);
  }

  async function fetchIPO() {
    if (ipoLoading) return;
    setIpoLoading(true); setIpoError(null);
    try {
      const r = await authFetch("/api/ipo");
      const d = await r.json();
      if (Array.isArray(d)) setIpoItems(d);
      else setIpoError(d.error || "Failed to load IPO data");
    } catch { setIpoError("Could not load IPO data — check your connection"); }
    setIpoLoading(false);
  }

  useEffect(() => { if (tab === "ipo") fetchIPO(); }, [tab]);

  useEffect(() => {
    if (tab !== "calls" || callRecords.length === 0) return;
    setCallsPriceFetching(true);
    const unique = callRecords
      .filter((c, i, a) => a.findIndex(x => x.sym === c.sym) === i)
      .map(c => ({ sym: c.sym, type: c.priceType }));
    authFetch("/api/prices", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({symbols:unique}) })
      .then(r => r.json()).then(d => setCallsPrices(d)).catch(() => {})
      .finally(() => setCallsPriceFetching(false));
  }, [tab]);

  const allHoldings = [...cbHoldings,...bnHoldings,...tigerHoldings,...ldgHoldings,...cmcHoldings];

  useEffect(() => {
    if (tab !== "coach" || allHoldings.length === 0) return;
    if (!coachReport && !coachLoading) runCoachAnalysis();
  }, [tab, allHoldings.length]);

  // Poll prices for active alerts every 60s
  useEffect(() => {
    const activeAlerts = Object.entries(alerts).filter(([,a]) => a.value && !a.triggered);
    if (!activeAlerts.length) return;
    function checkAlerts(prices) {
      setTriggeredAlerts(prev => {
        const newTriggers = [...prev];
        const updatedAlerts = { ...alerts };
        let changed = false;
        activeAlerts.forEach(([sym, alert]) => {
          const price = prices[sym]?.price;
          if (price == null) return;
          let hit = false;
          if (alert.type === "price_above"  && price >= parseFloat(alert.value)) hit = true;
          if (alert.type === "price_below"  && price <= parseFloat(alert.value)) hit = true;
          if (alert.type === "change_up"    && (prices[sym]?.change || 0) >= parseFloat(alert.value)) hit = true;
          if (alert.type === "change_down"  && (prices[sym]?.change || 0) <= -Math.abs(parseFloat(alert.value))) hit = true;
          if (hit && !updatedAlerts[sym].triggered) {
            updatedAlerts[sym] = { ...updatedAlerts[sym], triggered: true, triggeredAt: new Date().toISOString() };
            if (!newTriggers.includes(sym)) newTriggers.push(sym);
            changed = true;
          }
        });
        if (changed) setAlerts(updatedAlerts);
        return newTriggers;
      });
    }
    const syms = activeAlerts.map(([sym]) => ({ sym, type: watchlist.find(w=>w.sym===sym)?.priceType || "stock" }));
    authFetch("/api/prices", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ symbols: syms }) })
      .then(r => r.json()).then(checkAlerts).catch(() => {});
    const interval = setInterval(() => {
      authFetch("/api/prices", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ symbols: syms }) })
        .then(r => r.json()).then(checkAlerts).catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, [alerts, watchlist]);

  useEffect(() => {
    if (tab !== "macro") return;
    if (macroEvents.length && !macroError) return; // already loaded
    setMacroLoading(true); setMacroError(null);
    const holdingSyms = [...new Set([...allHoldings.map(h=>h.sym), ...watchlist.map(w=>w.sym)])];
    authFetch("/api/macro", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ holdingSyms }) })
      .then(r => r.json()).then(d => { if (d.error) setMacroError(d.error); else setMacroEvents(d); })
      .catch(()=>setMacroError("Could not load macro calendar")).finally(()=>setMacroLoading(false));
  }, [tab]);

  useEffect(() => {
    if (tab !== "earnings") return;
    const allSyms = [...new Set([
      ...allHoldings.map(h => h.sym),
      ...watchlist.map(w => w.sym),
    ])].filter(s => !["BTC","ETH","SOL","BNB","XRP","ADA","DOGE","AVAX"].includes(s.toUpperCase()));
    if (!allSyms.length) return;
    setEarningsLoading(true);
    authFetch("/api/earnings", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ symbols: allSyms }) })
      .then(r => r.json()).then(d => setEarningsData(d)).catch(() => {})
      .finally(() => setEarningsLoading(false));
  }, [tab]);

  async function openDetail(symInfo, stock = null, preloadedAnalysis = null) {
    setDetailFrom(tab);
    setDetailSym(symInfo);
    setDetailStock(stock);
    setTab("detail");
    setChartData(null);
    setChartRange("1mo");
    setLiveMode(false);
    setLiveLastUpdate(null);
    if (preloadedAnalysis) {
      setDetailAnalysis(preloadedAnalysis);
      setDetailAnalysing(false);
      await fetchChart(symInfo.sym, "1mo", true);
    } else {
      setDetailAnalysis(null);
      await fetchChart(symInfo.sym, "1mo");
    }
  }

  async function fetchChart(sym, range, skipAnalysis = false) {
    setChartLoading(true);
    if (!skipAnalysis) setDetailAnalysis(null);
    try {
      const r = await authFetch(`/api/chart/${encodeURIComponent(sym)}?range=${range}&currency=${displayCcy}`);
      const d = await r.json();
      if (!d.error) { setChartData(d); if (!skipAnalysis) fetchDetailAnalysis(d); }
      else console.error("Chart error:", d.error);
    } catch (err) { console.error("Chart fetch failed:", err); }
    setChartLoading(false);
  }

  async function fetchDetailAnalysis(data) {
    setDetailAnalysing(true);
    try {
      // Fetch fundamentals (skip for crypto/indices)
      let fundamentals = null;
      const isIdx = detailSym?.priceType === "index";
      if (!isIdx) {
        try {
          const fr = await authFetch(`/api/fundamentals/${encodeURIComponent(data.sym)}`);
          fundamentals = await fr.json();
        } catch {}
      }
      const iv = detailStock?.verdict || null;
      const r = await authFetch("/api/analyse/detail", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({ sym:data.sym, name:data.name, candles:data.candles, range:data.range, currentPrice:data.currentPrice, currency:data.currency, indicators:data.indicators, fundamentals, initialVerdict:iv, isIndex:isIdx })
      });
      const d = await r.json();
      if (!d.error) {
        setDetailAnalysis(d);
        const allText = [d.summary,d.macro,d.fundamental,d.technical,d.sentiment,d.portfolio,d.momentum,d.volume,d.pattern?.note].filter(Boolean).join(" ");
        extractGlossaryTerms(allText);
      }
    } catch {}
    setDetailAnalysing(false);
  }

  async function handleSearch(overrideQ) {
    const q = (overrideQ || searchQ).trim();
    if (!q) return;
    if (overrideQ) setSearchQ(overrideQ);
    setSearching(true); setResult(null); setSearchErr(null); setExplorerAnalysis(null); setExplorerChart(null);
    try {
      const today = new Date().toLocaleDateString("en-AU",{year:"numeric",month:"long",day:"numeric"});
      const KNOWN_CRYPTO = ["BTC","ETH","SOL","BNB","XRP","ADA","DOGE","AVAX","DOT","MATIC","LINK","UNI","LTC","BCH","ATOM","SHIB","TRX","TON","OP","ARB","NEAR","GRT","NU"];
      const upperQ = q.toUpperCase();
      const isKnownCrypto = KNOWN_CRYPTO.includes(upperQ);
      const isTickerLike = /^[A-Z0-9.]{1,8}$/.test(upperQ);
      const priceType = isKnownCrypto ? "crypto" : "stock";
      // Parallel: live price + fundamentals (if ticker-like and not crypto)
      let livePriceCtx = "";
      let livePrice = null;
      let fundamentals = null;
      if (isTickerLike) {
        try {
          const parallelFetches = [
            authFetch(`/api/price?sym=${upperQ}&type=${priceType}`).then(r=>r.json()).catch(()=>null),
            ...(!isKnownCrypto ? [authFetch(`/api/fundamentals/${upperQ}`).then(r=>r.json()).catch(()=>null)] : []),
          ];
          const [pd, fmpData] = await Promise.all(parallelFetches);
          if (pd?.price) { livePrice = pd.price; const pdCcy = pd.currency || "USD"; livePriceCtx = ` The current live market price is $${pd.price.toLocaleString("en",{minimumFractionDigits:2,maximumFractionDigits:2})} ${pdCcy} as of today — base all price levels, support/resistance, and targets on this actual price.`; }
          if (fmpData) fundamentals = fmpData;
        } catch {}
      }
      // Build fundamentals context for initial analyse call
      let fundamentalsCtx = "";
      if (fundamentals) {
        const parts = [];
        if (fundamentals.pe)        parts.push(`P/E: ${Number(fundamentals.pe).toFixed(1)}x`);
        if (fundamentals.evEbitda)  parts.push(`EV/EBITDA: ${Number(fundamentals.evEbitda).toFixed(1)}x`);
        if (fundamentals.fcfYield)  parts.push(`FCF Yield: ${Number(fundamentals.fcfYield).toFixed(1)}%`);
        if (fundamentals.roe)       parts.push(`ROE: ${(Number(fundamentals.roe)*100).toFixed(1)}%`);
        if (fundamentals.marketCap) parts.push(`Mkt Cap: ${fundamentals.marketCap}`);
        if (fundamentals.analystConsensus) {
          const c = fundamentals.analystConsensus;
          parts.push(`Analyst: ${c.strongBuy}×SB/${c.buy}×B/${c.hold}×H/${c.sell}×S`);
        }
        if (parts.length) fundamentalsCtx = `\n\nLIVE FUNDAMENTALS (FMP): ${parts.join(" | ")}\nUse this live data for your fundamental analysis.`;
      }
      // Stream analysis for faster perceived response
      const streamRes = await authFetch("/api/analyse/stream", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({
        model:"claude-sonnet-4-20250514", max_tokens:1000,
        system:`You are a senior investment analyst. Today is ${today}. Your training knowledge has a cutoff of approximately mid-2025 — do NOT present specific metrics from your training data (e.g. ETF flow volumes, exact hash rates, specific institutional inflow figures, dated earnings numbers) as if they are current facts for ${today}. For fundamentals, focus on structural and qualitative factors. If you cite a specific metric that may have changed, frame it as approximate or add "as of mid-2025". All macro commentary must reflect conditions as of ${today} — do not reference past rate decisions or events as if they are upcoming. For crypto assets (BTC, ETH, SOL etc) analyse the native coin/token directly — do NOT substitute an ETF or trust product. IMPORTANT: set priceStatic to 0 — the live price will be injected separately. Target price must be in the stock's native trading currency (AUD for ASX .AX stocks, USD for US stocks). Respond ONLY with valid JSON, no markdown:
{"sym":"TICKER","name":"Full name","sector":"sector","verdict":"BUY|WATCH|AVOID|HOLD","conviction":"HIGH|MEDIUM|LOW","horizon":"Short|Medium|Long","priceStatic":0,"target":"$X","upside":"+X%","up":true,"priceType":"stock or crypto","avgCurrency":"USD or AUD","priceCurrency":"USD or AUD","summary":"2-3 sentences","macro":"2-3 sentences","fundamental":"2-3 sentences","technical":"2-3 sentences","sentiment":"2-3 sentences","insider":"2-3 sentences","portfolio":"2-3 sentences"}`,
        messages:[{role:"user",content:`Analyse this investment: ${q}.${livePriceCtx}${fundamentalsCtx}`}]
      })});
      const reader = streamRes.body.getReader();
      const decoder = new TextDecoder();
      let fullText = "";
      let streamBuf = "";
      const statusStages = [
        { at: 50, msg: "Reading fundamentals..." },
        { at: 150, msg: "Checking macro conditions..." },
        { at: 300, msg: "Analysing technicals..." },
        { at: 450, msg: "Evaluating sentiment..." },
        { at: 600, msg: "Forming verdict..." },
      ];
      let charCount = 0;
      let stageIdx = 0;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        streamBuf += decoder.decode(value, { stream: true });
        const lines = streamBuf.split("\n");
        streamBuf = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ") || line.includes("[DONE]")) continue;
          try {
            const evt = JSON.parse(line.slice(6));
            if (evt.t) { fullText += evt.t; charCount += evt.t.length; }
            if (evt.error) throw new Error(evt.error);
          } catch (e) { if (e.message && !e.message.includes("JSON")) throw e; }
        }
        if (stageIdx < statusStages.length && charCount >= statusStages[stageIdx].at) {
          setSearchStatus(statusStages[stageIdx].msg);
          stageIdx++;
        }
      }
      setSearchStatus(null);
      const text = fullText;
      const parsed = JSON.parse(text.slice(text.indexOf("{"), text.lastIndexOf("}")+1));
      setResult(parsed);
      recordCall({ ...parsed, priceAtCall: livePrice || parsed.priceStatic }, "explorer");
      const explorerText = [parsed.summary,parsed.macro,parsed.fundamental,parsed.technical,parsed.sentiment,parsed.portfolio].filter(Boolean).join(" ");
      extractGlossaryTerms(explorerText);
      // Auto-fetch inline chart using display currency for crypto (BTC-USD or BTC-AUD)
      setExplorerChart(null); setExplorerChartLoading(true);
      authFetch(`/api/chart/${encodeURIComponent(parsed.sym)}?range=1mo&currency=${displayCcy}`)
        .then(r=>r.json()).then(async d=>{
          if(!d.error){
            setExplorerChart(d);
            // Fetch fundamentals for the parsed symbol (may differ from search query)
            let detailFundamentals = null;
            if (parsed.priceType !== "crypto") {
              try { detailFundamentals = await authFetch(`/api/fundamentals/${encodeURIComponent(parsed.sym)}`).then(r=>r.json()); } catch {}
            }
            // Run comprehensive analysis and update card verdict to match
            try {
              const ar = await authFetch("/api/analyse/detail",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({...d, fundamentals:detailFundamentals, initialVerdict:parsed.verdict})});
              const analysis = await ar.json();
              if(!analysis.error){
                setExplorerAnalysis(analysis);
                setResult(prev=>prev?{...prev,verdict:analysis.verdict,conviction:analysis.conviction,target:analysis.target,horizon:analysis.horizon}:prev);
              }
            } catch {}
          }
        }).catch(()=>{})
        .finally(()=>setExplorerChartLoading(false));
      // Save to history
      setHistory(prev => [
        { ...parsed, analysedAt: new Date().toISOString() },
        ...prev.filter(h => h.sym !== parsed.sym)
      ].slice(0, 10));
      authFetch(`/api/price?sym=${parsed.sym}&type=${parsed.priceType}`).then(r=>r.json()).then(d=>{
        setLP(p=>({...p,[parsed.sym]:d}));
        if(d?.price) setResult(prev=>prev?{...prev,priceStatic:d.price,priceCurrency:d.currency||prev.priceCurrency}:prev);
      }).catch(()=>{});
    } catch { setSearchErr("Analysis failed — please try again."); setSearchStatus(null); }
    setSearching(false);
  }

  function addToWatchlist(stock) {
    if (watchlist.find(w => w.sym === stock.sym)) return;
    setWatchlist(prev => [{
      sym: stock.sym, name: stock.name, sector: stock.sector,
      priceType: stock.priceType || "stock",
      priceCurrency: livePrices[stock.sym]?.currency || stock.priceCurrency || "USD",
      target: parseTargetNum(stock.target),
      targetCcy: livePrices[stock.sym]?.currency || stock.avgCurrency || "USD",
      addedAt: new Date().toISOString(),
      note: stock.summary ? stock.summary.slice(0, 100) + (stock.summary.length > 100 ? "…" : "") : "",
    }, ...prev]);
  }

  async function analyseJournal() {
    if (journalEntries.length < 3) return;
    setJournalAnalysing(true); setJournalAnalysisError(null);
    try {
      const r = await authFetch("/api/journal/analyse", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ entries: journalEntries }) });
      const d = await r.json();
      if (d.error) setJournalAnalysisError(d.error); else setJournalAnalysis(d);
    } catch { setJournalAnalysisError("Analysis failed — please try again."); }
    setJournalAnalysing(false);
  }

  async function runScreener(q) {
    const query = (q || screenerQ).trim();
    if (!query) return;
    setScreenerLoading(true); setScreenerError(null); setScreenerResults(null);
    try {
      const r = await authFetch("/api/screener", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ query }) });
      const d = await r.json();
      if (d.error) setScreenerError(d.error);
      else { setScreenerResults(d); d.filter(pick => ["BUY","WATCH"].includes(pick.verdict)).forEach(pick => recordCall({...pick, priceAtCall: pick.priceStatic}, "screener")); }
    } catch { setScreenerError("Screener failed — please try again."); }
    setScreenerLoading(false);
  }

  function buildPortfolioSnapshot(holdings, prices, audUsdRate) {
    let totalUSD = 0;
    const details = [];
    for (const h of holdings) {
      const lp = prices[h.sym];
      const priceCcy = h.priceCurrency || lp?.currency || "USD";
      const price = lp?.price;
      const priceUSD = price ? toDisplay(price, priceCcy, "USD", audUsdRate) : null;
      const avgUSD   = h.avg ? toDisplay(h.avg, h.avgCurrency || "USD", "USD", audUsdRate) : null;
      const valueUSD = priceUSD != null ? h.qty * priceUSD : (avgUSD ? h.qty * avgUSD : 0);
      const unrealisedPct = priceUSD && avgUSD ? ((priceUSD - avgUSD) / avgUSD) * 100 : null;
      totalUSD += valueUSD;
      details.push({ sym: h.sym, name: h.name || h.sym, sector: h.sector || "Unknown", priceType: h.priceType || "stock", valueUSD, unrealisedPct });
    }
    details.forEach(d => { d.pct = totalUSD > 0 ? (d.valueUSD / totalUSD) * 100 : 0; });
    details.sort((a, b) => b.valueUSD - a.valueUSD);
    const sectorBreakdown = {};
    details.forEach(d => { const s = d.sector || "Unknown"; sectorBreakdown[s] = (sectorBreakdown[s] || 0) + d.pct; });
    const cryptoPct = details.filter(d => d.priceType === "crypto").reduce((s, d) => s + d.pct, 0);
    return { totalValueUSD: totalUSD, holdings: details, sectorBreakdown, cryptoPct, stocksPct: 100 - cryptoPct };
  }

  async function runCoachAnalysis() {
    setCoachLoading(true); setCoachError(null);
    const snapshot = buildPortfolioSnapshot(allHoldings, livePrices, audUsd);
    if (!snapshot.holdings.length) { setCoachError("No holdings available"); setCoachLoading(false); return; }
    try {
      const r = await authFetch("/api/portfolio/coach", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ snapshot }) });
      const d = await r.json();
      if (d.error) setCoachError(d.error); else setCoachReport(d);
    } catch { setCoachError("Analysis failed — check your connection"); }
    setCoachLoading(false);
  }

  async function fetchEarningsBrief(sym, upcomingDate, recentSurprises) {
    setEarningsBriefing(p => ({...p, [sym]: true}));
    const today = new Date().toLocaleDateString("en-AU",{year:"numeric",month:"long",day:"numeric"});
    const surpriseCtx = recentSurprises?.length
      ? `Recent EPS surprises: ${recentSurprises.map(e => `${e.date} actual ${e.epsActual} vs est ${e.epsEstimated} (${e.surprise||"?"}) `).join(", ")}.`
      : "";
    try {
      const r = await authFetch("/api/analyse", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          model:"claude-haiku-4-5-20251001", max_tokens:600,
          system:`You are a senior analyst. Today is ${today}. Return ONLY valid JSON:
{"watch":"Key metric/number to watch for","consensus":"What the market expects — be specific","risk":"Main earnings risk in 1 sentence","setup":"Technical/positioning setup going into earnings in 1 sentence","verdict":"BEAT_LIKELY|MISS_LIKELY|IN_LINE|UNCERTAIN"}`,
          messages:[{role:"user",content:`Pre-earnings brief for ${sym}. Upcoming date: ${upcomingDate||"next quarter"}. ${surpriseCtx} What should investors watch?`}],
        }),
      });
      const d = await r.json();
      const text = d.content?.find(b=>b.type==="text")?.text||"";
      const start = text.indexOf("{"), end = text.lastIndexOf("}");
      if (start !== -1 && end !== -1) setEarningsBriefs(p => ({...p, [sym]: JSON.parse(text.slice(start, end+1))}));
    } catch {}
    setEarningsBriefing(p => ({...p, [sym]: false}));
  }

  function recordCall(parsed, source) {
    setCallRecords(prev => {
      // Never overwrite an existing record — preserve the original call date and entry price
      if (prev.find(c => c.sym === parsed.sym)) return prev;
      const lp = livePrices[parsed.sym];
      const record = {
        id: `${parsed.sym}-${Date.now()}`,
        sym: parsed.sym,           name: parsed.name || parsed.sym,
        verdict: parsed.verdict,   conviction: parsed.conviction || "MEDIUM",
        horizon: parsed.horizon || "Medium",
        priceAtCall:   lp?.price || parsed.priceAtCall || parsed.priceStatic || null,
        priceType:     parsed.priceType    || "stock",
        priceCurrency: lp?.currency || parsed.priceCurrency || "USD",
        calledAt: new Date().toISOString(),
        target: parsed.target || null,
        source,
      };
      return [record, ...prev].slice(0, 50);
    });
  }

  const filteredNews = newsFilter === "ALL"
    ? newsItems
    : newsItems.filter(n => n.tag === newsFilter);

  const fxLabel = `AUD/USD: ${audUsd.toFixed(4)}`;

  return (
    <GlossaryCtx.Provider value={{ allGlossary, openGlossary }}>
    <>
      <style>{css}</style>
      <div className="app-layout" style={{ display:"flex", minHeight:"100vh", overflow:"hidden", width:"100%" }}>

        {/* ── Sidebar ── */}
        <aside className="app-sidebar" style={{ width:"calc(220px + env(safe-area-inset-left, 0px))", background:"var(--sidebar)", borderRight:"1px solid var(--border)", padding:"20px 12px", paddingLeft:"calc(env(safe-area-inset-left, 0px) + 12px)", display:"flex", flexDirection:"column", position:"sticky", top:0, height:"100vh", flexShrink:0 }}>
          <div className="sidebar-header" style={{ padding:"8px 8px 24px", borderBottom:"1px solid var(--border)", marginBottom:24 }}>
            <span style={{ fontFamily:"var(--ff-head)", fontSize:20, fontWeight:900, letterSpacing:"-0.03em", color:"var(--text2)" }}>
              INTEL<span style={{ color:"var(--green)" }}>IQ</span>
            </span>
            <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"var(--ff-mono)", marginTop:2, letterSpacing:"0.08em" }}>INVESTMENT INTELLIGENCE</div>
          </div>
          <nav style={{ flex:1, display:"flex", flexDirection:"column", gap:4 }}>
            {TABS.map(t => (
              <button key={t.id} className={`nav-item${tab===t.id?" active":""}${!MOBILE_BAR_IDS.includes(t.id)?" mobile-hidden":""}`} onClick={()=>{setTab(t.id);setMobileMore(false);}}>
                <span className="nav-icon" style={{ fontSize:14, width:20, textAlign:"center", flexShrink:0, display:"inline-block" }}>{t.icon}</span>
                <span className="nav-label">{t.label}</span>
                {t.id==="portfolio"&&allHoldings.length>0&&(
                  <span className="nav-badge" style={{ marginLeft:"auto", background:"var(--green)20", color:"var(--green)", borderRadius:6, padding:"1px 7px", fontSize:10, fontFamily:"var(--ff-mono)", border:"1px solid var(--green)40" }}>{allHoldings.length}</span>
                )}
                {t.id==="watchlist"&&watchlist.length>0&&(
                  <span className="nav-badge" style={{ marginLeft:"auto", background:"var(--blue)20", color:"var(--blue)", borderRadius:6, padding:"1px 7px", fontSize:10, fontFamily:"var(--ff-mono)", border:"1px solid var(--blue)40" }}>{watchlist.length}</span>
                )}
                {t.id==="calls"&&callRecords.length>0&&(
                  <span className="nav-badge" style={{ marginLeft:"auto", background:"var(--purple)20", color:"var(--purple)", borderRadius:6, padding:"1px 7px", fontSize:10, fontFamily:"var(--ff-mono)", border:"1px solid var(--purple)40" }}>{callRecords.length}</span>
                )}
              </button>
            ))}
            <button className={`nav-item mobile-more-btn${MOBILE_MORE_TABS.some(t=>t.id===tab)?" active":""}`} onClick={()=>setMobileMore(m=>!m)}>
              <span className="nav-icon" style={{ fontSize:14 }}>⋯</span>
              <span className="nav-label">More</span>
            </button>
            <div className="sidebar-glossary" style={{ borderTop:"1px solid var(--border)", margin:"8px 0 4px", paddingTop:8 }}>
              <button className="nav-item" onClick={()=>openGlossary()} style={{ color:"var(--amber)", width:"100%" }}>
                <span className="nav-icon" style={{ fontSize:14 }}>◈</span>
                <span>Glossary</span>
                {customTerms.length > 0 && (
                  <span className="nav-badge" style={{ marginLeft:"auto", background:"var(--amber)20", color:"var(--amber)", borderRadius:6, padding:"1px 7px", fontSize:10, fontFamily:"var(--ff-mono)", border:"1px solid var(--amber)40" }}>{customTerms.length}</span>
                )}
              </button>
            </div>
          </nav>
          <div className="sidebar-footer" style={{ padding:"16px 8px 0", borderTop:"1px solid var(--border)", marginTop:16 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
              <span style={{ width:7, height:7, borderRadius:"50%", background:"var(--green)", animation:"pulse 2s infinite" }}/>
              <div>
                <div style={{ fontSize:10, fontFamily:"var(--ff-mono)", color:"var(--green)", letterSpacing:"0.08em" }}>LIVE DATA</div>
                <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"var(--ff-mono)" }}>Yahoo · CoinGecko</div>
              </div>
            </div>
            <div style={{ fontSize:9, color:"var(--muted)", fontFamily:"var(--ff-mono)", letterSpacing:"0.04em", marginBottom:10 }}>{fxLabel}</div>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:6 }}>
              <div style={{ fontSize:10, color:"var(--muted2)", fontFamily:"var(--ff-mono)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={user.email}>
                {user.name || user.email}
              </div>
              <button onClick={onLogout} style={{ background:"none", border:"1px solid var(--border)", borderRadius:6, padding:"4px 10px", fontSize:9, fontFamily:"var(--ff-mono)", color:"var(--muted)", cursor:"pointer", letterSpacing:"0.06em", flexShrink:0 }}>
                SIGN OUT
              </button>
            </div>
          </div>
        </aside>

        {/* ── Mobile More Menu ── */}
        {mobileMore && (
          <>
            <div className="mobile-more-overlay" onClick={()=>setMobileMore(false)}/>
            <div className="mobile-more-menu">
              {MOBILE_MORE_TABS.map(t => (
                <button key={t.id} className={tab===t.id?"active":""} onClick={()=>{setTab(t.id);setMobileMore(false);}}>
                  <span className="nav-icon">{t.icon}</span>
                  <span>{t.label}</span>
                </button>
              ))}
              <button className={false?"active":""} onClick={()=>{openGlossary();setMobileMore(false);}}>
                <span className="nav-icon" style={{color:"var(--amber)"}}>◈</span>
                <span>Glossary</span>
              </button>
            </div>
          </>
        )}

        {/* ── Main ── */}
        {/* Pull-to-refresh indicator */}
        {pullY > 0 && (
          <div className="pull-indicator" style={{ transform: `translateY(${pullY - 32}px)` }}>
            <div className={`pull-indicator-dot${isRefreshing?" refreshing":""}`}>{isRefreshing ? "↻" : "↓"}</div>
          </div>
        )}

        <main ref={mainRef} className="app-main" style={{ flex:1, padding:"32px 36px", paddingRight:"calc(env(safe-area-inset-right, 0px) + 36px)", overflowY:"auto", overflowX:"hidden", minWidth:0, maxWidth:"100%", background:"var(--bg)" }}
          onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>

          {/* ══ DASHBOARD ══ */}
          {tab==="dashboard"&&(
            <div>
              <div className="fu" style={{ marginBottom:32 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12, flexWrap:"wrap" }}>
                  <div>
                    <h1 style={{ fontFamily:"var(--ff-head)", fontSize:28, fontWeight:800, letterSpacing:"-0.03em", color:"var(--text2)", lineHeight:1.2, marginBottom:6 }}>
                      {(()=>{const h=new Date().getHours();const firstName=(user.name||"").split(" ")[0];const g=h<12?"Good morning":h<17?"Good afternoon":"Good evening";return firstName?`${g}, ${firstName}.`:`${g}.`;})()}<br/>
                      <span style={{ color:"var(--muted2)", fontWeight:600, fontSize:22 }}>
                        {dashLoading ? (dashStatus || "Generating picks…") : dashPicks.length > 0 ? `${dashPicks.length} high-conviction picks today.` : "Today's top picks."}
                      </span>
                    </h1>
                    <p style={{ fontSize:11, color:"var(--muted)", fontFamily:"var(--ff-mono)", letterSpacing:"0.06em" }}>
                      {new Date().toLocaleDateString("en-AU",{weekday:"long",day:"numeric",month:"long"}).toUpperCase()}
                      {dashPicks.length > 0 && <span style={{marginLeft:10,color:"var(--green)"}}>· AI GENERATED · LIVE</span>}
                    </p>
                  </div>
                  <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                    <button onClick={()=>fetchDashPicks(true)} disabled={dashLoading} style={{background:"none",border:"1px solid var(--border)",borderRadius:8,padding:"7px 16px",fontSize:10,color:dashLoading?"var(--muted)":"var(--muted2)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em",opacity:dashLoading?.5:1}}>
                      {dashLoading ? "↻ LOADING…" : "↻ REFRESH"}
                    </button>
                    {dashPicks.length > 0 && (
                      <button onClick={async()=>{setGlossaryExtracting(true);const t=dashPicks.map(p=>[p.summary,p.macro,p.fundamental,p.technical,p.sentiment,p.portfolio].filter(Boolean).join(" ")).join(" ");await extractGlossaryTerms(t);setGlossaryExtracting(false);openGlossary();}} disabled={glossaryExtracting} style={{background:"none",border:"1px solid var(--border)",borderRadius:8,padding:"7px 16px",fontSize:10,color:glossaryExtracting?"var(--muted)":"var(--amber)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em",opacity:glossaryExtracting?.5:1}}>
                        {glossaryExtracting ? "◈ UPDATING…" : "◈ UPDATE GLOSSARY"}
                      </button>
                    )}
                    <CurrencyToggle value={displayCcy} onChange={setDisplayCcy}/>
                  </div>
                </div>
              </div>

              {/* Loading shimmer */}
              {dashLoading && dashPicks.length === 0 && (
                <div style={{display:"grid",gap:12,marginBottom:28}}>
                  {dashStatus&&<div style={{fontSize:13,color:"var(--green)",fontFamily:"var(--ff-head)",fontWeight:600,marginBottom:4,display:"flex",alignItems:"center",gap:8}}><span style={{width:8,height:8,borderRadius:"50%",background:"var(--green)",display:"inline-block",animation:"pulse 1.5s infinite"}}></span>{dashStatus}</div>}
                  {[1,2,3].map(i=>(
                    <div key={i} className="card" style={{padding:20}}>
                      <div style={{display:"flex",gap:12,marginBottom:14,alignItems:"center"}}>
                        <div className="shimmer-el" style={{width:44,height:44,borderRadius:10,flexShrink:0}}/>
                        <div style={{flex:1}}>
                          <div className="shimmer-el" style={{width:"40%",height:14,marginBottom:8}}/>
                          <div className="shimmer-el" style={{width:"60%",height:11}}/>
                        </div>
                        <div className="shimmer-el" style={{width:60,height:24,borderRadius:6}}/>
                      </div>
                      <div className="shimmer-el" style={{width:"90%",height:11,marginBottom:6}}/>
                      <div className="shimmer-el" style={{width:"75%",height:11}}/>
                    </div>
                  ))}
                </div>
              )}

              {/* Error state */}
              {dashError && !dashLoading && (
                <div style={{background:"#ff525212",border:"1px solid #ff525230",borderRadius:12,padding:"20px 24px",marginBottom:28}}>
                  <div style={{fontSize:10,fontFamily:"var(--ff-mono)",color:"var(--red)",letterSpacing:"0.08em",marginBottom:6}}>COULD NOT GENERATE PICKS</div>
                  <p style={{fontSize:13,color:"var(--muted2)",marginBottom:14}}>{dashError}</p>
                  <button onClick={()=>fetchDashPicks()} style={{background:"none",border:"1px solid #ff525240",borderRadius:7,padding:"7px 16px",fontSize:11,color:"var(--red)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em",cursor:"pointer"}}>TRY AGAIN</button>
                </div>
              )}

              {/* Live picks */}
              {dashPicks.length > 0 && (
                <div className="fu3">
                  <div className="section-label">TOP PICKS TODAY</div>
                  {dashPicks.map(s=>(
                    <StockCard key={s.sym} stock={s} expanded={expanded===s.sym} onToggle={()=>setExp(expanded===s.sym?null:s.sym)} livePrices={livePrices} displayCcy={displayCcy} audUsd={audUsd} onViewChart={()=>openDetail({sym:s.sym,name:s.name,priceType:s.priceType,priceCurrency:s.priceCurrency||"USD",sector:s.sector},s)} onAddWatchlist={()=>addToWatchlist(s)} inWatchlist={!!watchlist.find(w=>w.sym===s.sym)} onTrade={s.priceType==="crypto"&&(cbLastSync||bnLastSync)?()=>setTradeModal({sym:s.sym,name:s.name,productId:`${s.sym}-USD`,side:"BUY",priceType:"crypto"}):undefined}/>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ══ EXPLORER ══ */}
          {tab==="explorer"&&(
            <div>
              <div className="fu" style={{marginBottom:28}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap",marginBottom:8}}>
                  <div>
                    <h1 style={{fontFamily:"var(--ff-head)",fontSize:26,fontWeight:800,color:"var(--text2)",marginBottom:6}}>Stock Explorer</h1>
                    <p style={{fontSize:13,color:"var(--muted2)"}}>AI analysis with drillable reasoning chain for any stock, ETF or crypto.</p>
                  </div>
                  <CurrencyToggle value={displayCcy} onChange={setDisplayCcy}/>
                </div>
              </div>
              <div className="fu2" style={{display:"flex",gap:8,marginBottom:20}}>
                <input value={searchQ} onChange={e=>setSearchQ(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleSearch()} placeholder="e.g. NVIDIA, BHP, Bitcoin…" style={{flex:1,minWidth:0,background:"var(--card)",border:"1px solid var(--border)",borderRadius:10,padding:"13px 18px",color:"var(--text2)",fontSize:14}}/>
                <button onClick={()=>handleSearch()} disabled={searching} style={{background:searching?"var(--card)":"var(--green)",color:searching?"var(--muted)":"#0a0a14",border:"none",borderRadius:10,padding:"13px 16px",fontSize:13,fontFamily:"var(--ff-head)",fontWeight:700,opacity:searching?.7:1,cursor:searching?"default":"pointer",flexShrink:0,whiteSpace:"nowrap"}}>
                  {searching?(searchStatus||"Analysing…"):"Analyse →"}
                </button>
              </div>

              {searching&&<div style={{display:"grid",gap:10,marginBottom:20}}>
                {searchStatus&&<div style={{fontSize:13,color:"var(--green)",fontFamily:"var(--ff-head)",fontWeight:600,marginBottom:4,display:"flex",alignItems:"center",gap:8}}><span style={{width:8,height:8,borderRadius:"50%",background:"var(--green)",display:"inline-block",animation:"pulse 1.5s infinite"}}></span>{searchStatus}</div>}
                {[85,65,75].map((w,i)=><div key={i} className="shimmer-el" style={{height:20,width:`${w}%`}}/>)}
              </div>}
              {searchError&&<div style={{background:"#ff525212",border:"1px solid #ff525230",borderRadius:10,padding:16,color:"var(--red)",fontSize:13,marginBottom:20}}>{searchError}</div>}

              {searchResult&&!searching&&(
                <div className="fi">
                  <StockCard
                    stock={searchResult} expanded={true} onToggle={()=>{}}
                    livePrices={livePrices} displayCcy={displayCcy} audUsd={audUsd}
                    onAddWatchlist={() => addToWatchlist(searchResult)}
                    inWatchlist={!!watchlist.find(w => w.sym === searchResult.sym)}
                    onViewChart={() => openDetail({ sym:searchResult.sym, name:searchResult.name, priceType:searchResult.priceType, priceCurrency:searchResult.priceCurrency||"USD", sector:searchResult.sector }, searchResult, explorerAnalysis)}
                    onTrade={searchResult.priceType==="crypto"&&(cbLastSync||bnLastSync)?()=>setTradeModal({sym:searchResult.sym,name:searchResult.name,productId:`${searchResult.sym}-USD`,side:"BUY",priceType:"crypto"}):searchResult.priceType==="stock"&&tigerLastSync?()=>setTradeModal({sym:searchResult.sym,name:searchResult.name,side:"BUY",priceType:"stock",exchange:"tiger"}):undefined}
                  />
                  {explorerChartLoading && (
                    <div className="shimmer-el" style={{height:260,marginTop:16,borderRadius:14}}/>
                  )}
                  {explorerChart && !explorerChartLoading && (
                    <div style={{marginTop:16,background:"var(--card)",border:"1px solid var(--border)",borderRadius:14,padding:"16px 20px"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                        <span style={{fontSize:10,fontFamily:"var(--ff-mono)",color:"var(--muted)",letterSpacing:"0.12em",textTransform:"uppercase"}}>Price Chart — 1 Month</span>
                        <div style={{display:"flex",gap:8}}>
                          {searchResult?.priceType==="crypto" && (cbLastSync || bnLastSync) && (
                            <button onClick={()=>setTradeModal({sym:searchResult.sym,name:searchResult.name,productId:`${searchResult.sym}-USD`,side:"BUY",priceType:"crypto"})} style={{background:"#00e67610",border:"1px solid #00e67640",borderRadius:7,padding:"4px 12px",fontSize:10,color:"var(--green)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em",fontWeight:700}}>
                              ↔ TRADE
                            </button>
                          )}
                          {searchResult?.priceType==="stock" && tigerLastSync && (
                            <button onClick={()=>setTradeModal({sym:searchResult.sym,name:searchResult.name,side:"BUY",priceType:"stock",exchange:"tiger"})} style={{background:"#00e67610",border:"1px solid #00e67640",borderRadius:7,padding:"4px 12px",fontSize:10,color:"var(--green)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em",fontWeight:700}}>
                              ↔ TRADE
                            </button>
                          )}
                          <button onClick={()=>openDetail({sym:searchResult.sym,name:searchResult.name,priceType:searchResult.priceType,priceCurrency:searchResult.priceCurrency||"USD",sector:searchResult.sector},searchResult,explorerAnalysis)} style={{background:"none",border:"1px solid var(--border)",borderRadius:7,padding:"4px 12px",fontSize:10,color:"var(--muted2)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em"}}>
                            FULL ANALYSIS →
                          </button>
                        </div>
                      </div>
                      <ChartCanvas candles={explorerChart.candles} analysis={null} range="1mo" currency={explorerChart.currency} indicators={explorerChart.indicators} displayCcy={displayCcy} audUsd={audUsd}/>
                    </div>
                  )}
                  <button onClick={()=>{setResult(null);setSearchQ("");setExplorerChart(null);}} style={{marginTop:8,background:"none",border:"none",fontSize:10,color:"var(--muted)",fontFamily:"var(--ff-mono)",padding:0,letterSpacing:"0.06em",cursor:"pointer"}}>
                    ← NEW SEARCH
                  </button>
                </div>
              )}

              {!searchResult&&!searching&&(
                <div>
                  {searchHistory.length > 0 && (
                    <div className="fu2" style={{marginBottom:28}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                        <div className="section-label" style={{marginBottom:0}}>RECENT ANALYSES</div>
                        <button onClick={()=>setHistory([])} style={{background:"none",border:"none",fontSize:10,color:"var(--muted)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em",cursor:"pointer"}}>CLEAR</button>
                      </div>
                      <div style={{display:"grid",gap:6}}>
                        {searchHistory.slice(0,6).map(h=>(
                          <div key={h.sym} onClick={()=>setResult(h)} style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:10,padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",transition:"all .15s"}} onMouseEnter={e=>e.currentTarget.style.borderColor="var(--border2)"} onMouseLeave={e=>e.currentTarget.style.borderColor="var(--border)"}>
                            <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                              <VerdictBadge v={h.verdict}/>
                              <span style={{fontFamily:"var(--ff-head)",fontSize:14,fontWeight:700,color:"var(--text2)"}}>{h.sym}</span>
                              <span style={{fontSize:12,color:"var(--muted2)"}}>{h.name}</span>
                              <SectorBadge sector={h.sector}/>
                            </div>
                            <div style={{display:"flex",gap:10,alignItems:"center",flexShrink:0}}>
                              {h.analysedAt && <span style={{fontSize:10,color:"var(--muted)",fontFamily:"var(--ff-mono)"}}>{fmtDate(h.analysedAt)}</span>}
                              <span style={{fontSize:10,color:"var(--green)",fontFamily:"var(--ff-mono)"}}>VIEW →</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="fu3">
                    <div className="section-label">SUGGESTED</div>
                    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                      {["NVIDIA","BHP.AX","Bitcoin","Rio Tinto","Solana","CBA.AX","Palantir","Ethereum","AMD"].map(s=>(
                        <button key={s} onClick={()=>setSearchQ(s)} style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:8,padding:"8px 16px",fontSize:12,color:"var(--muted2)",fontFamily:"var(--ff-mono)"}}>{s}</button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ══ SCREENER ══ */}
          {tab==="screener"&&(
            <div>
              <div className="fu" style={{marginBottom:28}}>
                <div>
                  <h1 style={{fontFamily:"var(--ff-head)",fontSize:26,fontWeight:800,color:"var(--text2)",marginBottom:6}}>Natural Language Screener</h1>
                  <p style={{fontSize:13,color:"var(--muted2)"}}>Describe what you're looking for — AI finds matching stocks with full analysis.</p>
                </div>
              </div>

              <div className="fu2" style={{display:"flex",gap:8,marginBottom:20}}>
                <input
                  value={screenerQ} onChange={e=>setScreenerQ(e.target.value)}
                  onKeyDown={e=>e.key==="Enter"&&runScreener()}
                  placeholder="e.g. Undervalued ASX small-caps with strong cash flow and insider buying…"
                  style={{flex:1,background:"var(--card)",border:"1px solid var(--border)",borderRadius:10,padding:"13px 18px",color:"var(--text2)",fontSize:14}}
                />
                <button onClick={()=>runScreener()} disabled={screenerLoading||!screenerQ.trim()} style={{background:screenerLoading||!screenerQ.trim()?"var(--card)":"var(--green)",color:screenerLoading||!screenerQ.trim()?"var(--muted)":"#0a0a14",border:"none",borderRadius:10,padding:"13px 28px",fontSize:13,fontFamily:"var(--ff-head)",fontWeight:700,opacity:screenerLoading?.7:1}}>
                  {screenerLoading?"Screening…":"Screen →"}
                </button>
              </div>

              {/* Suggested screens */}
              {!screenerResults && !screenerLoading && (
                <div className="fu3">
                  <div className="section-label">SUGGESTED SCREENS</div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    {[
                      "Undervalued ASX small-caps with strong cash flow",
                      "High-conviction AI infrastructure plays",
                      "Beaten-down crypto with strong fundamentals",
                      "ASX mining stocks leveraged to China recovery",
                      "US dividend stocks with growing yields",
                      "High-growth software companies with positive FCF",
                    ].map(s=>(
                      <button key={s} onClick={()=>{setScreenerQ(s);runScreener(s);}} style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:8,padding:"8px 14px",fontSize:11,color:"var(--muted2)",fontFamily:"var(--ff-mono)",textAlign:"left"}}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {screenerLoading && (
                <div style={{display:"grid",gap:12}}>
                  <div style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:10,padding:"14px 18px",marginBottom:4}}>
                    <div style={{display:"flex",gap:8,alignItems:"center"}}>
                      <div style={{width:8,height:8,borderRadius:"50%",background:"var(--green)",animation:"pulse 1s infinite"}}/>
                      <span style={{fontSize:11,fontFamily:"var(--ff-mono)",color:"var(--muted)"}}>AI is screening the market…</span>
                    </div>
                  </div>
                  {[1,2,3].map(i=>(
                    <div key={i} className="card" style={{padding:20}}>
                      <div style={{display:"flex",gap:12,marginBottom:14,alignItems:"center"}}>
                        <div className="shimmer-el" style={{width:44,height:44,borderRadius:10,flexShrink:0}}/>
                        <div style={{flex:1}}><div className="shimmer-el" style={{width:"40%",height:14,marginBottom:8}}/><div className="shimmer-el" style={{width:"60%",height:11}}/></div>
                        <div className="shimmer-el" style={{width:60,height:24,borderRadius:6}}/>
                      </div>
                      <div className="shimmer-el" style={{width:"90%",height:11,marginBottom:6}}/>
                      <div className="shimmer-el" style={{width:"75%",height:11}}/>
                    </div>
                  ))}
                </div>
              )}

              {screenerError && !screenerLoading && (
                <div style={{background:"#ff525212",border:"1px solid #ff525230",borderRadius:12,padding:"20px 24px",marginBottom:20}}>
                  <div style={{fontSize:10,fontFamily:"var(--ff-mono)",color:"var(--red)",letterSpacing:"0.08em",marginBottom:6}}>SCREENING FAILED</div>
                  <p style={{fontSize:13,color:"var(--muted2)",marginBottom:14}}>{screenerError}</p>
                  <button onClick={()=>runScreener()} style={{background:"none",border:"1px solid #ff525240",borderRadius:7,padding:"7px 16px",fontSize:11,color:"var(--red)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em",cursor:"pointer"}}>TRY AGAIN</button>
                </div>
              )}

              {screenerResults && !screenerLoading && (
                <div className="fi">
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                    <div className="section-label" style={{marginBottom:0}}>{screenerResults.length} MATCHES FOUND</div>
                    <button onClick={()=>{setScreenerResults(null);setScreenerQ("");}} style={{background:"none",border:"none",fontSize:10,color:"var(--muted)",fontFamily:"var(--ff-mono)",padding:0,letterSpacing:"0.06em",cursor:"pointer"}}>← NEW SCREEN</button>
                  </div>
                  {screenerResults.map(s => (
                    <div key={s.sym} className="card" style={{padding:22,marginBottom:10,borderLeft:`3px solid ${{BUY:"var(--green)",WATCH:"var(--amber)",AVOID:"var(--red)",HOLD:"var(--border2)"}[s.verdict]||"var(--border2)"}`}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
                        <div>
                          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6,flexWrap:"wrap"}}>
                            <span style={{fontFamily:"var(--ff-head)",fontSize:18,fontWeight:800,color:"var(--text2)"}}>{s.sym}</span>
                            <span style={{fontSize:13,color:"var(--muted2)"}}>{s.name}</span>
                            <VerdictBadge v={s.verdict}/>
                            <ConvictionDots level={s.conviction}/>
                          </div>
                          {s.matchReason && (
                            <div style={{background:"var(--green)10",border:"1px solid var(--green)25",borderRadius:8,padding:"6px 12px",marginBottom:8,display:"inline-block"}}>
                              <span style={{fontSize:10,fontFamily:"var(--ff-mono)",color:"var(--green)",letterSpacing:"0.06em"}}>MATCH — </span>
                              <span style={{fontSize:11,color:"var(--muted2)"}}>{s.matchReason}</span>
                            </div>
                          )}
                          <p style={{fontSize:13,color:"var(--muted2)",lineHeight:1.7,maxWidth:600}}>{s.summary}</p>
                        </div>
                        <div style={{textAlign:"right",flexShrink:0}}>
                          <div style={{fontFamily:"var(--ff-mono)",fontSize:18,fontWeight:600,color:"var(--text2)",marginBottom:3}}>{s.priceStatic ? fmtMoney(toDisplay(s.priceStatic, s.priceCurrency || "USD", displayCcy, audUsd), displayCcy) : "—"}</div>
                          <div style={{fontSize:11,color:"var(--muted)",fontFamily:"var(--ff-mono)"}}>tgt {(() => { const tn = parseTargetNum(s.target); if (tn == null) return s.target; return fmtMoney(toDisplay(tn, s.priceCurrency || "USD", displayCcy, audUsd), displayCcy); })()}</div>
                        </div>
                      </div>
                      <div style={{marginTop:12,display:"flex",gap:8,flexWrap:"wrap"}}>
                        <button onClick={()=>{setTab("explorer");setSearchQ(s.sym);handleSearch(s.sym);}} style={{background:"none",border:"1px solid var(--border)",borderRadius:7,padding:"5px 14px",fontSize:10,color:"var(--muted2)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em"}}>FULL ANALYSIS →</button>
                        <button onClick={()=>addToWatchlist(s)} disabled={!!watchlist.find(w=>w.sym===s.sym)} style={{background:watchlist.find(w=>w.sym===s.sym)?"#00e67612":"none",border:`1px solid ${watchlist.find(w=>w.sym===s.sym)?"#00e67640":"var(--border)"}`,borderRadius:7,padding:"5px 14px",fontSize:10,color:watchlist.find(w=>w.sym===s.sym)?"var(--green)":"var(--muted2)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em"}}>
                          {watchlist.find(w=>w.sym===s.sym)?"✓ WATCHLIST":"+ WATCHLIST"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ══ PORTFOLIO ══ */}
          {tab==="portfolio"&&(
            <div>
              <div className="fu" style={{marginBottom:24}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
                  <div>
                    <h1 style={{fontFamily:"var(--ff-head)",fontSize:26,fontWeight:800,color:"var(--text2)",marginBottom:5}}>My Portfolio</h1>
                    <p style={{fontSize:13,color:"var(--muted2)"}}>Live prices across all sources.</p>
                  </div>
                  <CurrencyToggle value={displayCcy} onChange={setDisplayCcy}/>
                </div>
              </div>

              <div style={{display:"flex",gap:6,marginBottom:24,overflowX:"auto",WebkitOverflowScrolling:"touch",paddingBottom:4}}>
                {PORT_TABS.map(t=>{
                  const count=t.id==="all"?allHoldings.length:t.id==="coinbase"?cbHoldings.length:t.id==="binance"?bnHoldings.length:t.id==="tiger"?tigerHoldings.length:t.id==="ledger"?ldgHoldings.length:cmcHoldings.length;
                  const isActive=portTab===t.id;
                  return (
                    <button key={t.id} onClick={()=>setPortTab(t.id)} style={{background:isActive?`${t.color}18`:"none",border:`1px solid ${isActive?`${t.color}50`:"var(--border)"}`,borderRadius:10,padding:"8px 20px",fontSize:12,fontWeight:isActive?600:400,color:isActive?t.color:"var(--muted2)",flexShrink:0,whiteSpace:"nowrap"}}>
                      {t.label}{count>0&&<span style={{marginLeft:7,fontSize:10,fontFamily:"var(--ff-mono)",opacity:.7}}>{count}</span>}
                    </button>
                  );
                })}
              </div>

              {portTab==="all"&&(
                allHoldings.length>0 ? (
                  <>
                    <div className="fu"><SummaryStrip holdings={allHoldings} livePrices={livePrices} displayCcy={displayCcy} audUsd={audUsd}/></div>
                    <div className="fu"><PortfolioChart holdings={allHoldings} displayCcy={displayCcy} audUsd={audUsd} authFetch={authFetch}/></div>
                    <div className="fu"><BenchmarkStrip holdings={allHoldings} livePrices={livePrices} audUsd={audUsd} authFetch={authFetch}/></div>
                    <div className="fu"><AllocationPanel holdings={allHoldings} livePrices={livePrices} audUsd={audUsd} displayCcy={displayCcy}/></div>
                    <div className="fu"><RiskPanel holdings={allHoldings} audUsd={audUsd} authFetch={authFetch}/></div>
                    <div className="fu"><DividendPanel holdings={allHoldings} livePrices={livePrices} audUsd={audUsd} displayCcy={displayCcy} authFetch={authFetch}/></div>
                    <div className="fu2">
                      <div className="section-label">ALL HOLDINGS</div>
                      <div style={{display:"grid",gap:8}}>
                        {(() => {
                          const totalPortUSD = allHoldings.reduce((acc, h) => {
                            const lp = livePrices[h.sym];
                            const priceCcy = h.priceCurrency || lp?.currency || "USD";
                            const priceUSD = lp?.price ? toDisplay(lp.price, priceCcy, "USD", audUsd) : null;
                            const avgUSD   = toDisplay(h.avg, h.avgCurrency || "USD", "USD", audUsd);
                            return acc + (priceUSD != null ? h.qty * priceUSD : h.qty * avgUSD);
                          }, 0);
                          return allHoldings.map(h => {
                            const lp = livePrices[h.sym];
                            const priceCcy = h.priceCurrency || lp?.currency || "USD";
                            const priceUSD = lp?.price ? toDisplay(lp.price, priceCcy, "USD", audUsd) : null;
                            const avgUSD   = toDisplay(h.avg, h.avgCurrency || "USD", "USD", audUsd);
                            const valueUSD = priceUSD != null ? h.qty * priceUSD : h.qty * avgUSD;
                            const weight   = totalPortUSD > 0 ? (valueUSD / totalPortUSD) * 100 : null;
                            return <HoldingRow key={`${h.source}-${h.sym}`} holding={h} livePrice={livePrices[h.sym]} expanded={portExp===`${h.source}-${h.sym}`} onToggle={()=>setPortExp(p=>p===`${h.source}-${h.sym}`?null:`${h.source}-${h.sym}`)} onRemove={h.source==="cmc"?()=>setCmc(p=>p.filter(x=>x.sym!==h.sym)):null} onViewChart={()=>openDetail({sym:h.sym,name:h.name,priceType:h.priceType,priceCurrency:h.priceCurrency||"USD",sector:h.sector})} onTrade={(h.source==="coinbase"||h.source==="binance"||h.source==="tiger")?cfg=>setTradeModal({...cfg,exchange:h.source}):null} displayCcy={displayCcy} audUsd={audUsd} portWeight={weight}/>;
                          });
                        })()}
                      </div>
                    </div>
                  </>
                ) : (
                  <div style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:14,padding:"56px 32px",textAlign:"center"}}>
                    <p style={{color:"var(--muted2)",fontSize:15,fontFamily:"var(--ff-head)",fontWeight:600,marginBottom:10}}>No holdings yet</p>
                    <p style={{color:"var(--muted)",fontSize:13,marginBottom:28}}>Connect Coinbase, Binance, Ledger, or import your CMC CSV to get started.</p>
                    <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
                      <button onClick={syncCoinbase} style={{background:"none",border:"1px solid var(--amber)50",borderRadius:10,padding:"10px 20px",fontSize:12,color:"var(--amber)"}}>Sync Coinbase</button>
                      <button onClick={syncBinance} style={{background:"none",border:"1px solid #F0B90B50",borderRadius:10,padding:"10px 20px",fontSize:12,color:"#F0B90B"}}>Sync Binance</button>
                      <button onClick={()=>setPortTab("ledger")} style={{background:"none",border:"1px solid #41a5f550",borderRadius:10,padding:"10px 20px",fontSize:12,color:"#41a5f5"}}>Add Ledger</button>
                      <button onClick={()=>setPortTab("cmc")} style={{background:"none",border:"1px solid #448aff50",borderRadius:10,padding:"10px 20px",fontSize:12,color:"var(--blue)"}}>Import CMC CSV</button>
                    </div>
                  </div>
                )
              )}

              {portTab==="coinbase"&&(
                <SourcePanel label="Coinbase" color="var(--amber)" holdings={cbHoldings} livePrices={livePrices} syncing={cbSyncing} lastSync={cbLastSync} error={cbError} onSync={syncCoinbase} onRemove={sym=>setCb(p=>p.filter(h=>h.sym!==sym))} onViewChart={h=>openDetail({sym:h.sym,name:h.name,priceType:h.priceType,priceCurrency:h.priceCurrency||"USD",sector:h.sector})} onTrade={cfg=>setTradeModal(cfg)} displayCcy={displayCcy} audUsd={audUsd}/>
              )}

              {portTab==="binance"&&(
                <SourcePanel label="Binance" color="#F0B90B" holdings={bnHoldings} livePrices={livePrices} syncing={bnSyncing} lastSync={bnLastSync} error={bnError} onSync={syncBinance} onRemove={sym=>setBn(p=>p.filter(h=>h.sym!==sym))} onViewChart={h=>openDetail({sym:h.sym,name:h.name,priceType:h.priceType,priceCurrency:h.priceCurrency||"USD",sector:h.sector})} onTrade={cfg=>setTradeModal({...cfg,exchange:"binance"})} displayCcy={displayCcy} audUsd={audUsd}/>
              )}

              {portTab==="tiger"&&(
                <div>
                <SourcePanel label="Tiger" color="#FF6600" holdings={tigerHoldings} livePrices={livePrices} syncing={tigerSyncing} lastSync={tigerLastSync} error={tigerError} onSync={syncTiger} onRemove={sym=>setTiger(p=>p.filter(h=>h.sym!==sym))} onViewChart={h=>openDetail({sym:h.sym,name:h.name,priceType:h.priceType,priceCurrency:h.priceCurrency||h.avgCurrency||"AUD",sector:h.sector})} onTrade={cfg=>setTradeModal({...cfg,exchange:"tiger"})} displayCcy={displayCcy} audUsd={audUsd}/>

                {/* Tiger Orders */}
                <div style={{marginTop:28}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                    <span style={{fontSize:11,fontFamily:"var(--ff-mono)",fontWeight:700,color:"var(--muted2)",letterSpacing:"0.08em"}}>ORDERS ({tigerOrders.length})</span>
                    <button onClick={syncTigerOrders} disabled={tigerOrdersLoading} style={{background:"none",border:"1px solid var(--border)",borderRadius:8,padding:"5px 12px",fontSize:10,color:tigerOrdersLoading?"var(--muted)":"var(--muted2)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em",opacity:tigerOrdersLoading?.5:1}}>
                      {tigerOrdersLoading?"↻ LOADING…":"↻ REFRESH"}
                    </button>
                  </div>
                  {tigerOrders.length===0?(
                    <div className="card" style={{padding:"36px 24px",textAlign:"center"}}>
                      <p style={{color:"var(--muted2)",fontSize:13,fontFamily:"var(--ff-head)",fontWeight:600}}>No orders yet</p>
                      <p style={{color:"var(--muted)",fontSize:12,marginTop:6}}>Orders placed via Tiger will appear here.</p>
                    </div>
                  ):(
                    <div style={{display:"grid",gap:8}}>
                      {tigerOrders.map(o=>{
                        const isBuy = (o.action||"").toUpperCase()==="BUY";
                        const st = (o.status||"").toUpperCase();
                        const isFaded = st.includes("INVALID")||st.includes("CANCEL")||st.includes("REJECT");
                        const stColor = st.includes("FILL")?"var(--green)":st.includes("PEND")||st.includes("INIT")||st.includes("NEW")?"var(--amber)":st.includes("INVALID")||st.includes("REJECT")?"var(--red)":"var(--muted)";
                        return (
                          <div key={o.id} className="card" style={{padding:16,borderLeft:`3px solid ${isBuy?"var(--green)":"var(--red)"}`,opacity:isFaded?0.55:1}}>
                            <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:6,flexWrap:"wrap"}}>
                              <span className="badge" style={{background:isBuy?"#00e67618":"#ff525218",color:isBuy?"var(--green)":"var(--red)",border:`1px solid ${isBuy?"#00e67640":"#ff525240"}`}}>{o.action}</span>
                              <span style={{fontFamily:"var(--ff-head)",fontSize:15,fontWeight:700,color:"var(--text2)"}}>{o.symbol}</span>
                              <span className="badge" style={{background:stColor+"18",color:stColor,border:`1px solid ${stColor}40`}}>{o.status}</span>
                            </div>
                            <div style={{fontSize:11,fontFamily:"var(--ff-mono)",color:"var(--muted2)",display:"flex",gap:14,flexWrap:"wrap"}}>
                              <span>Qty: {o.qty}</span>
                              {o.limitPrice!=null&&<span>Limit: {o.limitPrice}</span>}
                              {o.filledQty>0&&<span>Filled: {o.filledQty}</span>}
                              {o.avgFillPrice!=null&&o.avgFillPrice>0&&<span>Avg: {o.avgFillPrice}</span>}
                              {o.currency&&<span>{o.currency}</span>}
                            </div>
                            {o.openTime&&<div style={{fontSize:10,fontFamily:"var(--ff-mono)",color:"var(--muted)",marginTop:6}}>{new Date(typeof o.openTime==="number"?o.openTime:o.openTime).toLocaleString()}</div>}
                            {o.remark&&<p style={{fontSize:11,color:"var(--amber)",marginTop:6,fontStyle:"italic",lineHeight:1.4}}>{o.remark}</p>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                </div>
              )}

              {portTab==="ledger"&&(
                <div>
                  {ldgHoldings.length>0&&(
                    <SourcePanel label="Ledger" color="#41a5f5" holdings={ldgHoldings} livePrices={livePrices} syncing={ldgSyncing} lastSync={ldgLastSync} error={ldgError} onSync={syncLedger} onRemove={sym=>setLdg(p=>p.filter(h=>h.sym!==sym))} onViewChart={h=>openDetail({sym:h.sym,name:h.name,priceType:h.priceType,priceCurrency:h.priceCurrency||"USD",sector:h.sector})} displayCcy={displayCcy} audUsd={audUsd}/>
                  )}
                  <LedgerAddressManager addresses={ledgerAddrs} onAdd={a=>{setLedgerAddrs(p=>[...p,a]);setTimeout(syncLedger,100);}} onRemove={i=>{setLedgerAddrs(p=>p.filter((_,j)=>j!==i));setTimeout(syncLedger,100);}}/>
                </div>
              )}

              {portTab==="cmc"&&(
                <div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18,flexWrap:"wrap",gap:10}}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <span style={{width:10,height:10,borderRadius:"50%",background:"var(--blue)",boxShadow:"0 0 8px #448aff80"}}/>
                      <span style={{fontSize:13,fontFamily:"var(--ff-head)",fontWeight:700,color:"var(--blue)"}}>CMC Invest</span>
                      {cmcHoldings.length>0&&<span style={{fontSize:11,fontFamily:"var(--ff-mono)",color:"var(--muted2)"}}>{cmcHoldings.length} positions</span>}
                    </div>
                    {cmcHoldings.length>0&&<div style={{display:"flex",gap:8}}>
                      <button onClick={()=>setCmcReimport(p=>!p)} style={{background:"none",border:"1px solid var(--border)",borderRadius:8,padding:"6px 14px",fontSize:10,color:cmcReimport?"var(--green)":"var(--muted)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em"}}>{cmcReimport?"CANCEL":"RE-IMPORT"}</button>
                      <button onClick={()=>{setCmc([]);setCmcReimport(false);}} style={{background:"none",border:"1px solid var(--border)",borderRadius:8,padding:"6px 14px",fontSize:10,color:"var(--muted)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em"}}>CLEAR ALL</button>
                    </div>}
                  </div>
                  {cmcHoldings.length>0&&cmcReimport&&<div style={{marginBottom:14}}><CMCImport onImport={h=>{setCmc(h);setCmcReimport(false);}}/></div>}
                  {cmcHoldings.length>0&&<SummaryStrip holdings={cmcHoldings} livePrices={livePrices} displayCcy={displayCcy} audUsd={audUsd}/>}
                  {cmcHoldings.length===0&&<div style={{marginBottom:0}}><CMCImport onImport={h=>setCmc(h)}/></div>}
                  {cmcHoldings.length>0&&(
                    <div style={{display:"grid",gap:8}}>
                      {cmcHoldings.map(h=>(
                        <HoldingRow key={h.sym} holding={h} livePrice={livePrices[h.sym]} expanded={portExp===`cmc-${h.sym}`} onToggle={()=>setPortExp(p=>p===`cmc-${h.sym}`?null:`cmc-${h.sym}`)} onRemove={()=>setCmc(p=>p.filter(x=>x.sym!==h.sym))} onViewChart={()=>openDetail({sym:h.sym,name:h.name,priceType:h.priceType,priceCurrency:h.priceCurrency||"USD",sector:h.sector})} displayCcy={displayCcy} audUsd={audUsd} dividendInfo={(() => { const yf = cmcDivDates[h.sym]; const yld = h.divYieldPct > 0 ? h.divYieldPct / 100 : (yf?.yield || null); if (!yld) return null; return { yield: yld, mostRecentDividend: h.divCents > 0 ? h.divCents / 100 : (yf?.annualDPS || null), annualDivPerShare: h.divCents > 0 ? h.divCents / 100 : (yf?.annualDPS || null), frankingPct: h.frankingPct || 0, exDivDate: yf?.exDivDate || null, paymentDate: yf?.payDate || null }; })()}/>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ══ MACRO CALENDAR ══ */}
          {tab==="macro"&&(
            <div>
              <div className="fu" style={{marginBottom:24}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
                  <div>
                    <h1 style={{fontFamily:"var(--ff-head)",fontSize:26,fontWeight:800,color:"var(--text2)",marginBottom:6}}>Macro Calendar</h1>
                    <p style={{fontSize:13,color:"var(--muted2)"}}>Upcoming central bank decisions and data releases — contextualised against your portfolio.</p>
                  </div>
                  <button onClick={()=>{setMacroEvents([]);setMacroError(null);setMacroLoading(true);const syms=[...new Set([...allHoldings.map(h=>h.sym),...watchlist.map(w=>w.sym)])];authFetch("/api/macro",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({holdingSyms:syms})}).then(r=>r.json()).then(d=>{if(d.error)setMacroError(d.error);else setMacroEvents(d);}).catch(()=>setMacroError("Failed")).finally(()=>setMacroLoading(false));}} disabled={macroLoading} style={{background:"none",border:"1px solid var(--border)",borderRadius:8,padding:"7px 16px",fontSize:10,color:macroLoading?"var(--muted)":"var(--muted2)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em",opacity:macroLoading?.5:1}}>
                    {macroLoading?"↻ LOADING…":"↻ REFRESH"}
                  </button>
                </div>
              </div>

              {macroLoading && <div style={{display:"grid",gap:10}}>{[1,2,3,4].map(i=><div key={i} className="shimmer-el" style={{height:120}}/>)}</div>}
              {macroError && (
                <div style={{background:"#ff525212",border:"1px solid #ff525230",borderRadius:12,padding:"20px 24px",display:"flex",justifyContent:"space-between",alignItems:"center",gap:12}}>
                  <p style={{fontSize:13,color:"var(--red)"}}>{macroError}</p>
                </div>
              )}
              {!macroLoading && macroEvents.length > 0 && (() => {
                const now = new Date();
                const catColor = {FED:"var(--blue)",RBA:"var(--amber)",ECB:"var(--purple)",INFLATION:"var(--red)",EMPLOYMENT:"var(--green)",GROWTH:"var(--text2)",TRADE:"var(--muted2)",OTHER:"var(--muted2)"};
                const impColor = {HIGH:"var(--red)",MEDIUM:"var(--amber)",LOW:"var(--muted)"};
                return (
                  <div className="fu2">
                    {macroEvents.map((ev, i) => {
                      const evDate = new Date(ev.date);
                      const isPast = evDate < now;
                      const diffDays = Math.round((evDate - now) / 86400000);
                      const countdown = isPast ? `${Math.abs(diffDays)}d ago` : diffDays === 0 ? "TODAY" : diffDays === 1 ? "Tomorrow" : `in ${diffDays}d`;
                      const cc = catColor[ev.category] || "var(--muted2)";
                      return (
                        <div key={i} className="card" style={{padding:20,marginBottom:10,borderLeft:`3px solid ${impColor[ev.importance]||"var(--border)"}`,opacity:isPast?0.6:1}}>
                          <div style={{display:"flex",justifyContent:"space-between",gap:12,alignItems:"flex-start",flexWrap:"wrap"}}>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:8,flexWrap:"wrap"}}>
                                <span className="badge" style={{background:`${cc}18`,color:cc,border:`1px solid ${cc}35`}}>{ev.category}</span>
                                <span className="badge" style={{background:`${impColor[ev.importance]}18`,color:impColor[ev.importance],border:`1px solid ${impColor[ev.importance]}35`}}>{ev.importance}</span>
                                <span className="badge" style={{background:"var(--surface)",color:"var(--muted2)",border:"1px solid var(--border)"}}>{ev.country}</span>
                                {isPast && <span className="badge" style={{background:"var(--surface)",color:"var(--muted)",border:"1px solid var(--border)"}}>PAST</span>}
                              </div>
                              <div style={{fontFamily:"var(--ff-head)",fontSize:15,fontWeight:700,color:"var(--text2)",marginBottom:6}}>{ev.event}</div>
                              <p style={{fontSize:12,color:"var(--muted2)",lineHeight:1.6,marginBottom:ev.portfolioImpact?10:0}}>{ev.preview}</p>
                              {ev.portfolioImpact && (
                                <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:8,padding:"10px 14px",marginBottom:8}}>
                                  <div style={{fontSize:9,fontFamily:"var(--ff-mono)",color:"var(--muted)",letterSpacing:"0.1em",marginBottom:4}}>PORTFOLIO IMPACT</div>
                                  <p style={{fontSize:12,color:"var(--muted2)",lineHeight:1.5,margin:0}}>{ev.portfolioImpact}</p>
                                </div>
                              )}
                              {!isPast && (ev.bullCase || ev.bearCase) && (
                                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                                  {ev.bullCase && <div style={{background:"#00e67610",border:"1px solid #00e67625",borderRadius:7,padding:"8px 12px"}}>
                                    <div style={{fontSize:9,fontFamily:"var(--ff-mono)",color:"var(--green)",marginBottom:4,letterSpacing:"0.08em"}}>BULL CASE</div>
                                    <p style={{fontSize:11,color:"var(--muted2)",lineHeight:1.5,margin:0}}>{ev.bullCase}</p>
                                  </div>}
                                  {ev.bearCase && <div style={{background:"#ff525210",border:"1px solid #ff525225",borderRadius:7,padding:"8px 12px"}}>
                                    <div style={{fontSize:9,fontFamily:"var(--ff-mono)",color:"var(--red)",marginBottom:4,letterSpacing:"0.08em"}}>BEAR CASE</div>
                                    <p style={{fontSize:11,color:"var(--muted2)",lineHeight:1.5,margin:0}}>{ev.bearCase}</p>
                                  </div>}
                                </div>
                              )}
                              {ev.affectedHoldings?.length > 0 && (
                                <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:10}}>
                                  {ev.affectedHoldings.map(s=><span key={s} className="badge" style={{background:"var(--surface)",color:"var(--muted2)",border:"1px solid var(--border)"}}>{s}</span>)}
                                </div>
                              )}
                            </div>
                            <div style={{textAlign:"right",flexShrink:0}}>
                              <div style={{fontSize:11,fontFamily:"var(--ff-mono)",color:!isPast&&diffDays<=7?"var(--amber)":"var(--muted2)",fontWeight:600,marginBottom:4}}>{countdown}</div>
                              <div style={{fontSize:10,color:"var(--muted)",fontFamily:"var(--ff-mono)"}}>{fmtDate(ev.date)}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}

          {/* ══ NEWS ══ */}
          {tab==="news"&&(
            <div>
              <div className="fu" style={{marginBottom:24}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
                  <div>
                    <h1 style={{fontFamily:"var(--ff-head)",fontSize:26,fontWeight:800,color:"var(--text2)",marginBottom:6}}>News & Signals</h1>
                    <p style={{fontSize:13,color:"var(--muted2)"}}>
                      {newsLoading ? "Fetching live news…" : newsItems[0]?.live ? "Live from Yahoo Finance · refreshes every 15 min" : "Filtered to your universe."}
                    </p>
                  </div>
                  <button onClick={fetchNews} disabled={newsLoading} style={{background:"none",border:"1px solid var(--border)",borderRadius:8,padding:"7px 16px",fontSize:10,color:newsLoading?"var(--muted)":"var(--muted2)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em",opacity:newsLoading?.5:1}}>
                    {newsLoading ? "↻ LOADING…" : "↻ REFRESH"}
                  </button>
                </div>
              </div>
              <div style={{display:"flex",gap:6,marginBottom:20,flexWrap:"wrap"}}>
                {["ALL","US TECH","ASX MINING","CRYPTO"].map(f=>(
                  <button key={f} className={`filter-btn${newsFilter===f?" active":""}`} onClick={()=>setNewsFilter(f)}>{f}</button>
                ))}
              </div>
              {newsLoading && newsItems === NEWS_MOCK && (
                <div style={{display:"grid",gap:10,marginBottom:20}}>{[90,75,85].map((w,i)=><div key={i} className="shimmer-el" style={{height:80,width:`${w}%`}}/>)}</div>
              )}
              <div className="fu2">
                {filteredNews.length > 0
                  ? filteredNews.map((n,i)=><NewsCard key={i} item={n}/>)
                  : <div style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:12,padding:"32px",textAlign:"center"}}><p style={{color:"var(--muted2)",fontSize:13}}>No {newsFilter} news right now.</p></div>
                }
              </div>
            </div>
          )}

          {/* ══ WATCHLIST ══ */}
          {tab==="watchlist"&&(
            <div>
              <div className="fu" style={{marginBottom:28}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
                  <div>
                    <h1 style={{fontFamily:"var(--ff-head)",fontSize:26,fontWeight:800,color:"var(--text2)",marginBottom:6}}>Watchlist</h1>
                    <p style={{fontSize:13,color:"var(--muted2)"}}>Track stocks with price targets. Add from Explorer after any analysis.</p>
                  </div>
                  <CurrencyToggle value={displayCcy} onChange={setDisplayCcy}/>
                </div>
              </div>

              {watchlist.length === 0 ? (
                <div style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:14,padding:"56px 32px",textAlign:"center"}}>
                  <div style={{fontSize:32,marginBottom:16,opacity:.3}}>◇</div>
                  <p style={{color:"var(--muted2)",fontSize:15,fontFamily:"var(--ff-head)",fontWeight:600,marginBottom:10}}>Watchlist is empty</p>
                  <p style={{color:"var(--muted)",fontSize:13,marginBottom:24}}>Analyse any stock in Explorer and tap "+ Watchlist" to add it here.</p>
                  <button onClick={()=>setTab("explorer")} style={{background:"none",border:"1px solid var(--green)50",borderRadius:10,padding:"10px 24px",fontSize:12,color:"var(--green)"}}>Go to Explorer →</button>
                </div>
              ) : (
                <div style={{display:"grid",gap:8}}>
                  {watchlist.map(w => {
                    const lp = livePrices[w.sym];
                    const priceCcy = lp?.currency || w.priceCurrency || "USD";
                    const rawPrice = lp?.price;
                    const dispPrice = rawPrice ? toDisplay(rawPrice, priceCcy, displayCcy, audUsd) : null;
                    const targetDisp = w.target ? toDisplay(w.target, w.targetCcy || "USD", displayCcy, audUsd) : null;
                    const toTargetPct = (dispPrice && targetDisp) ? ((targetDisp - dispPrice) / dispPrice) * 100 : null;
                    const crossed = toTargetPct != null && toTargetPct <= 0;
                    const close   = toTargetPct != null && !crossed && Math.abs(toTargetPct) < 5;
                    const accent  = crossed ? "var(--green)" : close ? "var(--amber)" : "var(--border2)";

                    return (
                      <div key={w.sym} className="card" style={{padding:"16px 20px",borderLeft:`3px solid ${accent}`}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                          <div style={{display:"flex",gap:12,alignItems:"center",minWidth:0}}>
                            <div style={{width:40,height:40,borderRadius:10,background:`${accent}18`,border:`1px solid ${accent}30`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                              <span style={{fontFamily:"var(--ff-head)",fontSize:11,fontWeight:800,color:accent}}>{w.sym.replace(".AX","").slice(0,3)}</span>
                            </div>
                            <div>
                              <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:4,flexWrap:"wrap"}}>
                                <span style={{fontFamily:"var(--ff-head)",fontSize:15,fontWeight:700,color:"var(--text2)"}}>{w.sym}</span>
                                <SectorBadge sector={w.sector}/>
                                {crossed && <span className="badge" style={{background:"#00e67620",color:"var(--green)",border:"1px solid #00e67640"}}>TARGET HIT</span>}
                                {close && !crossed && <span className="badge" style={{background:"var(--amber)20",color:"var(--amber)",border:"1px solid var(--amber)40"}}>NEAR TARGET</span>}
                              </div>
                              <div style={{fontSize:12,color:"var(--muted2)"}}>{w.name}</div>
                            </div>
                          </div>

                          <div style={{display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
                            <div style={{textAlign:"right"}}>
                              <div style={{fontSize:9,fontFamily:"var(--ff-mono)",color:"var(--muted)",letterSpacing:"0.1em",marginBottom:4}}>PRICE</div>
                              {dispPrice != null
                                ? <div style={{fontFamily:"var(--ff-mono)",fontSize:16,color:"var(--text2)",fontWeight:500}}>{fmtMoney(dispPrice, displayCcy)}</div>
                                : <div className="shimmer-el" style={{width:64,height:16}}/>}
                              {lp && <div style={{fontSize:10,fontFamily:"var(--ff-mono)",color:lp.up?"var(--green)":"var(--red)",marginTop:2}}>{lp.changeStr} today</div>}
                            </div>

                            {targetDisp != null && (
                              <div style={{textAlign:"right"}}>
                                <div style={{fontSize:9,fontFamily:"var(--ff-mono)",color:"var(--muted)",letterSpacing:"0.1em",marginBottom:4}}>TARGET</div>
                                <div style={{fontFamily:"var(--ff-mono)",fontSize:16,color:accent,fontWeight:500}}>{fmtMoney(targetDisp, displayCcy)}</div>
                                {toTargetPct != null && (
                                  <div style={{fontSize:10,fontFamily:"var(--ff-mono)",color:accent,marginTop:2}}>
                                    {crossed ? "✓ reached" : `${toTargetPct > 0 ? "+" : ""}${toTargetPct.toFixed(1)}% to go`}
                                  </div>
                                )}
                              </div>
                            )}

                            <button onClick={()=>openDetail({sym:w.sym,name:w.name,priceType:w.priceType,priceCurrency:w.priceCurrency,sector:w.sector})} style={{background:"none",border:"1px solid var(--border)",borderRadius:8,padding:"7px 10px",color:"var(--muted2)",fontSize:10,fontFamily:"var(--ff-mono)",letterSpacing:"0.06em"}}>
                              CHART
                            </button>
                            <button onClick={()=>{setAlertEditSym(p=>p===w.sym?null:w.sym);const ex=alerts[w.sym];setAlertFormVal(ex?{type:ex.type,value:ex.value,note:ex.note||""}:{type:"price_above",value:"",note:""}); }} style={{background:alerts[w.sym]?`${triggeredAlerts.includes(w.sym)?"var(--amber)":"var(--purple)"}18`:"none",border:`1px solid ${alerts[w.sym]?(triggeredAlerts.includes(w.sym)?"var(--amber)40":"var(--purple)40"):"var(--border)"}`,borderRadius:8,padding:"7px 10px",color:alerts[w.sym]?(triggeredAlerts.includes(w.sym)?"var(--amber)":"var(--purple)"):"var(--muted2)",fontSize:10,fontFamily:"var(--ff-mono)",letterSpacing:"0.06em"}}>
                              {triggeredAlerts.includes(w.sym)?"🔔 ALERT":alerts[w.sym]?"◎ ALERT":"+ ALERT"}
                            </button>
                            <button onClick={()=>setWatchlist(prev=>prev.filter(x=>x.sym!==w.sym))} style={{background:"#ff525218",border:"1px solid #ff525240",borderRadius:8,padding:"7px 10px",color:"var(--red)",fontSize:10,fontFamily:"var(--ff-mono)",letterSpacing:"0.06em"}}>
                              REMOVE
                            </button>
                          </div>
                        </div>

                        {/* Alert form */}
                        {alertEditSym===w.sym && (
                          <div style={{marginTop:12,paddingTop:12,borderTop:"1px solid var(--border)"}}>
                            <div style={{display:"flex",gap:8,alignItems:"flex-end",flexWrap:"wrap"}}>
                              <div>
                                <div style={{fontSize:9,fontFamily:"var(--ff-mono)",color:"var(--muted)",letterSpacing:"0.1em",marginBottom:4}}>CONDITION</div>
                                <select value={alertFormVal.type} onChange={e=>setAlertFormVal(p=>({...p,type:e.target.value}))} style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:6,padding:"7px 10px",color:"var(--text2)",fontSize:12,fontFamily:"var(--ff-mono)"}}>
                                  <option value="price_above">Price above</option>
                                  <option value="price_below">Price below</option>
                                  <option value="change_up">Daily change ≥ +%</option>
                                  <option value="change_down">Daily change ≤ -%</option>
                                </select>
                              </div>
                              <div>
                                <div style={{fontSize:9,fontFamily:"var(--ff-mono)",color:"var(--muted)",letterSpacing:"0.1em",marginBottom:4}}>VALUE</div>
                                <input type="number" value={alertFormVal.value} onChange={e=>setAlertFormVal(p=>({...p,value:e.target.value}))} placeholder={alertFormVal.type.startsWith("change")?"5":"150.00"} style={{width:100,background:"var(--surface)",border:"1px solid var(--border)",borderRadius:6,padding:"7px 10px",color:"var(--text2)",fontSize:12,fontFamily:"var(--ff-mono)"}}/>
                              </div>
                              <div style={{flex:1,minWidth:120}}>
                                <div style={{fontSize:9,fontFamily:"var(--ff-mono)",color:"var(--muted)",letterSpacing:"0.1em",marginBottom:4}}>NOTE (optional)</div>
                                <input value={alertFormVal.note} onChange={e=>setAlertFormVal(p=>({...p,note:e.target.value}))} placeholder="Why this level?" style={{width:"100%",background:"var(--surface)",border:"1px solid var(--border)",borderRadius:6,padding:"7px 10px",color:"var(--text2)",fontSize:12,fontFamily:"var(--ff-mono)"}}/>
                              </div>
                              <button onClick={()=>{if(!alertFormVal.value)return;setAlerts(p=>({...p,[w.sym]:{type:alertFormVal.type,value:parseFloat(alertFormVal.value),note:alertFormVal.note,triggered:false,triggeredAt:null}}));setAlertEditSym(null);}} style={{background:"var(--purple)18",border:"1px solid var(--purple)40",borderRadius:6,padding:"7px 16px",fontSize:10,color:"var(--purple)",fontFamily:"var(--ff-mono)",fontWeight:600,letterSpacing:"0.06em",flexShrink:0}}>SET ALERT</button>
                              {alerts[w.sym] && <button onClick={()=>{setAlerts(p=>{const n={...p};delete n[w.sym];return n;});setAlertEditSym(null);setTriggeredAlerts(p=>p.filter(s=>s!==w.sym));}} style={{background:"#ff525212",border:"1px solid #ff525230",borderRadius:6,padding:"7px 12px",fontSize:10,color:"var(--red)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em",flexShrink:0}}>REMOVE</button>}
                            </div>
                            {alerts[w.sym] && (
                              <div style={{marginTop:8,fontSize:10,fontFamily:"var(--ff-mono)",color:triggeredAlerts.includes(w.sym)?"var(--amber)":"var(--purple)"}}>
                                {triggeredAlerts.includes(w.sym)?"🔔 TRIGGERED":"◎ ACTIVE"} — {alerts[w.sym].type.replace(/_/g," ")} {alerts[w.sym].value}{alerts[w.sym].type.startsWith("change")?"%":""}
                                {alerts[w.sym].note && <span style={{color:"var(--muted)",marginLeft:8}}>{alerts[w.sym].note}</span>}
                              </div>
                            )}
                          </div>
                        )}

                        {w.note && (
                          <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid var(--border)",fontSize:12,color:"var(--muted)",fontStyle:"italic",lineHeight:1.5}}>
                            {w.note}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}


          {/* ══ PORTFOLIO COACH ══ */}
          {tab==="coach"&&(
            <div>
              <div className="fu" style={{marginBottom:24}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
                  <div>
                    <h1 style={{fontFamily:"var(--ff-head)",fontSize:26,fontWeight:800,color:"var(--text2)",marginBottom:6}}>Portfolio Coach</h1>
                    <p style={{fontSize:13,color:"var(--muted2)"}}>AI-powered portfolio health check — concentration, risk, and rebalancing advice.</p>
                  </div>
                  {coachReport && (
                    <button onClick={()=>{setCoachReport(null);runCoachAnalysis();}} disabled={coachLoading} style={{background:"none",border:"1px solid var(--border)",borderRadius:8,padding:"7px 16px",fontSize:10,color:coachLoading?"var(--muted)":"var(--muted2)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em",opacity:coachLoading?.5:1}}>
                      {coachLoading?"↻ ANALYSING…":"↻ REFRESH"}
                    </button>
                  )}
                </div>
              </div>

              {allHoldings.length === 0 ? (
                <div style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:14,padding:"52px 32px",textAlign:"center"}}>
                  <div style={{fontSize:40,marginBottom:16,opacity:.25}}>◭</div>
                  <p style={{color:"var(--muted2)",fontSize:15,fontFamily:"var(--ff-head)",fontWeight:700,marginBottom:8}}>No portfolio connected.</p>
                  <p style={{color:"var(--muted)",fontSize:13,marginBottom:24}}>Connect Coinbase, Binance, Ledger, or import a CMC CSV to get AI coaching.</p>
                  <button onClick={()=>setTab("portfolio")} style={{background:"var(--green)18",border:"1px solid var(--green)40",borderRadius:8,padding:"8px 20px",fontSize:11,color:"var(--green)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em"}}>GO TO PORTFOLIO →</button>
                </div>
              ) : coachLoading && !coachReport ? (
                <div style={{display:"grid",gap:12}}>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:4}}>
                    {[1,2,3,4].map(i=><div key={i} className="shimmer-el" style={{height:88}}/>)}
                  </div>
                  {[1,2,3].map(i=><div key={i} className="shimmer-el" style={{height:72}}/>)}
                </div>
              ) : coachError ? (
                <div style={{background:"#ff525212",border:"1px solid #ff525230",borderRadius:12,padding:"20px 24px",display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                  <p style={{fontSize:13,color:"var(--red)"}}>{coachError}</p>
                  <button onClick={runCoachAnalysis} style={{background:"none",border:"1px solid var(--red)50",borderRadius:8,padding:"6px 14px",fontSize:10,color:"var(--red)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em"}}>TRY AGAIN</button>
                </div>
              ) : coachReport ? (() => {
                const gradeColor = {A:"var(--green)",B:"var(--blue)",C:"var(--amber)",D:"#ff6e40",F:"var(--red)"}[coachReport.grade] || "var(--muted2)";
                const riskColor  = {AGGRESSIVE:"var(--red)",BALANCED:"var(--amber)",CONSERVATIVE:"var(--green)"}[coachReport.riskProfile] || "var(--muted2)";
                const snap = buildPortfolioSnapshot(allHoldings, livePrices, audUsd);
                const topHolding = snap.holdings[0];
                const priorityColor = {HIGH:"var(--red)",MEDIUM:"var(--amber)",LOW:"var(--green)"};
                return (
                  <div className="fi">
                    {/* Stats strip */}
                    <div className="coach-stats-grid" style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}}>
                      <div className="stat-card" style={{borderTop:`3px solid ${gradeColor}`}}>
                        <div style={{fontSize:9,fontFamily:"var(--ff-mono)",color:"var(--muted)",letterSpacing:"0.12em",marginBottom:8}}>PORTFOLIO GRADE</div>
                        <div style={{fontSize:40,fontFamily:"var(--ff-head)",fontWeight:900,color:gradeColor,lineHeight:1}}>{coachReport.grade}</div>
                        <div style={{fontSize:10,color:"var(--muted2)",marginTop:6,lineHeight:1.4}}>{coachReport.gradeNote}</div>
                      </div>
                      <div className="stat-card" style={{borderTop:`3px solid ${riskColor}`}}>
                        <div style={{fontSize:9,fontFamily:"var(--ff-mono)",color:"var(--muted)",letterSpacing:"0.12em",marginBottom:8}}>RISK PROFILE</div>
                        <div style={{fontSize:18,fontFamily:"var(--ff-head)",fontWeight:800,color:riskColor,lineHeight:1,marginBottom:6}}>{coachReport.riskProfile}</div>
                        <div style={{fontSize:10,color:"var(--muted2)",lineHeight:1.4}}>{coachReport.riskNote}</div>
                      </div>
                      <div className="stat-card">
                        <div style={{fontSize:9,fontFamily:"var(--ff-mono)",color:"var(--muted)",letterSpacing:"0.12em",marginBottom:8}}>TOP HOLDING</div>
                        {topHolding ? (
                          <>
                            <div style={{fontSize:18,fontFamily:"var(--ff-head)",fontWeight:800,color:"var(--text2)",lineHeight:1}}>{topHolding.sym}</div>
                            <div style={{fontSize:14,fontFamily:"var(--ff-mono)",color:"var(--amber)",marginTop:4}}>{topHolding.pct.toFixed(1)}% of portfolio</div>
                          </>
                        ) : <div style={{fontSize:14,color:"var(--muted)"}}>—</div>}
                      </div>
                      <div className="stat-card">
                        <div style={{fontSize:9,fontFamily:"var(--ff-mono)",color:"var(--muted)",letterSpacing:"0.12em",marginBottom:8}}>CRYPTO / STOCKS</div>
                        <div style={{fontSize:18,fontFamily:"var(--ff-head)",fontWeight:800,color:"var(--text2)",lineHeight:1}}>{snap.cryptoPct.toFixed(0)}% / {snap.stocksPct.toFixed(0)}%</div>
                        <div style={{marginTop:8,height:6,borderRadius:3,background:"var(--border)",overflow:"hidden"}}>
                          <div style={{height:"100%",width:`${snap.cryptoPct}%`,background:"var(--purple)",borderRadius:3}}/>
                        </div>
                      </div>
                    </div>

                    {/* Summary */}
                    <div className="fu2 card" style={{padding:20,marginBottom:16}}>
                      <div className="section-label">OVERALL ASSESSMENT</div>
                      <p style={{fontSize:13,color:"var(--muted2)",lineHeight:1.7,marginBottom:coachReport.outlook?12:0}}>{coachReport.summary}</p>
                      {coachReport.outlook && <p style={{fontSize:12,color:"var(--muted)",lineHeight:1.6,fontStyle:"italic",borderTop:"1px solid var(--border)",paddingTop:10,marginTop:2}}>{coachReport.outlook}</p>}
                    </div>

                    {/* Strengths + Weaknesses */}
                    <div className="fu3" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
                      <div className="card" style={{padding:18}}>
                        <div className="section-label">STRENGTHS</div>
                        {(coachReport.strengths||[]).map((s,i)=>(
                          <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start",marginBottom:8}}>
                            <span style={{color:"var(--green)",fontWeight:700,flexShrink:0}}>✓</span>
                            <span style={{fontSize:12,color:"var(--muted2)",lineHeight:1.5}}>{s}</span>
                          </div>
                        ))}
                      </div>
                      <div className="card" style={{padding:18}}>
                        <div className="section-label">WEAKNESSES</div>
                        {(coachReport.weaknesses||[]).map((w,i)=>(
                          <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start",marginBottom:8}}>
                            <span style={{color:"var(--red)",fontWeight:700,flexShrink:0}}>✗</span>
                            <span style={{fontSize:12,color:"var(--muted2)",lineHeight:1.5}}>{w}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="card" style={{padding:20,marginBottom:16}}>
                      <div className="section-label">RECOMMENDED ACTIONS</div>
                      {(coachReport.actions||[]).map((a,i)=>(
                        <div key={i} style={{display:"flex",gap:12,alignItems:"flex-start",padding:"12px 0",borderBottom:i<(coachReport.actions.length-1)?"1px solid var(--border)":"none"}}>
                          <span className="badge" style={{background:`${priorityColor[a.priority]||"var(--muted)"}18`,color:priorityColor[a.priority]||"var(--muted2)",border:`1px solid ${priorityColor[a.priority]||"var(--border)"}40`,flexShrink:0}}>{a.priority}</span>
                          <div>
                            <div style={{fontSize:13,color:"var(--text2)",fontWeight:600,marginBottom:3}}>{a.action}</div>
                            <div style={{fontSize:11,color:"var(--muted2)"}}>{a.reason}</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Sector breakdown */}
                    <div className="card" style={{padding:20,marginBottom:16}}>
                      <div className="section-label">SECTOR BREAKDOWN</div>
                      {Object.entries(snap.sectorBreakdown).sort((a,b)=>b[1]-a[1]).map(([sector,pct])=>(
                        <div key={sector} style={{marginBottom:10}}>
                          <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                            <span style={{fontSize:12,color:"var(--muted2)"}}>{sector}</span>
                            <span style={{fontSize:12,fontFamily:"var(--ff-mono)",color:"var(--text2)"}}>{pct.toFixed(1)}%</span>
                          </div>
                          <div style={{height:6,borderRadius:3,background:"var(--border)",overflow:"hidden"}}>
                            <div style={{height:"100%",width:`${pct}%`,background:"var(--green)",borderRadius:3,opacity:0.7}}/>
                          </div>
                        </div>
                      ))}
                      {coachReport.sectorComment && <p style={{fontSize:11,color:"var(--muted)",marginTop:12,lineHeight:1.5,fontStyle:"italic"}}>{coachReport.sectorComment}</p>}
                    </div>

                    {/* Concentration + Diversification */}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
                      {coachReport.concentration && (
                        <div className="card" style={{padding:18}}>
                          <div className="section-label">CONCENTRATION RISK</div>
                          <p style={{fontSize:12,color:"var(--muted2)",lineHeight:1.6}}>{coachReport.concentration}</p>
                        </div>
                      )}
                      {coachReport.diversification && (
                        <div className="card" style={{padding:18}}>
                          <div className="section-label">DIVERSIFICATION</div>
                          <p style={{fontSize:12,color:"var(--muted2)",lineHeight:1.6}}>{coachReport.diversification}</p>
                        </div>
                      )}
                    </div>
                    {coachReport.cryptoComment && (
                      <div className="card" style={{padding:18,marginBottom:16}}>
                        <div className="section-label">CRYPTO ALLOCATION</div>
                        <p style={{fontSize:12,color:"var(--muted2)",lineHeight:1.6}}>{coachReport.cryptoComment}</p>
                      </div>
                    )}
                  </div>
                );
              })() : null}
            </div>
          )}

          {/* ══ EARNINGS CALENDAR ══ */}
          {tab==="earnings"&&(
            <div>
              <div className="fu" style={{marginBottom:24}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
                  <div>
                    <h1 style={{fontFamily:"var(--ff-head)",fontSize:26,fontWeight:800,color:"var(--text2)",marginBottom:6}}>Earnings Calendar</h1>
                    <p style={{fontSize:13,color:"var(--muted2)"}}>Upcoming & recent earnings for your holdings and watchlist. Get an AI brief before each one.</p>
                  </div>
                  <button onClick={()=>{setEarningsData({});setEarningsBriefs({});}} disabled={earningsLoading} style={{background:"none",border:"1px solid var(--border)",borderRadius:8,padding:"7px 16px",fontSize:10,color:earningsLoading?"var(--muted)":"var(--muted2)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em",opacity:earningsLoading?.5:1}}>
                    {earningsLoading?"↻ LOADING…":"↻ REFRESH"}
                  </button>
                </div>
              </div>

              {Object.keys(earningsData).length === 0 && !earningsLoading && (
                <div style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:12,padding:"32px",textAlign:"center",marginBottom:16}}>
                  <p style={{color:"var(--muted2)",fontSize:13,marginBottom:8}}>No earnings data found.</p>
                  <p style={{color:"var(--muted)",fontSize:12}}>Ensure <code style={{fontFamily:"var(--ff-mono)",color:"var(--amber)"}}>FMP_API_KEY</code> is set in your .env and you have holdings or watchlist items.</p>
                </div>
              )}

              {earningsLoading && (
                <div style={{display:"grid",gap:10}}>{[1,2,3,4].map(i=><div key={i} className="shimmer-el" style={{height:80}}/>)}</div>
              )}

              {!earningsLoading && (() => {
                const now = new Date();
                const allEntries = [];
                Object.entries(earningsData).forEach(([sym, events]) => {
                  events.forEach(ev => {
                    const d = new Date(ev.date);
                    allEntries.push({ sym, ...ev, dateObj: d, isFuture: d >= now });
                  });
                });
                const upcoming = allEntries.filter(e => e.isFuture).sort((a,b) => a.dateObj - b.dateObj);
                const recent   = allEntries.filter(e => !e.isFuture && (now - e.dateObj) < 60 * 86400000).sort((a,b) => b.dateObj - a.dateObj);

                if (allEntries.length === 0 && Object.keys(earningsData).length > 0) {
                  return <div style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:12,padding:"32px",textAlign:"center"}}><p style={{color:"var(--muted2)",fontSize:13}}>No earnings data found for your holdings/watchlist.</p></div>;
                }
                if (allEntries.length === 0) return null;

                function EarningsRow({ e }) {
                  const brief = earningsBriefs[e.sym];
                  const loading = earningsBriefing[e.sym];
                  const surprises = earningsData[e.sym]?.filter(x => x.epsActual != null && x.epsEstimated != null) || [];
                  const verdictColor = {BEAT_LIKELY:"var(--green)",MISS_LIKELY:"var(--red)",IN_LINE:"var(--amber)",UNCERTAIN:"var(--muted2)"};
                  const diffDays = Math.round((e.dateObj - now) / 86400000);
                  const countdown = e.isFuture
                    ? diffDays === 0 ? "TODAY" : diffDays === 1 ? "Tomorrow" : `in ${diffDays}d`
                    : Math.abs(diffDays) <= 1 ? "Yesterday" : `${Math.abs(diffDays)}d ago`;
                  const countdownColor = e.isFuture && diffDays <= 7 ? "var(--amber)" : e.isFuture ? "var(--text2)" : "var(--muted)";
                  const surprise = e.epsActual != null && e.epsEstimated != null
                    ? ((e.epsActual - e.epsEstimated) / Math.abs(e.epsEstimated || 1) * 100) : null;

                  return (
                    <div className="card" style={{padding:18,marginBottom:10}}>
                      <div style={{display:"flex",justifyContent:"space-between",gap:12,alignItems:"flex-start",flexWrap:"wrap"}}>
                        <div style={{display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
                          <div style={{width:40,height:40,borderRadius:10,background:"var(--surface)",border:"1px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                            <span style={{fontFamily:"var(--ff-head)",fontSize:10,fontWeight:800,color:"var(--muted2)"}}>{e.sym.replace(".AX","").slice(0,3)}</span>
                          </div>
                          <div>
                            <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:3,flexWrap:"wrap"}}>
                              <span style={{fontFamily:"var(--ff-head)",fontSize:14,fontWeight:700,color:"var(--text2)"}}>{e.sym}</span>
                              <span style={{fontSize:11,fontFamily:"var(--ff-mono)",color:countdownColor,fontWeight:600}}>{countdown}</span>
                              <span style={{fontSize:10,color:"var(--muted)",fontFamily:"var(--ff-mono)"}}>{fmtDate(e.date)}</span>
                              {e.time && <span className="badge" style={{background:"var(--surface)",color:"var(--muted2)",border:"1px solid var(--border)"}}>{e.time}</span>}
                            </div>
                            {!e.isFuture && surprise != null && (
                              <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                                <span style={{fontSize:11,fontFamily:"var(--ff-mono)",color:"var(--muted2)"}}>EPS actual: <span style={{color:"var(--text2)",fontWeight:600}}>{e.epsActual?.toFixed(2)}</span></span>
                                <span style={{fontSize:11,fontFamily:"var(--ff-mono)",color:"var(--muted2)"}}>est: {e.epsEstimated?.toFixed(2)}</span>
                                <span className="badge" style={{background:surprise>=0?"#00e67618":"#ff525218",color:surprise>=0?"var(--green)":"var(--red)",border:`1px solid ${surprise>=0?"#00e67640":"#ff525240"}`}}>
                                  {surprise>=0?"+":""}{surprise.toFixed(1)}% surprise
                                </span>
                              </div>
                            )}
                            {e.isFuture && e.epsEstimated != null && (
                              <span style={{fontSize:11,fontFamily:"var(--ff-mono)",color:"var(--muted2)"}}>Est EPS: <span style={{color:"var(--text2)"}}>{e.epsEstimated?.toFixed(2)}</span></span>
                            )}
                          </div>
                        </div>
                        {e.isFuture && (
                          <button onClick={()=>fetchEarningsBrief(e.sym, e.date, surprises.slice(0,4))} disabled={loading} style={{background:loading?"none":"var(--surface)",border:`1px solid ${brief?"var(--green)50":"var(--border)"}`,borderRadius:8,padding:"6px 14px",fontSize:10,color:loading?"var(--muted)":brief?"var(--green)":"var(--muted2)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em",flexShrink:0}}>
                            {loading?"BRIEFING…":brief?"✓ BRIEFED":"GET AI BRIEF"}
                          </button>
                        )}
                      </div>
                      {brief && (
                        <div style={{marginTop:14,paddingTop:14,borderTop:"1px solid var(--border)",display:"grid",gap:10}}>
                          <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                            <span className="badge" style={{background:`${verdictColor[brief.verdict]||"var(--muted)"}18`,color:verdictColor[brief.verdict]||"var(--muted2)",border:`1px solid ${verdictColor[brief.verdict]||"var(--border)"}40`}}>{brief.verdict?.replace("_"," ")}</span>
                            <span style={{fontSize:11,fontFamily:"var(--ff-mono)",color:"var(--muted)",letterSpacing:"0.06em"}}>AI PRE-EARNINGS BRIEF</span>
                          </div>
                          {[{l:"WATCH FOR",v:brief.watch},{l:"CONSENSUS",v:brief.consensus},{l:"KEY RISK",v:brief.risk},{l:"SETUP",v:brief.setup}].filter(x=>x.v).map(({l,v})=>(
                            <div key={l}>
                              <div style={{fontSize:9,fontFamily:"var(--ff-mono)",color:"var(--muted)",letterSpacing:"0.1em",marginBottom:3}}>{l}</div>
                              <p style={{fontSize:12,color:"var(--muted2)",lineHeight:1.5,margin:0}}>{v}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <>
                    {upcoming.length > 0 && (
                      <div className="fu2" style={{marginBottom:28}}>
                        <div className="section-label">UPCOMING EARNINGS ({upcoming.length})</div>
                        {upcoming.map((e,i) => <EarningsRow key={`${e.sym}-${e.date}-${i}`} e={e}/>)}
                      </div>
                    )}
                    {recent.length > 0 && (
                      <div className="fu3">
                        <div className="section-label">RECENT RESULTS</div>
                        {recent.map((e,i) => <EarningsRow key={`${e.sym}-${e.date}-${i}`} e={e}/>)}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* ══ IPO CALENDAR ══ */}
          {tab==="ipo"&&(
            <div>
              <div className="fu" style={{marginBottom:24}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
                  <div>
                    <h1 style={{fontFamily:"var(--ff-head)",fontSize:26,fontWeight:800,color:"var(--text2)",marginBottom:6}}>IPO Calendar</h1>
                    <p style={{fontSize:13,color:"var(--muted2)"}}>Upcoming &amp; recent listings — NASDAQ · NYSE · ASX</p>
                  </div>
                  <button onClick={()=>{setIpoItems([]);fetchIPO();}} disabled={ipoLoading} style={{background:"none",border:"1px solid var(--border)",borderRadius:8,padding:"7px 16px",fontSize:10,color:ipoLoading?"var(--muted)":"var(--muted2)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em",opacity:ipoLoading?.5:1}}>
                    {ipoLoading ? "↻ LOADING…" : "↻ REFRESH"}
                  </button>
                </div>
              </div>

              <div style={{display:"flex",gap:6,marginBottom:20,flexWrap:"wrap"}}>
                {["ALL","UPCOMING","PRICED","FILED","WITHDRAWN"].map(f=>(
                  <button key={f} className={`filter-btn${ipoFilter===f?" active":""}`} onClick={()=>setIpoFilter(f)}>{f}</button>
                ))}
              </div>

              {ipoError && (
                <div style={{background:"#ff525212",border:"1px solid #ff525230",borderRadius:12,padding:"20px 24px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                  <p style={{fontSize:13,color:"var(--red)"}}>{ipoError}</p>
                  <button onClick={fetchIPO} style={{background:"none",border:"1px solid var(--red)50",borderRadius:8,padding:"6px 14px",fontSize:10,color:"var(--red)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em"}}>TRY AGAIN</button>
                </div>
              )}

              {ipoLoading && (
                <div style={{display:"grid",gap:10,marginBottom:20}}>
                  {[1,2,3,4].map(i=><div key={i} className="shimmer-el" style={{height:90}}/>)}
                </div>
              )}

              {!ipoLoading && !ipoError && (() => {
                const statusMap = { UPCOMING:"expected", PRICED:"priced", FILED:"filed", WITHDRAWN:"withdrawn" };
                const filtered = ipoFilter === "ALL" ? ipoItems : ipoItems.filter(i => i.status === statusMap[ipoFilter]);
                if (filtered.length === 0) {
                  return (
                    <div style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:12,padding:"40px 32px",textAlign:"center"}}>
                      <div style={{fontSize:28,marginBottom:12,opacity:.3}}>◆</div>
                      <p style={{color:"var(--muted2)",fontSize:14,fontFamily:"var(--ff-head)",fontWeight:600,marginBottom:8}}>
                        {ipoItems.length === 0 ? "No IPO data available." : "No upcoming IPOs in this window."}
                      </p>
                      {ipoItems.length === 0 && <p style={{color:"var(--muted)",fontSize:12}}>Finnhub API key may not be configured.</p>}
                    </div>
                  );
                }
                return (
                  <div className="fu2">
                    {filtered.map((ipo,i) => (
                      <IpoCard key={`${ipo.symbol}-${ipo.date}-${i}`} ipo={ipo} onAnalyse={sym=>openDetail({sym,name:ipo.name,priceType:"stock",priceCurrency:"USD"})}/>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          {/* ══ AI CALLS ══ */}
          {tab==="calls"&&(
            <div>
              <div className="fu" style={{marginBottom:24}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
                  <div>
                    <h1 style={{fontFamily:"var(--ff-head)",fontSize:26,fontWeight:800,color:"var(--text2)",marginBottom:6}}>AI Track Record</h1>
                    <p style={{fontSize:13,color:"var(--muted2)"}}>Every verdict the AI has made — with live return tracking. CALLED date shows when the recommendation was first made.</p>
                  </div>
                  {callRecords.length > 0 && (
                    <button onClick={()=>{ if(window.confirm("Clear all call records? This cannot be undone.")) setCallRecords([]); }} style={{background:"#ff525218",border:"1px solid #ff525240",borderRadius:8,padding:"7px 16px",fontSize:10,color:"var(--red)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em"}}>
                      CLEAR ALL
                    </button>
                  )}
                </div>
              </div>

              {callRecords.length === 0 ? (
                <div style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:14,padding:"52px 32px",textAlign:"center"}}>
                  <div style={{fontSize:40,marginBottom:16,opacity:.25}}>◐</div>
                  <p style={{color:"var(--muted2)",fontSize:15,fontFamily:"var(--ff-head)",fontWeight:700,marginBottom:8}}>No calls recorded yet.</p>
                  <p style={{color:"var(--muted)",fontSize:13,marginBottom:24,lineHeight:1.6,maxWidth:420,margin:"0 auto 24px"}}>
                    Every time the AI gives a verdict in Explorer or Dashboard, it's automatically saved here with the entry price. Over time you'll build a track record you can actually verify.
                  </p>
                  <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
                    <button onClick={()=>setTab("explorer")} style={{background:"var(--green)18",border:"1px solid var(--green)40",borderRadius:8,padding:"8px 20px",fontSize:11,color:"var(--green)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em"}}>
                      GO TO EXPLORER →
                    </button>
                    <button onClick={()=>setTab("dashboard")} style={{background:"var(--purple)18",border:"1px solid var(--purple)40",borderRadius:8,padding:"8px 20px",fontSize:11,color:"var(--purple)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em"}}>
                      GO TO DASHBOARD →
                    </button>
                  </div>
                </div>
              ) : (() => {
                // ── Stats computation ──
                const withPrices = callRecords.filter(c => {
                  const cp = callsPrices[c.sym]?.price;
                  return cp != null && c.priceAtCall != null && c.priceAtCall > 0;
                }).map(c => {
                  const cp = callsPrices[c.sym]?.price;
                  const ret = ((cp - c.priceAtCall) / c.priceAtCall) * 100;
                  return { ...c, returnPct: ret };
                });

                // BUY stats (only BUY verdicts)
                const buyCallsWithPrices = withPrices.filter(c => c.verdict === "BUY");
                const buyWins = buyCallsWithPrices.filter(c => c.returnPct > 0).length;
                const buyWinRate = buyCallsWithPrices.length >= 3 ? (buyWins / buyCallsWithPrices.length * 100) : null;
                const avgBuyReturn = buyCallsWithPrices.length > 0
                  ? buyCallsWithPrices.reduce((s, c) => s + c.returnPct, 0) / buyCallsWithPrices.length
                  : null;

                // AVOID stats (correct = stock went down)
                const avoidCalls = withPrices.filter(c => c.verdict === "AVOID" || c.verdict === "SELL");
                const avoidCorrect = avoidCalls.filter(c => c.returnPct < 0).length;
                const avoidRate = avoidCalls.length >= 3 ? (avoidCorrect / avoidCalls.length * 100) : null;
                const avgAvoidReturn = avoidCalls.length > 0
                  ? avoidCalls.reduce((s, c) => s + c.returnPct, 0) / avoidCalls.length
                  : null;

                const best  = buyCallsWithPrices.length > 0 ? buyCallsWithPrices.reduce((a, b) => a.returnPct > b.returnPct ? a : b) : null;
                const worst = buyCallsWithPrices.length > 0 ? buyCallsWithPrices.reduce((a, b) => a.returnPct < b.returnPct ? a : b) : null;

                const verdictCounts = { BUY:0, WATCH:0, AVOID:0, HOLD:0 };
                callRecords.forEach(c => { if (verdictCounts[c.verdict] != null) verdictCounts[c.verdict]++; });

                const filtered = callsFilter === "ALL" ? callRecords : callRecords.filter(c => c.verdict === callsFilter);

                return (
                  <>
                    {/* Stats strip */}
                    <div className="fu2" style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:12,marginBottom:12}}>
                      {[
                        { l:"TOTAL CALLS", v:`${callRecords.length}`, c:"var(--text2)", sub:null },
                        { l:"BUY WIN RATE", v: buyWinRate != null ? `${buyWinRate.toFixed(0)}%` : buyCallsWithPrices.length < 3 ? `need ${3 - buyCallsWithPrices.length} more` : "—", c: buyWinRate != null ? (buyWinRate >= 50 ? "var(--green)" : "var(--red)") : "var(--muted)", sub: buyCallsWithPrices.length > 0 ? `${buyWins}/${buyCallsWithPrices.length} BUYs` : null },
                        { l:"AVG BUY RETURN", v: avgBuyReturn != null ? `${avgBuyReturn >= 0 ? "+" : ""}${avgBuyReturn.toFixed(1)}%` : "—", c: avgBuyReturn != null ? (avgBuyReturn >= 0 ? "var(--green)" : "var(--red)") : "var(--muted)", sub:null },
                        { l:"BEST BUY", v: best ? `${best.returnPct >= 0 ? "+" : ""}${best.returnPct.toFixed(1)}%` : "—", c:"var(--green)", sub: best ? best.sym : null },
                        { l:"WORST BUY", v: worst ? `${worst.returnPct >= 0 ? "+" : ""}${worst.returnPct.toFixed(1)}%` : "—", c:"var(--red)", sub: worst ? worst.sym : null },
                      ].map(m => (
                        <div key={m.l} className="stat-card">
                          <div style={{fontSize:9,fontFamily:"var(--ff-mono)",color:"var(--muted)",letterSpacing:"0.12em",marginBottom:10}}>{m.l}</div>
                          <div style={{fontSize:20,fontFamily:"var(--ff-head)",fontWeight:800,color:m.c,lineHeight:1}}>{m.v}</div>
                          {m.sub && <div style={{fontSize:10,color:"var(--muted2)",marginTop:5,fontFamily:"var(--ff-mono)"}}>{m.sub}</div>}
                        </div>
                      ))}
                    </div>
                    {avoidCalls.length > 0 && (
                    <div className="fu2" style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:24}}>
                      {[
                        { l:"AVOID ACCURACY", v: avoidRate != null ? `${avoidRate.toFixed(0)}%` : avoidCalls.length < 3 ? `need ${3 - avoidCalls.length} more` : "—", c: avoidRate != null ? (avoidRate >= 50 ? "var(--green)" : "var(--red)") : "var(--muted)", sub: avoidCalls.length > 0 ? `${avoidCorrect}/${avoidCalls.length} correct` : null },
                        { l:"AVG AVOIDED LOSS", v: avgAvoidReturn != null ? `${avgAvoidReturn >= 0 ? "+" : ""}${avgAvoidReturn.toFixed(1)}%` : "—", c: avgAvoidReturn != null ? (avgAvoidReturn < 0 ? "var(--green)" : "var(--red)") : "var(--muted)", sub: avgAvoidReturn != null && avgAvoidReturn < 0 ? "you dodged this" : null },
                        { l:"AVOIDS TRACKED", v:`${avoidCalls.length}`, c:"var(--text2)", sub:null },
                      ].map(m => (
                        <div key={m.l} className="stat-card">
                          <div style={{fontSize:9,fontFamily:"var(--ff-mono)",color:"var(--muted)",letterSpacing:"0.12em",marginBottom:10}}>{m.l}</div>
                          <div style={{fontSize:20,fontFamily:"var(--ff-head)",fontWeight:800,color:m.c,lineHeight:1}}>{m.v}</div>
                          {m.sub && <div style={{fontSize:10,color:"var(--muted2)",marginTop:5,fontFamily:"var(--ff-mono)"}}>{m.sub}</div>}
                        </div>
                      ))}
                    </div>
                    )}

                    {/* Filter pills */}
                    <div className="fu3" style={{display:"flex",gap:6,marginBottom:20,flexWrap:"wrap"}}>
                      {["ALL","BUY","WATCH","AVOID","HOLD"].map(f => {
                        const count = f === "ALL" ? callRecords.length : verdictCounts[f] || 0;
                        return (
                          <button key={f} className={`filter-btn${callsFilter===f?" active":""}`} onClick={()=>setCallsFilter(f)}>
                            {f} {count > 0 && `(${count})`}
                          </button>
                        );
                      })}
                    </div>

                    {/* Card list */}
                    {callsPriceFetching && callsPrices && Object.keys(callsPrices).length === 0 ? (
                      <div style={{display:"grid",gap:10}}>
                        {[1,2,3].map(i=><div key={i} className="shimmer-el" style={{height:88}}/>)}
                      </div>
                    ) : filtered.length === 0 ? (
                      <div style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:12,padding:"32px",textAlign:"center"}}>
                        <p style={{color:"var(--muted2)",fontSize:13}}>No {callsFilter} calls recorded.</p>
                      </div>
                    ) : (
                      <div className="fu2">
                        {filtered.map(c => (
                          <CallCard
                            key={c.id}
                            record={c}
                            currentPriceData={callsPrices[c.sym]}
                            priceFetching={callsPriceFetching}
                            onAnalyse={sym => { setTab("explorer"); handleSearch(sym); }}
                            onRemove={sym => setCallRecords(prev => prev.filter(x => x.sym !== sym))}
                          />
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* ══ TRADE JOURNAL ══ */}
          {tab==="journal"&&(
            <div>
              <div className="fu" style={{marginBottom:24}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
                  <div>
                    <h1 style={{fontFamily:"var(--ff-head)",fontSize:26,fontWeight:800,color:"var(--text2)",marginBottom:6}}>Trade Journal</h1>
                    <p style={{fontSize:13,color:"var(--muted2)"}}>Log your trades. AI identifies behavioural patterns over time.</p>
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    {journalEntries.length >= 3 && (
                      <button onClick={analyseJournal} disabled={journalAnalysing} style={{background:journalAnalysing?"none":"#448aff18",border:`1px solid ${journalAnalysing?"var(--border)":"#448aff40"}`,borderRadius:8,padding:"7px 16px",fontSize:10,color:journalAnalysing?"var(--muted)":"var(--blue)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em",opacity:journalAnalysing?.5:1}}>
                        {journalAnalysing?"◈ ANALYSING…":"◈ ANALYSE PATTERNS"}
                      </button>
                    )}
                    <button onClick={()=>setJournalFormOpen(p=>!p)} style={{background:journalFormOpen?"var(--green)18":"none",border:`1px solid ${journalFormOpen?"var(--green)40":"var(--border)"}`,borderRadius:8,padding:"7px 16px",fontSize:10,color:journalFormOpen?"var(--green)":"var(--muted2)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em"}}>
                      {journalFormOpen?"✕ CANCEL":"+ LOG TRADE"}
                    </button>
                  </div>
                </div>
              </div>

              {/* Add trade form */}
              {journalFormOpen && (
                <div className="fu card" style={{padding:20,marginBottom:20}}>
                  <div className="section-label">NEW TRADE ENTRY</div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:12}}>
                    {[
                      {label:"DATE",      key:"date",     type:"date",   placeholder:""},
                      {label:"SYMBOL",    key:"sym",      type:"text",   placeholder:"e.g. NVDA"},
                      {label:"ACTION",    key:"action",   type:"select", options:["BUY","SELL"]},
                      {label:"QUANTITY",  key:"qty",      type:"number", placeholder:"10"},
                      {label:"PRICE",     key:"price",    type:"number", placeholder:"450.00"},
                      {label:"CURRENCY",  key:"currency", type:"select", options:["USD","AUD"]},
                    ].map(f=>(
                      <div key={f.key}>
                        <div style={{fontSize:9,fontFamily:"var(--ff-mono)",color:"var(--muted)",letterSpacing:"0.1em",marginBottom:5}}>{f.label}</div>
                        {f.type==="select"
                          ? <select value={journalForm[f.key]} onChange={e=>setJournalForm(p=>({...p,[f.key]:e.target.value}))} style={{width:"100%",background:"var(--surface)",border:"1px solid var(--border)",borderRadius:8,padding:"9px 12px",color:"var(--text2)",fontSize:13,fontFamily:"var(--ff-mono)"}}>
                              {f.options.map(o=><option key={o} value={o}>{o}</option>)}
                            </select>
                          : <input type={f.type} value={journalForm[f.key]} placeholder={f.placeholder} onChange={e=>setJournalForm(p=>({...p,[f.key]:e.target.value}))} style={{width:"100%",background:"var(--surface)",border:"1px solid var(--border)",borderRadius:8,padding:"9px 12px",color:"var(--text2)",fontSize:13,fontFamily:"var(--ff-mono)"}}/>
                        }
                      </div>
                    ))}
                  </div>
                  <div style={{marginBottom:12}}>
                    <div style={{fontSize:9,fontFamily:"var(--ff-mono)",color:"var(--muted)",letterSpacing:"0.1em",marginBottom:5}}>INVESTMENT THESIS</div>
                    <textarea value={journalForm.thesis} onChange={e=>setJournalForm(p=>({...p,thesis:e.target.value}))} placeholder="Why are you making this trade? What's the thesis?" rows={2} style={{width:"100%",background:"var(--surface)",border:"1px solid var(--border)",borderRadius:8,padding:"9px 12px",color:"var(--text2)",fontSize:13,fontFamily:"var(--ff-body)",resize:"vertical"}}/>
                  </div>
                  <button
                    onClick={()=>{
                      const {date,sym,action,qty,price,currency,thesis} = journalForm;
                      if (!sym.trim() || !qty || !price) return;
                      const entry = { id:`j-${Date.now()}`, date, sym:sym.toUpperCase().trim(), action, qty:parseFloat(qty), price:parseFloat(price), currency, thesis, loggedAt:new Date().toISOString(), exitDate:null, exitPrice:null };
                      setJournalEntries(p=>[entry,...p]);
                      setJournalFormOpen(false);
                      setJournalForm(p=>({...p,sym:"",qty:"",price:"",thesis:""}));
                    }}
                    style={{background:"var(--green)",color:"#0a0a14",border:"none",borderRadius:8,padding:"9px 24px",fontSize:12,fontFamily:"var(--ff-head)",fontWeight:700}}
                  >
                    LOG TRADE
                  </button>
                </div>
              )}

              {/* AI Analysis */}
              {journalAnalysisError && <div style={{background:"#ff525212",border:"1px solid #ff525230",borderRadius:10,padding:"12px 16px",color:"var(--red)",fontSize:12,marginBottom:16}}>{journalAnalysisError}</div>}
              {journalAnalysis && (
                <div className="fu2 card" style={{padding:20,marginBottom:20}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                    <div className="section-label" style={{marginBottom:0}}>AI PATTERN ANALYSIS</div>
                    <button onClick={()=>setJournalAnalysis(null)} style={{background:"none",border:"none",fontSize:10,color:"var(--muted)",fontFamily:"var(--ff-mono)",cursor:"pointer",letterSpacing:"0.06em"}}>DISMISS</button>
                  </div>
                  <p style={{fontSize:13,color:"var(--muted2)",lineHeight:1.7,marginBottom:16}}>{journalAnalysis.summary}</p>
                  {journalAnalysis.keyAdvice && (
                    <div style={{background:"var(--blue)12",border:"1px solid var(--blue)30",borderRadius:8,padding:"12px 16px",marginBottom:16}}>
                      <div style={{fontSize:9,fontFamily:"var(--ff-mono)",color:"var(--blue)",letterSpacing:"0.1em",marginBottom:4}}>KEY ADVICE</div>
                      <p style={{fontSize:13,color:"var(--text2)",lineHeight:1.6,margin:0,fontWeight:500}}>{journalAnalysis.keyAdvice}</p>
                    </div>
                  )}
                  {journalAnalysis.patterns?.length > 0 && (
                    <div style={{marginBottom:16}}>
                      <div className="section-label">BEHAVIOURAL PATTERNS</div>
                      {journalAnalysis.patterns.map((p,i)=>(
                        <div key={i} style={{padding:"12px 0",borderBottom:i<journalAnalysis.patterns.length-1?"1px solid var(--border)":"none"}}>
                          <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:4}}>
                            <span style={{fontFamily:"var(--ff-head)",fontSize:13,fontWeight:700,color:"var(--text2)"}}>{p.pattern}</span>
                            <span className="badge" style={{background:({HIGH:"#ff525218",MEDIUM:"#ffab4018",LOW:"#00e67618"})[p.severity]||"var(--surface)",color:({HIGH:"var(--red)",MEDIUM:"var(--amber)",LOW:"var(--green)"})[p.severity]||"var(--muted2)",border:`1px solid ${({HIGH:"#ff525240",MEDIUM:"#ffab4040",LOW:"#00e67640"})[p.severity]||"var(--border)"}`}}>{p.severity}</span>
                          </div>
                          <p style={{fontSize:12,color:"var(--muted2)",lineHeight:1.5,marginBottom:6}}>{p.description}</p>
                          <p style={{fontSize:11,color:"var(--green)",fontFamily:"var(--ff-mono)"}}>{p.advice}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{display:"flex",gap:20,flexWrap:"wrap"}}>
                    {journalAnalysis.winRate && <div><div style={{fontSize:9,fontFamily:"var(--ff-mono)",color:"var(--muted)",letterSpacing:"0.1em",marginBottom:3}}>WIN RATE</div><div style={{fontSize:16,fontFamily:"var(--ff-head)",fontWeight:700,color:"var(--green)"}}>{journalAnalysis.winRate}</div></div>}
                    {journalAnalysis.topMistake && <div style={{flex:1,minWidth:200}}><div style={{fontSize:9,fontFamily:"var(--ff-mono)",color:"var(--muted)",letterSpacing:"0.1em",marginBottom:3}}>BIGGEST MISTAKE</div><div style={{fontSize:12,color:"var(--red)"}}>{journalAnalysis.topMistake}</div></div>}
                  </div>
                </div>
              )}

              {/* Entry list */}
              {journalEntries.length === 0 ? (
                <div style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:14,padding:"52px 32px",textAlign:"center"}}>
                  <div style={{fontSize:40,marginBottom:16,opacity:.25}}>◫</div>
                  <p style={{color:"var(--muted2)",fontSize:15,fontFamily:"var(--ff-head)",fontWeight:700,marginBottom:8}}>No trades logged yet.</p>
                  <p style={{color:"var(--muted)",fontSize:13,marginBottom:24}}>Log 3+ trades and AI will identify behavioural patterns — are you holding losers too long? Selling winners too early?</p>
                  <button onClick={()=>setJournalFormOpen(true)} style={{background:"var(--green)18",border:"1px solid var(--green)40",borderRadius:8,padding:"8px 20px",fontSize:11,color:"var(--green)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em"}}>LOG YOUR FIRST TRADE →</button>
                </div>
              ) : (
                <div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                    <div className="section-label" style={{marginBottom:0}}>TRADE LOG ({journalEntries.length})</div>
                    {journalEntries.length < 3 && <span style={{fontSize:11,color:"var(--muted)",fontFamily:"var(--ff-mono)"}}>Add {3-journalEntries.length} more trade{3-journalEntries.length!==1?"s":""} to unlock pattern analysis</span>}
                  </div>
                  {journalEntries.map(e=>{
                    const isClosed = !!e.exitDate;
                    const pnlPct = isClosed && e.price && e.exitPrice
                      ? ((e.exitPrice - e.price) / e.price * 100 * (e.action==="SELL"?-1:1))
                      : null;
                    return (
                      <div key={e.id} className="card" style={{padding:18,marginBottom:10,borderLeft:`3px solid ${e.action==="BUY"?"var(--green)":"var(--red)"}`,opacity:isClosed?0.65:1}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
                          <div>
                            <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:4,flexWrap:"wrap"}}>
                              <span className="badge" style={{background:e.action==="BUY"?"#00e67618":"#ff525218",color:e.action==="BUY"?"var(--green)":"var(--red)",border:`1px solid ${e.action==="BUY"?"#00e67640":"#ff525240"}`}}>{e.action}</span>
                              <span style={{fontFamily:"var(--ff-head)",fontSize:15,fontWeight:700,color:"var(--text2)"}}>{e.sym}</span>
                              <span style={{fontSize:11,fontFamily:"var(--ff-mono)",color:"var(--muted)"}}>{fmtDate(e.date)}</span>
                              {isClosed && <span className="badge" style={{background:"var(--surface)",color:"var(--muted)",border:"1px solid var(--border)"}}>CLOSED</span>}
                              {pnlPct != null && <span style={{fontSize:11,fontFamily:"var(--ff-mono)",color:pnlPct>=0?"var(--green)":"var(--red)",fontWeight:600}}>{pnlPct>=0?"+":""}{pnlPct.toFixed(1)}%</span>}
                            </div>
                            <div style={{fontSize:11,fontFamily:"var(--ff-mono)",color:"var(--muted2)"}}>
                              {e.qty} × {e.currency}{e.price}
                              {isClosed && ` → ${e.currency}${e.exitPrice} (${fmtDate(e.exitDate)})`}
                            </div>
                            {e.thesis && <p style={{fontSize:11,color:"var(--muted)",marginTop:6,fontStyle:"italic",lineHeight:1.4}}>{e.thesis}</p>}
                          </div>
                          <div style={{display:"flex",gap:6,flexShrink:0}}>
                            {!isClosed && (
                              journalExitForm===e.id ? (
                                <div style={{display:"flex",gap:6,alignItems:"center"}}>
                                  <input type="number" placeholder="Exit price" id={`exit-${e.id}`} style={{width:100,background:"var(--surface)",border:"1px solid var(--border)",borderRadius:6,padding:"5px 8px",color:"var(--text2)",fontSize:12,fontFamily:"var(--ff-mono)"}}/>
                                  <button onClick={()=>{
                                    const ep = parseFloat(document.getElementById(`exit-${e.id}`)?.value);
                                    if (!ep) return;
                                    setJournalEntries(p=>p.map(x=>x.id===e.id?{...x,exitDate:new Date().toISOString().slice(0,10),exitPrice:ep}:x));
                                    setJournalExitForm(null);
                                  }} style={{background:"var(--green)",color:"#0a0a14",border:"none",borderRadius:6,padding:"5px 12px",fontSize:10,fontFamily:"var(--ff-mono)",fontWeight:700}}>CLOSE</button>
                                  <button onClick={()=>setJournalExitForm(null)} style={{background:"none",border:"1px solid var(--border)",borderRadius:6,padding:"5px 10px",fontSize:10,color:"var(--muted)",fontFamily:"var(--ff-mono)"}}>✕</button>
                                </div>
                              ) : (
                                <button onClick={()=>setJournalExitForm(e.id)} style={{background:"none",border:"1px solid var(--border)",borderRadius:6,padding:"5px 12px",fontSize:10,color:"var(--muted2)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em"}}>CLOSE TRADE</button>
                              )
                            )}
                            <button onClick={()=>setJournalEntries(p=>p.filter(x=>x.id!==e.id))} style={{background:"#ff525212",border:"1px solid #ff525230",borderRadius:6,padding:"5px 10px",fontSize:10,color:"var(--red)",fontFamily:"var(--ff-mono)"}}>✕</button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ══ INDICES ══ */}
          {tab==="indices"&&(
            <div>
              <div className="fu" style={{marginBottom:24}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
                  <div>
                    <h1 style={{fontFamily:"var(--ff-head)",fontSize:26,fontWeight:800,color:"var(--text2)",marginBottom:6}}>Market Indices</h1>
                    <p style={{fontSize:13,color:"var(--muted2)"}}>Major market indices with AI-powered analysis.</p>
                  </div>
                </div>
              </div>

              {indicesLoading && (
                <div style={{display:"grid",gap:10}}>
                  {[1,2,3].map(i=><div key={i} className="card shimmer-el" style={{height:90}}/>)}
                </div>
              )}

              {indicesData && indicesData.length > 0 && (
                <div>
                  {/* ── Overlay % change line chart ── */}
                  <div className="card" style={{padding:16,marginBottom:14}}>
                    <div style={{fontSize:10,fontFamily:"var(--ff-mono)",color:"var(--muted)",letterSpacing:"0.06em",marginBottom:10}}>1-MONTH % CHANGE</div>
                    <canvas ref={el=>{
                      if(!el) return;
                      const COLORS={"^AORD":"#ffab40","^IXIC":"#448aff","^DJI":"#00e676"};
                      const dpr=window.devicePixelRatio||1;
                      const W=el.parentElement.clientWidth-32;
                      const H=180;
                      el.width=W*dpr; el.height=H*dpr;
                      el.style.width="100%"; el.style.height=H+"px";
                      const ctx=el.getContext("2d");
                      ctx.setTransform(dpr,0,0,dpr,0,0);
                      // Find global date range and % range
                      let allPcts=[];
                      for(const idx of indicesData){if(idx.series) idx.series.forEach(p=>allPcts.push(p.pct));}
                      if(!allPcts.length) return;
                      const minPct=Math.min(...allPcts);
                      const maxPct=Math.max(...allPcts);
                      const pad=(maxPct-minPct)*0.15||0.5;
                      const yMin=minPct-pad, yMax=maxPct+pad;
                      let allTs=[];
                      for(const idx of indicesData){if(idx.series) idx.series.forEach(p=>allTs.push(p.t));}
                      const tMin=Math.min(...allTs), tMax=Math.max(...allTs);
                      const mL=40,mR=12,mT=12,mB=28;
                      const cW=W-mL-mR, cH=H-mT-mB;
                      const toX=t=>mL+((t-tMin)/(tMax-tMin||1))*cW;
                      const toY=p=>mT+((yMax-p)/(yMax-yMin||1))*cH;
                      // Background
                      ctx.fillStyle="var(--card)"; ctx.fillRect(0,0,W,H);
                      // 0% line
                      const zeroY=toY(0);
                      ctx.strokeStyle="rgba(255,255,255,0.1)"; ctx.lineWidth=1;
                      ctx.beginPath(); ctx.moveTo(mL,zeroY); ctx.lineTo(W-mR,zeroY); ctx.stroke();
                      ctx.fillStyle="rgba(255,255,255,0.3)"; ctx.font="9px var(--ff-mono)"; ctx.textAlign="right";
                      ctx.fillText("0%",mL-4,zeroY+3);
                      // Y axis labels
                      const ySteps=[yMin,yMax];
                      for(const v of ySteps){
                        const y=toY(v);
                        ctx.fillStyle="rgba(255,255,255,0.2)";
                        ctx.fillText((v>=0?"+":"")+v.toFixed(1)+"%",mL-4,y+3);
                      }
                      // Draw lines + legend
                      let legendX=mL;
                      for(const idx of indicesData){
                        if(!idx.series||idx.series.length<2) continue;
                        const col=COLORS[idx.sym]||"#888";
                        ctx.strokeStyle=col; ctx.lineWidth=1.5; ctx.lineJoin="round";
                        ctx.beginPath();
                        idx.series.forEach((p,i)=>{
                          const x=toX(p.t), y=toY(p.pct);
                          i===0?ctx.moveTo(x,y):ctx.lineTo(x,y);
                        });
                        ctx.stroke();
                        // Legend
                        ctx.fillStyle=col; ctx.font="bold 9px var(--ff-mono)"; ctx.textAlign="left";
                        ctx.fillText(idx.name,legendX,H-4);
                        legendX+=ctx.measureText(idx.name).width+14;
                      }
                      // X axis dates
                      ctx.fillStyle="rgba(255,255,255,0.3)"; ctx.font="8px var(--ff-mono)"; ctx.textAlign="center";
                      const refSeries=indicesData.find(i=>i.series)?.series||[];
                      const step=Math.max(1,Math.floor(refSeries.length/5));
                      for(let i=0;i<refSeries.length;i+=step){
                        const d=new Date(refSeries[i].t);
                        ctx.fillText(`${d.getDate()}/${d.getMonth()+1}`,toX(refSeries[i].t),H-mB+14);
                      }
                    }} style={{display:"block"}}/>
                  </div>
                  {/* ── Index cards ── */}
                  <div style={{display:"grid",gap:10}}>
                    {indicesData.map(idx => {
                      const colors = {"^AORD":"var(--amber)","^IXIC":"var(--blue)","^DJI":"var(--green)"};
                      const accent = colors[idx.sym] || "var(--border2)";
                      return (
                        <button key={idx.sym} className="card" onClick={()=>openDetail({sym:idx.sym,name:idx.name,priceType:"index",priceCurrency:"USD"})} style={{padding:"20px 24px",borderLeft:`3px solid ${accent}`,cursor:"pointer",textAlign:"left",width:"100%",background:"var(--card)",border:"1px solid var(--border)",borderLeftWidth:3,borderLeftColor:accent}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12}}>
                            <div style={{minWidth:0}}>
                              <span style={{fontFamily:"var(--ff-head)",fontSize:18,fontWeight:700,color:"var(--text2)"}}>{idx.name}</span>
                              <div style={{fontSize:10,fontFamily:"var(--ff-mono)",color:"var(--muted)",letterSpacing:"0.06em",marginTop:4}}>{idx.sym}</div>
                            </div>
                            <div style={{textAlign:"right",flexShrink:0}}>
                              <div style={{fontFamily:"var(--ff-mono)",fontSize:18,fontWeight:600,color:"var(--text2)",marginBottom:3}}>
                                {idx.price?.toLocaleString("en",{minimumFractionDigits:2,maximumFractionDigits:2})} pts
                              </div>
                              {idx.change1 != null && (
                                <div style={{fontFamily:"var(--ff-mono)",fontSize:11,color:idx.change1>=0?"var(--green)":"var(--red)"}}>
                                  {idx.day1Date}: {idx.change1>=0?"+":""}{idx.change1.toFixed(2)}%
                                </div>
                              )}
                            </div>
                          </div>
                          <div style={{marginTop:10,display:"flex",justifyContent:"flex-end"}}>
                            <span style={{fontSize:10,fontFamily:"var(--ff-mono)",color:accent,letterSpacing:"0.06em"}}>VIEW CHART & ANALYSIS →</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {!indicesLoading && (!indicesData || indicesData.length === 0) && (
                <div className="card" style={{padding:40,textAlign:"center"}}>
                  <p style={{color:"var(--muted)",fontSize:13}}>Could not load indices data. Try again later.</p>
                </div>
              )}
            </div>
          )}

          {/* ══ DETAIL / CHART ══ */}
          {tab==="detail"&&(
            <div>
              {/* back + header */}
              <div className="fu" style={{marginBottom:20}}>
                <button onClick={()=>setTab(detailFrom||"explorer")} style={{background:"none",border:"none",fontSize:11,color:"var(--muted2)",fontFamily:"var(--ff-mono)",padding:"0 0 12px",letterSpacing:"0.06em",cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
                  ← BACK
                </button>
                {detailSym && (
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
                    <div>
                      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8,flexWrap:"wrap"}}>
                        <h1 style={{fontFamily:"var(--ff-head)",fontSize:28,fontWeight:900,color:"var(--text2)",letterSpacing:"-0.02em"}}>{detailSym.sym}</h1>
                        <span style={{fontSize:14,color:"var(--muted2)"}}>{detailSym.name}</span>
                        <SectorBadge sector={detailSym.sector}/>
                      </div>
                      {/* price row */}
                      <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",marginBottom:12}}>
                        {chartData && (() => {
                          const isIdx = detailSym?.priceType === "index";
                          const rawPrice = chartData.currentPrice;
                          const dispPrice = isIdx ? rawPrice : toDisplay(rawPrice, chartData.currency, displayCcy, audUsd);
                          const dispPrev  = isIdx ? chartData.previousClose : toDisplay(chartData.previousClose, chartData.currency, displayCcy, audUsd);
                          const chg = dispPrev ? ((dispPrice - dispPrev) / dispPrev) * 100 : null;
                          const symbol = isIdx ? "" : (displayCcy === "AUD" ? "A$" : "$");
                          const suffix = isIdx ? " pts" : "";
                          return (
                            <>
                              <span style={{fontFamily:"var(--ff-mono)",fontSize:22,fontWeight:600,color:"var(--text2)"}}>
                                {symbol}{dispPrice?.toLocaleString("en",{minimumFractionDigits:2,maximumFractionDigits:dispPrice>=10?2:4})}{suffix}
                              </span>
                              {chg != null && <span style={{fontFamily:"var(--ff-mono)",fontSize:13,color:chg>=0?"var(--green)":"var(--red)"}}>{chg>=0?"+":""}{chg.toFixed(2)}%</span>}
                              {!isIdx && <span style={{fontSize:11,color:"var(--muted)",fontFamily:"var(--ff-mono)"}}>{displayCcy}</span>}
                            </>
                          );
                        })()}
                      </div>
                      {/* single unified verdict */}
                      <div style={{display:"flex",gap:16,flexWrap:"wrap",alignItems:"center"}}>
                        {detailAnalysing && (
                          <div style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:10,padding:"10px 16px",display:"flex",alignItems:"center",gap:10}}>
                            <div style={{width:8,height:8,borderRadius:"50%",background:"var(--amber)",animation:"pulse 1s infinite"}}/>
                            <span style={{fontSize:11,fontFamily:"var(--ff-mono)",color:"var(--muted)"}}>Analysing…</span>
                          </div>
                        )}
                        {detailAnalysis && !detailAnalysing && (
                          <div style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:10,padding:"10px 16px"}}>
                            <div style={{display:"flex",alignItems:"center",gap:8}}>
                              <VerdictBadge v={detailAnalysis.verdict}/>
                              <ConvictionDots level={detailAnalysis.conviction}/>
                              {detailAnalysis.horizon && <span style={{fontSize:11,fontFamily:"var(--ff-mono)",color:"var(--muted2)"}}>{detailAnalysis.horizon} term</span>}
                              {detailAnalysis.target && <span style={{fontSize:11,fontFamily:"var(--ff-mono)",color:"var(--muted2)"}}>Target {detailSym?.priceType==="index" ? detailAnalysis.target.replace(/\$/g,"") + " pts" : detailAnalysis.target}</span>}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{display:"flex",gap:8,alignItems:"flex-start",flexWrap:"wrap"}}>
                      {detailSym?.priceType==="crypto" && (cbLastSync || bnLastSync) && (
                        <button onClick={()=>setTradeModal({sym:detailSym.sym,name:detailSym.name,productId:`${detailSym.sym}-USD`,side:"BUY",priceType:"crypto"})} style={{background:"#00e67610",border:"1px solid #00e67640",borderRadius:8,padding:"8px 18px",fontSize:11,color:"var(--green)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em",fontWeight:700,cursor:"pointer"}}>
                          ↔ TRADE
                        </button>
                      )}
                      {detailSym?.priceType==="stock" && tigerLastSync && (
                        <button onClick={()=>setTradeModal({sym:detailSym.sym,name:detailSym.name,side:"BUY",priceType:"stock",exchange:"tiger"})} style={{background:"#00e67610",border:"1px solid #00e67640",borderRadius:8,padding:"8px 18px",fontSize:11,color:"var(--green)",fontFamily:"var(--ff-mono)",letterSpacing:"0.06em",fontWeight:700,cursor:"pointer"}}>
                          ↔ TRADE
                        </button>
                      )}
                      {detailSym?.priceType!=="index"&&<CurrencyToggle value={displayCcy} onChange={setDisplayCcy}/>}
                    </div>
                  </div>
                )}
              </div>

              {/* range selector */}
              <div style={{display:"flex",gap:4,marginBottom:12,alignItems:"center",justifyContent:"space-between",flexWrap:"wrap"}}>
                <div style={{display:"flex",gap:4,alignItems:"center",flexWrap:"wrap"}}>
                  {["1d","7d","1mo","3mo","1y"].map(r=>(
                    <button key={r} onClick={()=>setChartRange(r)} style={{background:chartRange===r?"var(--green)":"none",color:chartRange===r?"#0a0a14":"var(--muted2)",border:`1px solid ${chartRange===r?"var(--green)":"var(--border)"}`,borderRadius:7,padding:"5px 14px",fontSize:11,fontFamily:"var(--ff-mono)",letterSpacing:"0.06em",fontWeight:chartRange===r?700:400}}>
                      {r.toUpperCase()}
                    </button>
                  ))}
                  {chartRange==="1d"&&(
                    <>
                      <button onClick={()=>setLiveMode(m=>!m)} style={{background:liveMode?"#00e67620":"none",color:liveMode?"var(--green)":"var(--muted2)",border:`1px solid ${liveMode?"#00e67640":"var(--border)"}`,borderRadius:7,padding:"5px 12px",fontSize:11,fontFamily:"var(--ff-mono)",letterSpacing:"0.06em",fontWeight:liveMode?700:400,marginLeft:4}}>
                        {liveMode?<><span style={{display:"inline-block",width:6,height:6,borderRadius:"50%",background:"var(--green)",marginRight:5,animation:"pulse 1.5s infinite"}}/>LIVE</>:"○ LIVE"}
                      </button>
                      {liveMode&&(
                        <div style={{display:"flex",gap:2,marginLeft:2}}>
                          {[{s:10,l:"10s"},{s:30,l:"30s"},{s:60,l:"1m"},{s:300,l:"5m"}].map(({s,l})=>(
                            <button key={s} onClick={()=>setLiveInterval(s)} style={{background:liveInterval===s?"var(--surface)":"none",color:liveInterval===s?"var(--text2)":"var(--muted)",border:`1px solid ${liveInterval===s?"var(--border)":"transparent"}`,borderRadius:5,padding:"3px 8px",fontSize:9,fontFamily:"var(--ff-mono)"}}>
                              {l}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <button onClick={()=>openGlossary()} style={{background:"none",border:"1px solid var(--border)",borderRadius:7,padding:"5px 14px",fontSize:11,fontFamily:"var(--ff-mono)",letterSpacing:"0.06em",color:"var(--amber)"}}>
                  ◈ GLOSSARY
                </button>
              </div>
              {liveMode&&(liveLastUpdate||chartData?.marketState)&&(()=>{
                const isClosed = chartData?.marketState && chartData.marketState !== "REGULAR";
                const isCrypto = detailSym?.priceType === "crypto";
                return <div style={{fontSize:10,fontFamily:"var(--ff-mono)",color:isClosed && !isCrypto ? "var(--amber)" : "var(--green)",marginBottom:8,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                  <span style={{width:6,height:6,borderRadius:"50%",background:isClosed && !isCrypto ? "var(--amber)" : "var(--green)",animation:"pulse 1.5s infinite"}}/>
                  {isClosed && !isCrypto
                    ? `Market closed — showing last trading day data · updates every ${liveInterval<60?`${liveInterval}s`:`${liveInterval/60}m`}`
                    : `Live · updates every ${liveInterval<60?`${liveInterval}s`:`${liveInterval/60}m`}${liveLastUpdate ? ` · last update ${new Date(liveLastUpdate).toLocaleTimeString()}` : ""}`
                  }
                </div>;
              })()}

              {/* chart */}
              <div className="card" style={{padding:12,marginBottom:16,overflow:"hidden"}}>
                {chartLoading && (
                  <div style={{height:420,display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <div>
                      <div style={{display:"grid",gap:8}}>{[90,70,80].map((w,i)=><div key={i} className="shimmer-el" style={{height:16,width:`${w}%`}}/>)}</div>
                      <p style={{color:"var(--muted)",fontSize:12,fontFamily:"var(--ff-mono)",marginTop:16,textAlign:"center"}}>Loading chart…</p>
                    </div>
                  </div>
                )}
                {!chartLoading && chartData?.candles?.length > 0 && (
                  <ChartCanvas candles={chartData.candles} analysis={detailAnalysis} range={chartRange} currency={chartData.currency} indicators={chartData.indicators} displayCcy={displayCcy} audUsd={audUsd} priceType={detailSym?.priceType}/>
                )}
                {!chartLoading && !chartData && (
                  <div style={{height:420,display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <p style={{color:"var(--red)",fontSize:13}}>Chart data unavailable</p>
                  </div>
                )}
              </div>

              {/* ── Technical chart analysis — shown first, right after the chart ── */}
              {detailAnalysis && !detailAnalysing && (
                <div className="fu2" style={{display:"grid",gap:12,marginBottom:16}}>
                  <div className="section-label">TECHNICAL CHART ANALYSIS</div>

                  {/* Summary */}
                  <div className="card" style={{padding:20}}>
                    <p style={{fontSize:13,color:"var(--muted2)",lineHeight:1.7,margin:0}}>{linkifyText(detailAnalysis.summary, openGlossary, allGlossary)}</p>
                    {detailAnalysis.stopLoss && <div style={{marginTop:10,fontSize:12,fontFamily:"var(--ff-mono)",color:"var(--red)"}}>Stop loss: {detailAnalysis.stopLoss}</div>}
                  </div>

                  {/* Reasoning sections */}
                  {[
                    {l:"MACRO ENVIRONMENT", v:detailAnalysis.macro},
                    {l:"FUNDAMENTALS",      v:detailAnalysis.fundamental},
                    {l:"TECHNICAL ANALYSIS",v:detailAnalysis.technical},
                    {l:"SENTIMENT",         v:detailAnalysis.sentiment},
                    {l:"PORTFOLIO FIT",     v:detailAnalysis.portfolio},
                  ].filter(x=>x.v).length > 0 && (
                    <div className="card" style={{padding:20,display:"grid",gap:14}}>
                      {[
                        {l:"MACRO ENVIRONMENT", v:detailAnalysis.macro},
                        {l:"FUNDAMENTALS",      v:detailAnalysis.fundamental},
                        {l:"TECHNICAL ANALYSIS",v:detailAnalysis.technical},
                        {l:"SENTIMENT",         v:detailAnalysis.sentiment},
                        {l:"PORTFOLIO FIT",     v:detailAnalysis.portfolio},
                      ].filter(x=>x.v).map(({l,v},i,arr)=>(
                        <div key={l} style={{paddingBottom:i<arr.length-1?14:0,borderBottom:i<arr.length-1?"1px solid var(--border)":"none"}}>
                          <div style={{fontSize:9,fontFamily:"var(--ff-mono)",color:"var(--muted)",letterSpacing:"0.12em",marginBottom:5}}>{l}</div>
                          <p style={{fontSize:13,color:"var(--muted2)",lineHeight:1.7,margin:0}}>{linkifyText(v, openGlossary, allGlossary)}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Chart levels */}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                    {detailAnalysis.support?.length > 0 && (
                      <div className="card" style={{padding:16}}>
                        <div className="section-label">SUPPORT LEVELS</div>
                        {detailAnalysis.support.map((s,i)=>(
                          <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",borderBottom:i<detailAnalysis.support.length-1?"1px solid var(--border)":"none"}}>
                            <span style={{fontSize:12,color:"var(--muted2)"}}>{s.label}</span>
                            <div style={{display:"flex",gap:8,alignItems:"center"}}>
                              <span className="badge" style={{background:"#00e67612",color:"var(--green)",border:"1px solid #00e67630",fontSize:9}}>{s.strength}</span>
                              <span style={{fontFamily:"var(--ff-mono)",fontSize:13,color:"var(--green)",fontWeight:600}}>${s.price?.toLocaleString()}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {detailAnalysis.resistance?.length > 0 && (
                      <div className="card" style={{padding:16}}>
                        <div className="section-label">RESISTANCE LEVELS</div>
                        {detailAnalysis.resistance.map((r,i)=>(
                          <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",borderBottom:i<detailAnalysis.resistance.length-1?"1px solid var(--border)":"none"}}>
                            <span style={{fontSize:12,color:"var(--muted2)"}}>{r.label}</span>
                            <div style={{display:"flex",gap:8,alignItems:"center"}}>
                              <span className="badge" style={{background:"#ff525212",color:"var(--red)",border:"1px solid #ff525230",fontSize:9}}>{r.strength}</span>
                              <span style={{fontFamily:"var(--ff-mono)",fontSize:13,color:"var(--red)",fontWeight:600}}>${r.price?.toLocaleString()}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {(detailAnalysis.pattern?.name || detailAnalysis.momentum || detailAnalysis.volume) && (
                    <div className="card" style={{padding:20}}>
                      {detailAnalysis.pattern?.name && detailAnalysis.pattern.name !== "null" && (
                        <div style={{marginBottom:14}}>
                          <div className="section-label">PATTERN DETECTED</div>
                          <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:6}}>
                            {(() => {
                              const pName = detailAnalysis.pattern.name;
                              const col = detailAnalysis.pattern.bullish ? "var(--green)" : "var(--red)";
                              const matched = allGlossary.find(g => pName.toLowerCase().includes(g.term.toLowerCase()));
                              return matched
                                ? <span onClick={() => openGlossary(matched.term)} style={{fontFamily:"var(--ff-head)",fontSize:14,fontWeight:700,color:col,cursor:"pointer",borderBottom:"1px dotted currentColor"}} title={`Click to define: ${matched.term}`}>{pName}</span>
                                : <span style={{fontFamily:"var(--ff-head)",fontSize:14,fontWeight:700,color:col}}>{pName}</span>;
                            })()}
                            <span className="badge" style={{background:detailAnalysis.pattern.bullish?"#00e67618":"#ff525218",color:detailAnalysis.pattern.bullish?"var(--green)":"var(--red)",border:`1px solid ${detailAnalysis.pattern.bullish?"#00e67640":"#ff525240"}`}}>{detailAnalysis.pattern.bullish?"BULLISH":"BEARISH"}</span>
                          </div>
                          {detailAnalysis.pattern.note && <p style={{fontSize:12,color:"var(--muted2)",lineHeight:1.6}}>{linkifyText(detailAnalysis.pattern.note, openGlossary, allGlossary)}</p>}
                        </div>
                      )}
                      {detailAnalysis.momentum && (
                        <div style={{marginBottom:detailAnalysis.volume?14:0}}>
                          <div className="section-label">MOMENTUM</div>
                          <p style={{fontSize:12,color:"var(--muted2)",lineHeight:1.6}}>{linkifyText(detailAnalysis.momentum, openGlossary, allGlossary)}</p>
                        </div>
                      )}
                      {detailAnalysis.volume && (
                        <div>
                          <div className="section-label">VOLUME</div>
                          <p style={{fontSize:12,color:"var(--muted2)",lineHeight:1.6}}>{linkifyText(detailAnalysis.volume, openGlossary, allGlossary)}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

        </main>
      </div>
      {tradeModal && (
        <TradeModal config={tradeModal} onClose={()=>setTradeModal(null)} onSuccess={(ex)=>{setTradeModal(null);if(ex==="binance")syncBinance();else if(ex==="tiger"){syncTiger();syncTigerOrders();}else syncCoinbase();}} displayCcy={displayCcy} audUsd={audUsd} authFetch={authFetch} />
      )}
      <GlossaryModal open={glossaryOpen} onClose={()=>setGlossaryOpen(false)} focusTerm={glossaryTerm} allGlossary={allGlossary}/>
    </>
    </GlossaryCtx.Provider>
  );
}
